// App.tsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  Suspense,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { normalizeRoddoLanguageCode, setRoddoLanguage } from "./i18n";
import { getRoddoTheme } from "./src/constants/theme";

import { FEATURE } from "./src/constants/features";
import { challenges as defaultChallenges } from "./src/constants/challenges";
import BazaarComingSoonView from "./src/components/BazaarComingSoonView";
import TournamentsComingSoonView from "./src/components/TournamentsComingSoonView";
import TournamentView from "./src/components/TournamentView";
import { RoddoFishIcon } from "./src/components/icons";
import { fishIQBootstrapUser } from "./src/services/fishIQClient";
import WaitlistView from "./src/components/WaitlistView";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { db, auth, functions } from "./src/lib/firebase";
import {
  onAuthStateChanged,
  signOut,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import {
  ensureJsAuthFromNative,
  getAuthBridgeDiag,
  runNativeAuthBridgeNetDiag,
} from "./src/services/authBridge";

import { httpsCallable } from "firebase/functions";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
} from "firebase/firestore";

import { netProbeSafe } from "./src/services/netProbeSafe";
import { cfBootstrapMemberAndAccess } from "./src/services/accessClient";

import {
  redirectToMarketplaceCheckout,
  requestSellerPayout,
} from "./src/services/stripe";
import { releaseMarketplaceEscrow } from "./src/services/escrow";
import { reverseGeocodeServer } from "./src/services/geocodeClient";

import {
  LEGAL_SCHEMA_VERSION as LEGAL_SCHEMA_VERSION_TEXT,
  TOS_VERSION as TOS_VERSION_TEXT,
  PRIVACY_VERSION as PRIVACY_VERSION_TEXT,
} from "./src/legal/legalText";
import {
  buildRoddoAgeEligibilityRecord,
  checkRoddoAgeEligibility,
  getRoddoVerifiedAgeBracket,
  isRoddoTeenBracket,
  RODDO_AGE_BRACKET_KEY,
  RODDO_AGE_GATE_BLOCKED_KEY,
  RODDO_AGE_GATE_PASSED_KEY,
  type RoddoAgeBracket,
} from "./src/utils/ageEligibility";

import Dashboard from "./src/components/Dashboard";

import {
  addDiaryEntryFromPost,
  buildCatchTimelineEntries,
  subscribeToCatchDiaryEntries,
  CatchDiaryEntry,
} from "./src/services/catchDiary";
import { subscribeToCatchLog } from "./src/services/catchLog";

import { subscribeToGear } from "./src/services/gear";

import { subscribeToTournaments } from "./src/services/tournaments";
import { cfAdminDeleteTournament } from "./src/services/adminTournaments";
import { subscribeTournamentConfig, DEFAULT_TOURNAMENT_CONFIG } from "./src/services/tournamentConfig";

import {
  cfAdminUpdateUserRoles,
  cfAdminDeleteUserAndPosts,
  cfAdminSendAnnouncement,
  cfAdminDeletePost,
  cfAdminEditPost,
  cfAdminDeleteMarketplaceItem,
  cfAdminUpdateMarketplaceItem,
} from "./src/services/adminClient";

import {
  subscribeMarketplaceItems,
  createMarketplaceItem,
  deleteMarketplaceItem,
  updateMarketplaceItem,
  addOfferToMarketplaceItem,
  updateOfferStatus,
  subscribeMarketplaceTransactionsForUser,
} from "./src/services/marketplace";

import {
  createCommunityPost,
  updateCommunityPost,
  deleteCommunityPost,
  addCommentToPost,
  deleteCommentFromPost,
  updateCommentOnPost,
  togglePostLike,
  toggleCommentLike,
  subscribeCommentsForPosts,
  subscribeCommunityPosts,
} from "./src/services/community";

import {
  updateUserProfile,
  subscribeToUserProfile,
  subscribeToUsers,
} from "./src/services/users";

import { subscribeToUserLicenses } from "./src/services/licenses";
import { getRoddoAccountIdentity } from "./src/services/accountLinking";
import { trackRoddoEvent } from "./src/services/analytics";
import { initializeRoddoAdMob } from "./src/services/adMob";

import {
  subscribeToNotifications,
  createNotification,
  buildNotificationLink,
  markAllNotificationsReadForUser,
  clearAllNotificationsForUser,
  markNotificationRead,
} from "./src/services/notifications";

import { openNotification as routeNotification } from "./src/services/notificationRouter";
import {
  subscribeToAchievements,
  subscribeToChallengeCompletions,
  subscribeToChallengeProgress,
  type BackendAchievement,
  type BackendChallengeCompletion,
  type BackendChallengeProgress,
} from "./src/services/challengeEngine";

import { subscribeToFollowing } from "./src/services/following";
import { startUserActivityPulse } from "./src/services/userActivity";

import {
  View,
  UserProfile,
  CommunityPost,
  LogEntry,
  AppUser,
  MarketplaceItem,
  Comment,
  LicenseInfo,
  Notification,
  NotificationType,
  Toast,
  ToastType,
  UserLicense,
  MarketplaceTransaction,
  WeatherData,
  TackleBoxItem,
  Tournament,
  TournamentPlatformConfig,
  Offer,
} from "./src/types";


import { buildReputationSummary } from "./src/utils/reputation";

// ------------------------------------------------------------
// Core UI Views
// ------------------------------------------------------------
import AuthView from "./src/components/AuthView";
import LandingPage from "./src/components/LandingPage";
import BannedView from "./src/components/BannedView";
import BottomNav from "./src/components/BottomNav";
import MapView from "./src/components/MapView";
import BigBlueGlobeView, {
  preloadBigBlueGlobeAssets,
} from "./src/components/BigBlueGlobeView";
import DevicesView from "./src/components/DevicesView";
import BazaarView from "./src/components/BazaarView";
import Header from "./src/components/Header";
import ProfileView from "./src/components/ProfileView";
import PublicProfileView from "./src/components/PublicProfileView";
import ChallengesView from "./src/components/ChallengesView";

import LicenseDetailView from "./src/components/LicenseDetailView";
import EditProfileView from "./src/components/EditProfileView";
import AccountInfoView from "./src/components/AccountInfoView";
import AdminView from "./src/components/AdminView";
import SellerDashboardView from "./src/components/SellerDashboardView";
import NotificationSettingsView from "./src/components/NotificationSettingsView";
import NotificationsPanel from "./src/components/NotificationsPanel";
import PaymentGateway from "./src/components/PaymentGateway";

import CheckoutSuccessView from "./src/components/CheckoutSuccessView";
import CheckoutCancelView from "./src/components/CheckoutCancelView";
import Spinner from "./src/components/Spinner";
import LoadingScreen from "./src/components/LoadingScreen";
import { RoutePortalProvider } from "./src/components/layout/RoutePortalHost";
import { startBazaarInboxStore } from "./src/services/bazaarInboxStore";
import { startDashboardDailyStore } from "./src/services/dashboardDailyStore";
import RoddoWelcomeCard from "./src/components/onboarding/RoddoWelcomeCard";
import RoddoGuidedTour from "./src/components/onboarding/RoddoGuidedTour";
import { RODDO_GUIDED_TOUR_VERSION } from "./src/components/onboarding/tourSteps";
import ToastContainer from "./src/components/ToastContainer";
import DailyRewardModal from "./src/components/DailyRewardModal";
import RoddoConfirmDialog from "./src/components/RoddoConfirmDialog";
import NotificationsView from "./src/components/NotificationsView";
import AchievementsView from "./src/components/AchievementsView";

import FishIQView from "./src/components/FishIQView";
import LogCatchView from "./src/components/LogCatchView";
import RoddoInsightCard from "./src/components/RoddoInsightCard";
import TackleBoxView from "./src/components/TackleBoxView";
import CommunityView from "./src/components/CommunityView";
import CrewView from "./src/components/CrewView";
import CrewSettingsView from "./src/components/CrewSettingsView";
import CatchHistoryView from "./src/components/CatchHistoryView";
import GearHubView from "./src/components/GearHubView";
import PurchaseHistoryView from "./src/components/PurchaseHistoryView";
import ReceiptView from "./src/components/ReceiptView";
import LegalSettingsView from "./src/components/LegalSettingsView";
import TermsOfServiceView from "./src/components/TermsOfServiceView";
import PrivacyPolicyView from "./src/components/PrivacyPolicyView";
import SupportView from "./src/components/SupportView";
import RoddoRadioView from "./src/components/RoddoRadioView";
import { RoddoRadioProvider } from "./src/context/RoddoRadioProvider";
import {
  claimDailyReward,
  getDailyRewardPreview,
} from "./src/services/dailyRewards";

// Layout shells
import CommunityShell from "./src/components/layout/CommunityShell";
import ChallengesShell from "./src/components/layout/ChallengesShell";
import AchievementsShell from "./src/components/layout/AchievementsShell";
import FishIQShell from "./src/components/layout/FishIQShell";
import LicenseDetailShell from "./src/components/layout/LicenseDetailShell";
import SettingsShell from "./src/components/layout/SettingsShell";
import SellerDashboardShell from "./src/components/layout/SellerDashboardShell";
import AdminShell from "./src/components/layout/AdminShell";
import NotificationsShell from "./src/components/layout/NotificationsShell";

// Map HUD
import MapHudButton from "./src/components/MapHudButton";

function isProbablyNetworkError(e: any): boolean {
  const msg = String(e?.message || e || "");
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("Load failed") ||
    msg.includes("ERR_SSL_PROTOCOL_ERROR")
  );
}

function installWebGlobalErrorGuards() {
  // Only on web
  const isNative = (() => {
    try {
      return (window as any)?.Capacitor?.isNativePlatform?.() === true;
    } catch {
      return false;
    }
  })();
  if (isNative) return;

  // Prevent "network-ish" async errors from escalating to a full fatal overlay.
  window.addEventListener("unhandledrejection", (ev) => {
    try {
      if (isProbablyNetworkError((ev as any).reason)) {
        console.warn(
          "[web-guard] swallowed unhandledrejection (network-ish)",
          (ev as any).reason,
        );
        ev.preventDefault();
      }
    } catch {}
  });

  window.addEventListener("error", (ev) => {
    try {
      const anyEv: any = ev as any;
      const err = anyEv?.error || anyEv?.message || "";
      if (isProbablyNetworkError(err)) {
        console.warn("[web-guard] swallowed window.error (network-ish)", err);
        ev.preventDefault();
      }
    } catch {}
  });
}

// ------------------------------------------------------------
// Local escrow mapping type (for SellerDashboardView)
// ------------------------------------------------------------
type EscrowInfo = {
  transactionId: string;
  amountTotal: number;
  currency: string;
};

// ------------------------------------------------------------
// Checkout status handler (HARDENED: runs once, no rerender churn)
// ------------------------------------------------------------
interface CheckoutStatusHandlerProps {
  addToast: (message: string, type?: ToastType) => void;
  onCheckoutResult?: (
    status: "success" | "cancel",
    itemId?: string | null,
    stripeSessionId?: string | null,
  ) => void;
}

const CheckoutStatusHandler: React.FC<CheckoutStatusHandlerProps> = ({
  addToast,
  onCheckoutResult,
}) => {
  const handledRef = React.useRef(false);
  const onCheckoutResultRef =
    React.useRef<typeof onCheckoutResult>(onCheckoutResult);

  // keep latest callback without re-triggering the main effect
  React.useEffect(() => {
    onCheckoutResultRef.current = onCheckoutResult;
  }, [onCheckoutResult]);

  React.useEffect(() => {
    if (handledRef.current) return;

    const params = new URLSearchParams(window.location.search);

    const statusParamRaw = params.get("status");
    const itemIdParam = params.get("itemId");
    const stripeSessionIdParam = params.get("session_id");

    const statusParam =
      (statusParamRaw as "success" | "cancel" | null) ??
      (stripeSessionIdParam ? "success" : null);

    if (!statusParam) return;

    handledRef.current = true;

    if (statusParam === "success") addToast("Purchase completed!", "success");
    else addToast("Checkout was cancelled.", "info");

    // clean everything we used
    [
      "status",
      "itemId",
      "session_id",
      "amountCents",
      "itemName",
      "receiptUrl",
    ].forEach((k) => params.delete(k));

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", newUrl);

    onCheckoutResultRef.current?.(
      statusParam,
      itemIdParam ?? null,
      stripeSessionIdParam ?? null,
    );
  }, [addToast]);

  return null;
};

// ─────────────────────────────────────────────────────────────
// Weather helpers (REAL weather + reverse geocode + AI insight)
// ─────────────────────────────────────────────────────────────
type LatLon = { lat: number; lon: number };
const DEFAULT_COORDS_LA: LatLon = { lat: 34.0522, lon: -118.2437 };

const distanceMeters = (a: LatLon, b: LatLon) => {
  const R = 6371e3;
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180;
  const Δλ = ((b.lon - a.lon) * Math.PI) / 180;

  const x =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  return R * (2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
};

const conditionFromOpenMeteoCode = (code: number) => {
  if (code === 0) return "Sunny";
  if (code === 1 || code === 2) return "Partly Cloudy";
  if (code === 3) return "Cloudy";
  if ([45, 48].includes(code)) return "Cloudy";
  if ([51, 53, 55, 56, 57].includes(code)) return "Rainy";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "Rainy";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";
  if ([95, 96, 99].includes(code)) return "Stormy";
  return "Cloudy";
};

const fmtWind = (kmh: number) => `${Math.round(kmh)} km/h`;

const fmtLocalTime = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const fmtForecastDay = (value?: string | null) => {
  if (!value) return "Day";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString([], { weekday: "short" });
};

const moonPhaseName = (date = new Date()) => {
  // Lightweight lunar age approximation. Good enough for fishing-context labels; not an astronomy guarantee.
  const synodicMonth = 29.530588853;
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0);
  const days = (date.getTime() - knownNewMoon) / 86400000;
  const age = ((days % synodicMonth) + synodicMonth) % synodicMonth;

  if (age < 1.84566) return "New Moon";
  if (age < 5.53699) return "Waxing Crescent";
  if (age < 9.22831) return "First Quarter";
  if (age < 12.91963) return "Waxing Gibbous";
  if (age < 16.61096) return "Full Moon";
  if (age < 20.30228) return "Waning Gibbous";
  if (age < 23.99361) return "Last Quarter";
  if (age < 27.68493) return "Waning Crescent";
  return "New Moon";
};


const buildBiteWindowsV2 = (
  sunriseRaw?: string,
  sunsetRaw?: string,
  moonPhaseRaw?: string,
) => {
  const sunrise = sunriseRaw ? new Date(sunriseRaw) : null;
  const sunset = sunsetRaw ? new Date(sunsetRaw) : null;
  const moon = String(moonPhaseRaw || "").toLowerCase();
  const moonBoost = moon.includes("new") || moon.includes("full");

  const windows: any[] = [];

  if (sunrise && !Number.isNaN(sunrise.getTime())) {
    const start = new Date(sunrise);
    const end = new Date(sunrise);
    start.setMinutes(start.getMinutes() - 60);
    end.setMinutes(end.getMinutes() + 90);
    windows.push({
      type: "Major",
      window: `${fmtLocalTime(start.toISOString())} - ${fmtLocalTime(end.toISOString())}`,
      reason: "Dawn low-light feed",
    });
  }

  if (sunset && !Number.isNaN(sunset.getTime())) {
    const start = new Date(sunset);
    const end = new Date(sunset);
    start.setMinutes(start.getMinutes() - 90);
    end.setMinutes(end.getMinutes() + 60);
    windows.push({
      type: "Major",
      window: `${fmtLocalTime(start.toISOString())} - ${fmtLocalTime(end.toISOString())}`,
      reason: "Dusk transition bite",
    });
  }

  windows.push({
    type: "Minor",
    window: moonBoost ? "Late night edges" : "Midday shade",
    reason: moonBoost
      ? "Moon phase can shift feeding"
      : "Structure, shade, and depth changes",
  });

  windows.push({
    type: "Minor",
    window: "Weather-change pockets",
    reason: "Wind lanes, pressure changes, and cloud breaks",
  });

  return windows.slice(0, 4);
};

const buildBiteScoreV2 = (opts: {
  condition: string;
  windKmh: number;
  humidityPct: number | null;
  moonPhase: string;
  hasTide: boolean;
}) => {
  const kind = String(opts.condition || "").toLowerCase();
  let score = 58;

  if (kind.includes("cloud")) score += 12;
  if (kind.includes("partly")) score += 7;
  if (kind.includes("rain")) score += 3;
  if (kind.includes("storm") || kind.includes("snow")) score -= 22;
  if (Number.isFinite(opts.windKmh)) {
    if (opts.windKmh >= 35) score -= 16;
    else if (opts.windKmh >= 16) score += 8;
    else if (opts.windKmh <= 4) score -= 4;
  }
  if (
    typeof opts.humidityPct === "number" &&
    opts.humidityPct >= 72 &&
    !kind.includes("storm")
  )
    score += 4;
  if (/new moon|full moon/i.test(opts.moonPhase)) score += 5;
  if (opts.hasTide) score += 7;

  return Math.max(8, Math.min(96, Math.round(score)));
};

type TideStationLite = { id: string; name: string; lat: number; lon: number };

const tideCacheKeyFor = (coords: LatLon) =>
  `roddo.tides.v2.${coords.lat.toFixed(2)}.${coords.lon.toFixed(2)}`;

const readJsonCache = <T,>(key: string, maxAgeMs: number): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - Number(parsed.ts) > maxAgeMs) return null;
    return parsed.data as T;
  } catch {
    return null;
  }
};

const writeJsonCache = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

const getNoaaStations = async (): Promise<TideStationLite[]> => {
  const cacheKey = "roddo.noaa.tideStations.v1";
  const cached = readJsonCache<TideStationLite[]>(
    cacheKey,
    7 * 24 * 60 * 60 * 1000,
  );
  if (cached?.length) return cached;

  const res = await fetch(
    "https://api.tidesandcurrents.noaa.gov/mdapi/prod/webapi/stations.json?type=waterlevels&units=english",
  );
  if (!res.ok) throw new Error(`NOAA stations failed: ${res.status}`);
  const data = await res.json();
  const stations = (Array.isArray(data?.stations) ? data.stations : [])
    .map((s: any) => ({
      id: String(s?.id || "").trim(),
      name: String(s?.name || "").trim(),
      lat: Number(s?.lat),
      lon: Number(s?.lng ?? s?.lon),
    }))
    .filter(
      (s: TideStationLite) =>
        s.id && s.name && Number.isFinite(s.lat) && Number.isFinite(s.lon),
    );

  writeJsonCache(cacheKey, stations);
  return stations;
};

const nearestTideStation = async (
  coords: LatLon,
  maxDistanceMeters = 120000,
) => {
  const stations = await getNoaaStations();
  let best: (TideStationLite & { distanceMeters: number }) | null = null;

  for (const station of stations) {
    const distance = distanceMeters(coords, {
      lat: station.lat,
      lon: station.lon,
    });
    if (!best || distance < best.distanceMeters)
      best = { ...station, distanceMeters: distance };
  }

  return best && best.distanceMeters <= maxDistanceMeters ? best : null;
};

const yyyymmdd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
};

const fetchTidesForCoords = async (coords: LatLon) => {
  const cacheKey = tideCacheKeyFor(coords);
  const cached = readJsonCache<any>(cacheKey, 2 * 60 * 60 * 1000);
  if (cached) return cached;

  const station = await nearestTideStation(coords);
  if (!station) {
    return {
      mode: "coastal_no_station",
      message:
        "Coastal water found, but no nearby NOAA tide station was available.",
      events: [],
    };
  }

  const start = new Date();
  const end = new Date(start);
  end.setDate(end.getDate() + 2);

  const url =
    `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter` +
    `?product=predictions` +
    `&application=roddo` +
    `&begin_date=${yyyymmdd(start)}` +
    `&end_date=${yyyymmdd(end)}` +
    `&datum=MLLW` +
    `&station=${encodeURIComponent(station.id)}` +
    `&time_zone=lst_ldt` +
    `&units=english` +
    `&interval=hilo` +
    `&format=json`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`NOAA tides failed: ${res.status}`);
  const data = await res.json();
  const now = Date.now();

  const events = (Array.isArray(data?.predictions) ? data.predictions : [])
    .map((p: any) => {
      const rawTime = String(p?.t || "");
      const eventDate = new Date(rawTime.replace(" ", "T"));
      return {
        type: String(p?.type || "").toUpperCase() === "H" ? "High" : "Low",
        time: Number.isNaN(eventDate.getTime())
          ? rawTime
          : fmtLocalTime(eventDate.toISOString()),
        value: p?.v != null ? `${Number(p.v).toFixed(1)} ft` : "",
        ts: Number.isNaN(eventDate.getTime()) ? 0 : eventDate.getTime(),
      };
    })
    .filter((p: any) => !p.ts || p.ts >= now - 60 * 60 * 1000)
    .slice(0, 8);

  const nextHigh = events.find((e: any) => e.type === "High");
  const nextLow = events.find((e: any) => e.type === "Low");

  const result = {
    mode: "coastal",
    stationId: station.id,
    stationName: station.name,
    nextHigh: nextHigh
      ? `${nextHigh.time}${nextHigh.value ? ` · ${nextHigh.value}` : ""}`
      : "",
    nextLow: nextLow
      ? `${nextLow.time}${nextLow.value ? ` · ${nextLow.value}` : ""}`
      : "",
    events,
    message: `Nearest NOAA station: ${station.name}`,
  };

  writeJsonCache(cacheKey, result);
  return result;
};

const fetchMarineForCoords = async (coords: LatLon) => {
  try {
    const url =
      `https://marine-api.open-meteo.com/v1/marine` +
      `?latitude=${encodeURIComponent(coords.lat)}` +
      `&longitude=${encodeURIComponent(coords.lon)}` +
      `&current=wave_height,wave_direction,wave_period` +
      `&length_unit=imperial` +
      `&timezone=auto`;

    const res = await fetch(url);
    if (!res.ok) return { isCoastal: false, marine: null };
    const data = await res.json();
    const cur = data?.current || {};
    const waveHeight = Number(cur?.wave_height);

    if (!Number.isFinite(waveHeight)) return { isCoastal: false, marine: null };

    return {
      isCoastal: true,
      marine: {
        waveHeight: `${Math.round(waveHeight * 10) / 10} ft`,
        waveDirection: Number.isFinite(Number(cur?.wave_direction))
          ? `${Math.round(Number(cur.wave_direction))}°`
          : undefined,
        wavePeriod: Number.isFinite(Number(cur?.wave_period))
          ? `${Math.round(Number(cur.wave_period))}s`
          : undefined,
      },
    };
  } catch {
    return { isCoastal: false, marine: null };
  }
};

const fetchRealWeather = async (
  coords: LatLon,
  tempUnit: "Celsius" | "Fahrenheit",
): Promise<any> => {
  const omTempUnit = tempUnit === "Fahrenheit" ? "fahrenheit" : "celsius";

  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${encodeURIComponent(coords.lat)}` +
    `&longitude=${encodeURIComponent(coords.lon)}` +
    `&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,precipitation,cloud_cover,pressure_msl,visibility` +
    `&hourly=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,precipitation_probability,precipitation,cloud_cover,pressure_msl,visibility` +
    `&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset` +
    `&forecast_days=3` +
    `&temperature_unit=${omTempUnit}` +
    `&wind_speed_unit=kmh` +
    `&timezone=auto`;

  const res = await fetch(url, { cache: "no-store" });
  console.debug("[weather] status", res.status, {
    lat: coords.lat,
    lon: coords.lon,
    tempUnit,
  });

  if (!res.ok) throw new Error(`OpenMeteo failed: ${res.status}`);
  const data = await res.json();

  const cur = data?.current ?? {};
  const temp =
    typeof cur.temperature_2m === "number" ? cur.temperature_2m : NaN;
  const feelsLike =
    typeof cur.apparent_temperature === "number"
      ? cur.apparent_temperature
      : NaN;
  const windKmh =
    typeof cur.wind_speed_10m === "number" ? cur.wind_speed_10m : NaN;
  const code = typeof cur.weather_code === "number" ? cur.weather_code : 3;
  const condition = conditionFromOpenMeteoCode(code);

  const humidityPct =
    typeof cur.relative_humidity_2m === "number"
      ? Math.round(cur.relative_humidity_2m)
      : null;
  const humidity = humidityPct == null ? "—" : `${humidityPct}%`;

  const cloudsPct =
    typeof cur.cloud_cover === "number" ? Math.round(cur.cloud_cover) : null;
  const pressureHpa =
    typeof cur.pressure_msl === "number" ? Math.round(cur.pressure_msl) : null;
  const visibilityKm =
    typeof cur.visibility === "number"
      ? Math.round((cur.visibility / 1000) * 10) / 10
      : null;
  const precipMm =
    typeof cur.precipitation === "number"
      ? Math.round(cur.precipitation * 10) / 10
      : null;

  const daily = data?.daily || {};
  const sunriseRaw = Array.isArray(daily?.sunrise)
    ? daily.sunrise[0]
    : undefined;
  const sunsetRaw = Array.isArray(daily?.sunset)
    ? daily.sunset[0]
    : undefined;
  const nowIso: string | undefined = cur?.time;
  const moonPhase = moonPhaseName(new Date());

  const forecast = Array.isArray(daily?.time)
    ? daily.time
        .map((day: string, idx: number) => ({
          day: fmtForecastDay(day),
          high: Math.round(Number(daily?.temperature_2m_max?.[idx] ?? 0)),
          low: Math.round(Number(daily?.temperature_2m_min?.[idx] ?? 0)),
          condition: conditionFromOpenMeteoCode(
            Number(daily?.weather_code?.[idx] ?? 3),
          ),
        }))
        .slice(0, 3)
    : [];

  const hourlyTimes: string[] = Array.isArray(data?.hourly?.time)
    ? data.hourly.time
    : [];

  const currentTimeMs = nowIso ? new Date(nowIso).getTime() : Date.now();
  let hourlyStartIndex = 0;
  if (hourlyTimes.length) {
    let bestDiff = Number.POSITIVE_INFINITY;
    hourlyTimes.forEach((value, idx) => {
      const ms = new Date(value).getTime();
      const diff = Number.isFinite(ms) ? Math.abs(ms - currentTimeMs) : idx;
      if (diff < bestDiff) {
        bestDiff = diff;
        hourlyStartIndex = idx;
      }
    });
  }

  const hourly = hourlyTimes
    .slice(hourlyStartIndex, hourlyStartIndex + 24)
    .map((time, offset) => {
      const idx = hourlyStartIndex + offset;
      const rawTemp = Number(data?.hourly?.temperature_2m?.[idx]);
      const rawFeels = Number(data?.hourly?.apparent_temperature?.[idx]);
      const rawHumidity = Number(data?.hourly?.relative_humidity_2m?.[idx]);
      const rawWind = Number(data?.hourly?.wind_speed_10m?.[idx]);
      const rawPop = Number(data?.hourly?.precipitation_probability?.[idx]);
      const rawPrecip = Number(data?.hourly?.precipitation?.[idx]);
      const rawClouds = Number(data?.hourly?.cloud_cover?.[idx]);
      const rawPressure = Number(data?.hourly?.pressure_msl?.[idx]);
      const rawVisibility = Number(data?.hourly?.visibility?.[idx]);
      const hourlyCode = Number(data?.hourly?.weather_code?.[idx]);

      return {
        time,
        label: new Date(time).toLocaleTimeString([], {
          hour: "numeric",
        }),
        temp: Number.isFinite(rawTemp)
          ? Math.round(rawTemp * 10) / 10
          : null,
        feelsLike: Number.isFinite(rawFeels)
          ? Math.round(rawFeels * 10) / 10
          : null,
        humidity: Number.isFinite(rawHumidity)
          ? Math.round(rawHumidity)
          : null,
        windKmh: Number.isFinite(rawWind)
          ? Math.round(rawWind * 10) / 10
          : null,
        precipitationProbability: Number.isFinite(rawPop)
          ? Math.round(rawPop)
          : null,
        precipitation: Number.isFinite(rawPrecip)
          ? Math.round(rawPrecip * 10) / 10
          : null,
        cloudCover: Number.isFinite(rawClouds)
          ? Math.round(rawClouds)
          : null,
        pressure: Number.isFinite(rawPressure)
          ? Math.round(rawPressure)
          : null,
        visibilityKm: Number.isFinite(rawVisibility)
          ? Math.round((rawVisibility / 1000) * 10) / 10
          : null,
        condition: conditionFromOpenMeteoCode(
          Number.isFinite(hourlyCode) ? hourlyCode : 3,
        ),
      };
    });

  const marineRead = await fetchMarineForCoords(coords);
  let tides: any = {
    mode: "inland",
    message:
      "No coastal tide station detected. Using moon, weather, and fishing context instead.",
    events: [],
  };

  if (marineRead.isCoastal) {
    try {
      tides = await fetchTidesForCoords(coords);
    } catch (tideErr) {
      console.warn("[weather] tides soft fail", tideErr);
      tides = {
        mode: "coastal_unavailable",
        message:
          "Coastal water detected, but tide data is temporarily unavailable.",
        events: [],
      };
    }
  }

  const biteWindows = buildBiteWindowsV2(sunriseRaw, sunsetRaw, moonPhase);
  const fishBiteScore = buildBiteScoreV2({
    condition,
    windKmh: Number.isFinite(windKmh) ? windKmh : 0,
    humidityPct,
    moonPhase,
    hasTide: Boolean(tides?.nextHigh || tides?.nextLow),
  });

  return {
    current: {
      temp: Number.isFinite(temp) ? Math.round(temp * 10) / 10 : "--",
      feelsLike: Number.isFinite(feelsLike)
        ? Math.round(feelsLike * 10) / 10
        : "--",
      condition,
      wind: Number.isFinite(windKmh) ? fmtWind(windKmh) : "—",
      humidity,
      clouds: cloudsPct == null ? "—" : `${cloudsPct}%`,
      pressure: pressureHpa == null ? "—" : `${pressureHpa} hPa`,
      visibility: visibilityKm == null ? "—" : `${visibilityKm} km`,
      precipitation: precipMm == null ? "—" : `${precipMm} mm`,
      moonPhase,
      sunrise: fmtLocalTime(sunriseRaw),
      sunset: fmtLocalTime(sunsetRaw),
      nextHighTide: tides?.nextHigh || undefined,
      nextLowTide: tides?.nextLow || undefined,
      observedAt: nowIso || new Date().toISOString(),
    },
    hourly,
    forecast,
    moonPhase,
    sunrise: fmtLocalTime(sunriseRaw),
    sunset: fmtLocalTime(sunsetRaw),
    astronomy: {
      moonPhase,
      sunrise: fmtLocalTime(sunriseRaw),
      sunset: fmtLocalTime(sunsetRaw),
    },
    tides,
    marine: marineRead.marine || undefined,
    biteWindows,
    fishBiteScore,
    fishIQHints: [],
    source: "Open-Meteo",
    provider: "Open-Meteo",
    timezone: data?.timezone || null,
    utcOffsetSeconds: Number(data?.utc_offset_seconds || 0),
    fetchedAt: Date.now(),
    coords: {
      lat: Number(coords.lat),
      lon: Number(coords.lon),
    },
  };
};

const reverseGeocode = async (coords: LatLon): Promise<string | null> => {
  try {
    return await reverseGeocodeServer(coords.lat, coords.lon);
  } catch (e) {
    console.debug("[reverseGeocode] coords", coords);
    console.warn("[reverseGeocode] failed", e);
    return null;
  }
};

// ─────────────────────────────────────────────────────────────
// Legal / Consent helpers
// ─────────────────────────────────────────────────────────────
type LegalAcceptancePatch = {
  fishiqContributionEnabled?: boolean;
  ageBracket: RoddoAgeBracket;
  ageVerifiedAt: number;
  ageEligibility: ReturnType<typeof buildRoddoAgeEligibilityRecord>;
  legal: {
    schemaVersion: string;
    tos: { version: string; acceptedAt: number };
    privacy: { version: string; acceptedAt: number };
    consents: { fishiqContribution: { enabled: boolean; updatedAt: number } };
    minor: {
      isMinor: boolean;
      ageBracket: RoddoAgeBracket;
      parentalConsent: {
        required: boolean;
        received: boolean;
        receivedAt?: number;
        method?:
          | "email_verification"
          | "signed_form"
          | "credit_card_check"
          | "other";
        parentEmail?: string;
      };
    };
  };
};

function normalizeProgressionKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/__all_time$/, "");
}

function buildCompletedMapFromBackend(
  rows: Record<string, BackendChallengeCompletion>,
): Record<string, any> {
  const out: Record<string, any> = {};

  Object.entries(rows || {}).forEach(([rawKey, row]) => {
    const key = normalizeProgressionKey(rawKey || row?.key || row?.id);
    if (!key) return;

    out[key] = {
      challengeId: key,
      id: row?.id,
      key,
      rewardXP: Number(row?.rewardXp ?? 0),
      completedAt: Number(row?.completedAtMs ?? 0),
      title: row?.title,
      description: row?.description,
    };
  });

  return out;
}

function buildCompletedMapFromAchievements(
  rows: Record<string, BackendAchievement>,
): Record<string, any> {
  const out: Record<string, any> = {};

  Object.entries(rows || {}).forEach(([rawKey, row]) => {
    const key = normalizeProgressionKey(rawKey || row?.key || row?.id);
    if (!key) return;

    out[key] = {
      challengeId: key,
      id: row?.id,
      key,
      rewardXP: Number(row?.rewardXp ?? 0),
      completedAt: Number(row?.unlockedAtMs ?? 0),
      title: row?.title,
      description: row?.description,
    };
  });

  return out;
}

function diffNewKeys(
  prevMap: Record<string, any>,
  nextMap: Record<string, any>,
): string[] {
  const prevKeys = new Set(Object.keys(prevMap || {}));
  return Object.keys(nextMap || {}).filter((key) => !prevKeys.has(key));
}

const safeReputationNumber = (value: any, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeProfileSavedSpotTimestamp = (value: any): number => {
  if (typeof value?.toMillis === "function") {
    const millis = Number(value.toMillis());
    return Number.isFinite(millis) ? millis : 0;
  }
  if (Number.isFinite(Number(value?.seconds)))
    return Number(value.seconds) * 1000;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeProfileSavedSpotsForKey = (input: any): any[] => {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: any[] = [];

  input.forEach((spot: any, idx: number) => {
    const lat = Number(spot?.lat ?? spot?.position?.lat);
    const lng = Number(
      spot?.lng ?? spot?.lon ?? spot?.position?.lng ?? spot?.position?.lon,
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const kindRaw = String(
      spot?.kind ||
        spot?.type ||
        spot?.category ||
        spot?.spotType ||
        "favorite",
    )
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    const kind =
      kindRaw === "launch" ||
      kindRaw === "boat" ||
      kindRaw === "ramp" ||
      kindRaw === "boat_launch"
        ? "launch"
        : kindRaw === "sunset" || kindRaw === "view" || kindRaw === "scenic"
          ? "sunset"
          : kindRaw === "fish" ||
              kindRaw === "fishing" ||
              kindRaw === "fishspot" ||
              kindRaw === "fish_spot" ||
              kindRaw === "fish-spot"
            ? "fish"
            : kindRaw === "food" ||
                kindRaw === "restaurant" ||
                kindRaw === "eat" ||
                kindRaw === "meal" ||
                kindRaw === "foodspot" ||
                kindRaw === "food_spot" ||
                kindRaw === "food-spot"
              ? "food"
              : "favorite";
    const name = String(spot?.name || spot?.title || kind).trim() || kind;
    const item = {
      id: String(spot?.id || `saved_${idx}`),
      name,
      kind,
      lat: Number(lat.toFixed(6)),
      lng: Number(lng.toFixed(6)),
      note: String(spot?.note || ""),
      createdAt: normalizeProfileSavedSpotTimestamp(
        spot?.createdAt ?? spot?.savedAt ?? spot?.timestamp,
      ),
    };

    const key = [item.kind, item.name.toLowerCase(), item.lat, item.lng].join(
      "|",
    );
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });

  return out.sort(
    (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0),
  );
};

const normalizeUserProfileReputation = <
  T extends Record<string, any> | null | undefined,
>(
  input: T,
): T => {
  if (!input) return input;

  const existing = (input as any)?.reputation;

  // Reputation is server-authoritative. Keep an empty shaped summary while a
  // fresh account is waiting for its first backend reputation write, but never
  // synthesize trust badges from loose client stats, logs, or cached counters.
  const reputation = existing?.tracks
    ? existing
    : buildReputationSummary({});

  return {
    ...(input as any),
    reputation,
  } as T;
};

const stableProfileSnapshotKey = (profile: any): string => {
  if (!profile) return "";

  const savedSpots = normalizeProfileSavedSpotsForKey(profile?.savedSpots);

  return JSON.stringify({
    uid: String(profile?.uid || profile?.id || ""),
    name: String(profile?.name || ""),
    displayName: String(profile?.displayName || ""),
    username: String(profile?.username || ""),
    avatar: String(
      profile?.avatar ||
        profile?.avatarUrl ||
        profile?.photoURL ||
        profile?.photoUrl ||
        profile?.profilePhotoUrl ||
        profile?.imageUrl ||
        "",
    ),
    avatarUpdatedAt: Number(profile?.avatarUpdatedAt || 0),
    onboardingCompleted: !!profile?.onboardingCompleted,
    legal: profile?.legal || null,
    notificationSettings: profile?.notificationSettings || null,
    xp: Number(profile?.xp || profile?.points || 0),
    cosmeticLoadout: profile?.cosmeticLoadout || null,
    cosmeticLoadoutUpdatedAt: Number(profile?.cosmeticLoadoutUpdatedAt || 0),
    shells: Number(profile?.shells ?? profile?.shellBalance ?? 0),
    lifetimeShells: Number(profile?.lifetimeShells ?? 0),
    searchLures: Number(profile?.searchLures ?? 0),
    searchLureRegenAnchorAtMs: Number(profile?.searchLureRegenAnchorAtMs ?? 0),
    lifetimeSearchLuresGranted: Number(profile?.lifetimeSearchLuresGranted ?? 0),
    lifetimeSearchLuresSpent: Number(profile?.lifetimeSearchLuresSpent ?? 0),
    reputation: profile?.reputation || null,
    dailyRewards: profile?.dailyRewards || null,
    stats: profile?.stats || null,
    achievements: profile?.achievements || null,
    completedChallenges: profile?.completedChallenges || null,
    foundingMember: profile?.foundingMember || null,
    entitlements: profile?.entitlements || null,
    equippedGear: {
      rodId: profile?.equippedGear?.rodId ?? null,
      reelId: profile?.equippedGear?.reelId ?? null,
      lureOrBaitId:
        profile?.equippedGear?.lureOrBaitId ??
        profile?.equippedGear?.lureId ??
        null,
      lureId:
        profile?.equippedGear?.lureId ??
        profile?.equippedGear?.lureOrBaitId ??
        null,
      otherId: profile?.equippedGear?.otherId ?? null,
      useTackleBoxSets: !!profile?.equippedGear?.useTackleBoxSets,
    },
    savedSpots,
  });
};

const profileToAppUser = (profile: any): AppUser => {
  const normalized = normalizeUserProfileReputation(profile as any) as any;
  return {
    ...(normalized || {}),
    uid: String(normalized?.uid || ""),
    name:
      normalized?.name ||
      normalized?.displayName ||
      normalized?.username ||
      "Angler",
    avatar:
      normalized?.avatar ||
      normalized?.avatarUrl ||
      normalized?.photoURL ||
      normalized?.photoUrl ||
      "",
    xp: safeReputationNumber(
      normalized?.xp,
      safeReputationNumber(normalized?.points, 0),
    ),
    points: safeReputationNumber(
      normalized?.points,
      safeReputationNumber(normalized?.xp, 0),
    ),
    level: Math.max(1, safeReputationNumber(normalized?.level, 1)),
    reputation: normalized?.reputation,
  } as AppUser;
};

const withAuthorReputation = (
  post: CommunityPost,
  usersMap: Record<string, AppUser>,
  currentProfile: UserProfile | null,
): CommunityPost => {
  const authorId = String((post as any)?.authorId || "");
  const authorUser = authorId ? usersMap?.[authorId] : undefined;
  const fallbackReputation =
    (post as any)?.authorReputation?.reputation ||
    (post as any)?.authorReputation ||
    authorUser?.reputation ||
    (currentProfile?.uid === authorId
      ? (currentProfile as any)?.reputation
      : undefined);

  if (!fallbackReputation) return post;

  return {
    ...(post as any),
    authorReputation: {
      primaryBadgeKey:
        (post as any)?.authorReputation?.primaryBadgeKey ??
        fallbackReputation?.primaryBadgeKey ??
        null,
      featuredBadgeKeys:
        (post as any)?.authorReputation?.featuredBadgeKeys ??
        fallbackReputation?.featuredBadgeKeys ??
        [],
      reputation: fallbackReputation,
    },
  } as CommunityPost;
};

const RODDO_INVITE_CODE_KEY = "roddo_invite_code";
const RODDO_AUTH_INTENT_KEY = "roddo_auth_intent";

declare global {
  interface Window {
    __roddoSignupAccessBootstrapPending?: boolean;
  }
}

const isSignupAccessBootstrapPending = (): boolean => {
  // Only the live in-memory flag may pause App's access check.
  // A persisted signup intent can survive a failed/abandoned signup on Safari
  // and must never hold the app on “Checking access…” indefinitely.
  try {
    return window.__roddoSignupAccessBootstrapPending === true;
  } catch {
    return false;
  }
};

const readStoredInviteCode = (): string => {
  try {
    const sessionCode = String(
      window.sessionStorage.getItem(RODDO_INVITE_CODE_KEY) || "",
    )
      .trim()
      .toUpperCase();
    if (sessionCode) return sessionCode;
  } catch {}

  try {
    const localCode = String(
      window.localStorage.getItem(RODDO_INVITE_CODE_KEY) || "",
    )
      .trim()
      .toUpperCase();
    if (localCode) return localCode;
  } catch {}

  return "";
};

const writeStoredInviteCode = (code: string) => {
  const cleanCode = String(code || "")
    .trim()
    .toUpperCase();
  try {
    if (cleanCode)
      window.sessionStorage.setItem(RODDO_INVITE_CODE_KEY, cleanCode);
    else window.sessionStorage.removeItem(RODDO_INVITE_CODE_KEY);
  } catch {}
  try {
    if (cleanCode)
      window.localStorage.setItem(RODDO_INVITE_CODE_KEY, cleanCode);
    else window.localStorage.removeItem(RODDO_INVITE_CODE_KEY);
  } catch {}
};

const clearStoredInviteCode = () => writeStoredInviteCode("");

const clearStoredAuthIntent = () => {
  try {
    window.sessionStorage.removeItem(RODDO_AUTH_INTENT_KEY);
  } catch {}
  try {
    window.localStorage.removeItem(RODDO_AUTH_INTENT_KEY);
  } catch {}
};

const resolveProfileLanguage = (profile: any) => {
  return normalizeRoddoLanguageCode(
    profile?.preferredLanguage ??
      profile?.language ??
      profile?.locale ??
      profile?.appLanguage ??
      profile?.settings?.language,
  );
};

type LegalGateProps = {
  tosVersion: string;
  privacyVersion: string;
  preverifiedAgeBracket?: RoddoAgeBracket | null;
  onAccept: (opts: {
    fishiqContributionEnabled: boolean;
    ageBracket: RoddoAgeBracket;
    parentalConsentReceived: boolean;
  }) => void;
  isSaving: boolean;
  isDarkMode: boolean;
};

const LegalGate: React.FC<LegalGateProps> = ({
  tosVersion,
  privacyVersion,
  preverifiedAgeBracket: profileAgeBracket,
  onAccept,
  isSaving,
  isDarkMode,
}) => {
  const [tosOk, setTosOk] = React.useState(false);
  const [privacyOk, setPrivacyOk] = React.useState(false);
  const [fishiqOk, setFishiqOk] = React.useState(true);
  const [birthMonth, setBirthMonth] = React.useState("");
  const [birthYear, setBirthYear] = React.useState("");
  const [guardianOk, setGuardianOk] = React.useState(false);
  const [deviceAgeBracket] = React.useState<RoddoAgeBracket | null>(() => {
    try {
      const value = window.localStorage.getItem(RODDO_AGE_BRACKET_KEY);
      return value === "13_15" || value === "16_17" || value === "18_PLUS"
        ? value
        : null;
    } catch {
      return null;
    }
  });
  const preverifiedAgeBracket = profileAgeBracket ?? deviceAgeBracket;

  const ageCheck = React.useMemo(
    () => checkRoddoAgeEligibility(birthMonth, birthYear),
    [birthMonth, birthYear],
  );
  const ageBracket = preverifiedAgeBracket ?? ageCheck.bracket;
  const ageCheckValid = Boolean(preverifiedAgeBracket) || ageCheck.valid;
  const isUnder13 = !preverifiedAgeBracket && ageCheck.valid && ageBracket === "U13";
  const isTeen = isRoddoTeenBracket(ageBracket);
  const canAccept =
    tosOk &&
    privacyOk &&
    ageCheckValid &&
    !isUnder13 &&
    (!isTeen || guardianOk);
  const requiredOption =
    "group flex min-h-[70px] items-center gap-3 rounded-[16px] border px-4 py-3 transition " +
    "border-black/[.08] bg-white/70 hover:border-[#2F80ED]/30 hover:bg-white " +
    "dark:border-white/[.09] dark:bg-white/[.035] dark:hover:border-[#9278EC]/35 dark:hover:bg-white/[.055]";
  const checkboxClass =
    "h-5 w-5 shrink-0 rounded accent-[#2F80ED] dark:accent-[#9278EC]";

  return (
    <div className={`fixed inset-0 z-[2000] flex items-center justify-center overflow-y-auto px-4 py-5 sm:px-6 ${isDarkMode ? "dark bg-[#070A12]/96" : "bg-[#F3EADF]/95"}`}>
      <div
        className="w-full max-w-[470px] overflow-hidden rounded-[24px] border shadow-[0_28px_80px_rgba(37,42,52,.18)] dark:shadow-[0_32px_90px_rgba(0,0,0,.48)]"
        style={{
          background: isDarkMode ? "#101727" : "#FFFCF7",
          borderColor: isDarkMode ? "rgba(159,140,220,.20)" : "rgba(93,72,49,.14)",
        }}
      >
        <div className="px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-black/[.07] bg-black/[.025] px-3 py-1.5 text-[10px] font-black uppercase tracking-[.17em] text-[#7A6A59] dark:border-white/[.08] dark:bg-white/[.045] dark:text-[#AAA3BC]">
            <span aria-hidden="true">🎣</span>
            Before you cast
          </div>
          <h2 className="text-[26px] font-black leading-tight tracking-[-.035em] text-[#253648] dark:text-white sm:text-[28px]">
            One quick check
          </h2>
          <p className="mt-2 text-[13px] font-semibold leading-5 text-[#66717C] dark:text-[#AEB7C9]">
            Please accept Roddo&apos;s current Terms and Privacy Policy to continue.
          </p>
        </div>

        <div className="space-y-3 border-t border-black/[.06] px-5 py-5 dark:border-white/[.07] sm:px-6">
          <div className="rounded-[16px] border border-black/[.08] bg-black/[.018] px-4 py-4 dark:border-white/[.08] dark:bg-white/[.025]">
            <div className="text-[12px] font-black text-[#2E3440] dark:text-[#F5F3FF]">Age check</div>
            <p className="mt-1 text-[11px] font-semibold leading-4 text-[#776C61] dark:text-[#919AAD]">
              Enter your birth month and year. Roddo uses them only to determine age eligibility and does not save the entered month/year as your date of birth.
            </p>
            {preverifiedAgeBracket ? (
              <div className="mt-3 rounded-[12px] border border-emerald-500/20 bg-emerald-500/[.06] px-3 py-2.5 text-[11px] font-bold text-emerald-800 dark:text-emerald-200">
                Age eligibility check complete.
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="rounded-[12px] border border-black/[.10] bg-white px-3 py-2.5 text-sm font-bold text-[#2E3440] outline-none dark:border-white/[.10] dark:bg-[#151D2F] dark:text-white"
                  aria-label="Birth month"
                >
                  <option value="">Birth month</option>
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <option key={month} value={month}>{new Date(2000, month - 1, 1).toLocaleString(undefined, { month: "long" })}</option>
                  ))}
                </select>
                <input
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  inputMode="numeric"
                  autoComplete="bday-year"
                  placeholder="Birth year"
                  aria-label="Birth year"
                  className="rounded-[12px] border border-black/[.10] bg-white px-3 py-2.5 text-sm font-bold text-[#2E3440] outline-none dark:border-white/[.10] dark:bg-[#151D2F] dark:text-white"
                />
              </div>
            )}
            {isUnder13 ? (
              <div className="mt-3 rounded-[12px] border border-red-500/20 bg-red-50 px-3 py-2.5 text-[11px] font-bold leading-4 text-red-800 dark:bg-red-500/10 dark:text-red-200">
                Based on the age entered, Roddo cannot continue account access.
              </div>
            ) : null}
            {isTeen ? (
              <label className="mt-3 flex items-start gap-2 text-[11px] font-semibold leading-4 text-[#5F5448] dark:text-[#B9C0D0]">
                <input type="checkbox" checked={guardianOk} onChange={(e) => setGuardianOk(e.target.checked)} className={`${checkboxClass} mt-0.5`} />
                <span>I have permission from a parent or legal guardian to use Roddo where that permission is required.</span>
              </label>
            ) : null}
          </div>

          <label className={requiredOption}>
            <input type="checkbox" className={checkboxClass} checked={tosOk} onChange={(e) => setTosOk(e.target.checked)} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-[#2E3440] dark:text-[#F5F3FF]">Terms of Service</span>
              <span className="mt-1 block text-[11px] font-semibold text-[#8A8177] dark:text-[#8F96AA]">Required · Version {tosVersion}</span>
            </span>
            <a href="https://roddo.app/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black text-[#2F80ED] hover:bg-[#2F80ED]/8 dark:text-[#A997F0] dark:hover:bg-[#9278EC]/10">Read ↗</a>
          </label>

          <label className={requiredOption}>
            <input type="checkbox" className={checkboxClass} checked={privacyOk} onChange={(e) => setPrivacyOk(e.target.checked)} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-[#2E3440] dark:text-[#F5F3FF]">Privacy Policy</span>
              <span className="mt-1 block text-[11px] font-semibold text-[#8A8177] dark:text-[#8F96AA]">Required · Version {privacyVersion}</span>
            </span>
            <a href="https://roddo.app/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black text-[#2F80ED] hover:bg-[#2F80ED]/8 dark:text-[#A997F0] dark:hover:bg-[#9278EC]/10">Read ↗</a>
          </label>

          <label className={`flex items-start gap-3 rounded-[16px] border px-4 py-3.5 transition ${fishiqOk ? "border-emerald-500/20 bg-emerald-500/[.06] dark:border-emerald-300/16 dark:bg-emerald-400/[.07]" : "border-black/[.07] bg-black/[.018] dark:border-white/[.07] dark:bg-white/[.025]"}`}>
            <input type="checkbox" className={`${checkboxClass} mt-0.5`} checked={fishiqOk} onChange={(e) => setFishiqOk(e.target.checked)} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-black text-[#2E3440] dark:text-[#F5F3FF]">Help improve Roddo <span className="font-bold opacity-45">· Optional</span></span>
              <span className="mt-1 block text-[11px] font-semibold leading-4 text-[#6F7D78] dark:text-[#AEBDB8]">Eligible observations may contribute in aggregated, privacy-protected form.</span>
            </span>
          </label>

          <div className="flex items-start gap-2 rounded-[14px] bg-black/[.025] px-3.5 py-3 text-[11px] font-semibold leading-4 text-[#776C61] dark:bg-white/[.035] dark:text-[#919AAD]">
            <span aria-hidden="true">🔒</span>
            <span>Your exact private fishing spots stay private unless you choose to share content that reveals them.</span>
          </div>

          <button
            type="button"
            disabled={!canAccept || isSaving}
            onClick={() => {
              if (!ageBracket || ageBracket === "U13") return;
              onAccept({
                fishiqContributionEnabled: fishiqOk,
                ageBracket,
                parentalConsentReceived: isTeen ? guardianOk : false,
              });
            }}
            className="mt-1 w-full rounded-[15px] px-4 py-3.5 text-sm font-black text-white shadow-[0_10px_24px_rgba(47,128,237,.16)] transition active:scale-[.99] disabled:cursor-not-allowed disabled:bg-black/[.08] disabled:text-black/30 disabled:shadow-none dark:disabled:bg-white/[.07] dark:disabled:text-white/30"
            style={{ background: canAccept ? (isDarkMode ? "#7657E8" : "#2F80ED") : undefined }}
          >
            {isSaving ? "Saving…" : "Accept & Continue"}
          </button>

          <p className="text-center text-[10px] font-semibold leading-4 text-[#9A9187] dark:text-[#737B8D]">Terms and Privacy are required. Fishing-data contribution is optional.</p>
        </div>
      </div>
    </div>
  );
};


const PERSISTENT_APP_SURFACE_ORDER: View[] = [
  View.Home,
  View.TackleBox,
  View.LogCatch,
  View.Community,
  View.Bazaar,
];

const PERSISTENT_APP_SURFACES = new Set<View>(PERSISTENT_APP_SURFACE_ORDER);

const persistentSurfacePortalId = (view: View) =>
  `roddo-surface-portal-${String(view).replace(/[^a-z0-9_-]/gi, "-")}`;

type PersistentRouteSurfaceProps = {
  view: View;
  active: boolean;
  portalTargetId: string;
  scrollRef: { current: HTMLDivElement | null };
  children: React.ReactNode;
};

/**
 * Session-persistent primary app surface.
 *
 * The active child is cached in a layout effect before the browser paints the
 * next route. When inactive, the exact mounted React subtree stays alive but is
 * removed from presentation with visibility/pointer ownership rather than being
 * destroyed. This keeps subscriptions, local UI state, scroll position and
 * hydrated data warm without allowing cross-view ghost paint.
 */
const PersistentRouteSurface: React.FC<PersistentRouteSurfaceProps> = ({
  view,
  active,
  portalTargetId,
  scrollRef,
  children,
}) => {
  const cachedNodeRef = React.useRef<React.ReactNode>(null);

  React.useLayoutEffect(() => {
    if (children != null) cachedNodeRef.current = children;
  }, [children]);

  const node = children ?? cachedNodeRef.current;
  if (node == null) return null;

  const isBazaarComingSoon =
    view === View.Bazaar &&
    !FEATURE.BAZAAR_ENABLED &&
    !FEATURE.BAZAAR_LISTINGS_ONLY;

  return (
    <div
      data-roddo-persistent-surface={String(view)}
      data-roddo-surface-active={active ? "true" : "false"}
      aria-hidden={!active}
      ref={(element) => {
        if (active) {
          scrollRef.current = element;
        } else if (scrollRef.current === element) {
          scrollRef.current = null;
        }
      }}
      className={[
        "roddo-persistent-route-surface absolute inset-0 h-full min-h-0",
        "bg-transparent text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]",
        isBazaarComingSoon
          ? "overflow-hidden overscroll-none"
          : "roddo-content-scroll overflow-y-auto",
        active ? "is-active z-10" : "is-inactive -z-10",
      ].join(" ")}
      style={{
        visibility: active ? "visible" : "hidden",
        pointerEvents: active ? "auto" : "none",
      }}
    >
      <RoutePortalProvider targetId={portalTargetId}>{node}</RoutePortalProvider>
    </div>
  );
};

const RoddoApp: React.FC = () => {
  useEffect(() => {
    if (import.meta.env.DEV)
      console.debug("[BOOT] App.tsx marker v2026-03-17-FORENSIC");
  }, []);

  const { t, i18n } = useTranslation();
  const [appVisible, setAppVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAppVisible(true), 40);
    return () => window.clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const nativePlatform = Capacitor.isNativePlatform()
      ? Capacitor.getPlatform()
      : "web";
    const ua = window.navigator.userAgent || "";
    const isiOSBrowser =
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes("Macintosh") && "ontouchend" in document);
    const isStandalonePwa =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      (window.navigator as any).standalone === true;
    const platform = nativePlatform;

    root.dataset.roddoPlatform = platform;
    root.dataset.roddoIosLike = platform === "ios" || isiOSBrowser ? "true" : "false";
    root.dataset.roddoStandalone = isStandalonePwa ? "true" : "false";

    // Native Capacitor already lays the WebView inside the iOS safe area.
    // Do not add env(safe-area-inset-top) again or the Roddo header gets
    // pushed down twice. Standalone browser/PWA still needs the CSS safe area.
    const topSafeInsetCss =
  platform === "ios"
    ? "36px"
    : isiOSBrowser && isStandalonePwa
      ? "max(12px, calc(env(safe-area-inset-top) + 4px))"
      : platform === "android"
        ? "8px"
        : "6px";

    const syncViewportVars = () => {
      const viewport = window.visualViewport;

      // Native WebViews can report a short visualViewport during cold boot.
      // That makes the app shell too short for the first paint, which visually
      // lifts the absolute BottomNav until a hot reload / resize corrects it.
      const layoutHeight = Math.round(window.innerHeight || 0);
      const visualHeight = Math.round(viewport?.height || 0);
      const visualOffsetTop = Math.max(0, Math.round(viewport?.offsetTop || 0));
      const keyboardInset =
        visualHeight > 0 && layoutHeight > 0
          ? Math.max(0, layoutHeight - visualHeight - visualOffsetTop)
          : 0;
      const keyboardOpen =
        (platform === "ios" || platform === "android") && keyboardInset >= 120;

      // Keep the native app shell pinned to the layout viewport even while the
      // keyboard is open. The keyboard should behave like an overlay, not resize
      // Roddo's entire shell / fixed navigation. Native WebKit owns focus reveal.
      const liveHeight =
        platform === "web"
          ? visualHeight || layoutHeight
          : Math.max(layoutHeight, visualHeight);

      const layoutWidth = Math.round(window.innerWidth || 0);
      const visualWidth = Math.round(viewport?.width || 0);
      const liveWidth =
        platform === "web"
          ? visualWidth || layoutWidth
          : Math.max(layoutWidth, visualWidth);

      if (liveHeight > 0) {
        root.style.setProperty("--roddo-live-vh", `${liveHeight}px`);
        root.style.setProperty("--roddo-app-vh", `${liveHeight}px`);
      }

      if (visualHeight > 0) {
        root.style.setProperty("--roddo-visual-vh", `${visualHeight}px`);
      }
      root.style.setProperty("--roddo-keyboard-inset", `${keyboardInset}px`);
      root.dataset.roddoKeyboardOpen = keyboardOpen ? "true" : "false";

      if (liveWidth > 0) {
        root.style.setProperty("--roddo-live-vw", `${liveWidth}px`);
      }

      // Single source of truth for safe-area clearances.
      // Keep Android/web away from raw env(safe-area-inset-top), because some
      // devices report an oversized browser/status-bar inset there. Instead use a
      // tiny comfort cushion so the wood sign/buttons do not crowd the system bar.
      // iOS still gets real safe-area protection, capped with a small visual cushion.
      root.style.setProperty("--roddo-safe-top", topSafeInsetCss);
      root.style.setProperty(
        "--roddo-nav-safe-pad",
        // Native iOS already owns the bottom safe area. Standalone Safari/PWA
        // still needs the browser-provided inset.
        platform === "ios"
          ? "0px"
          : isiOSBrowser && isStandalonePwa
            ? "env(safe-area-inset-bottom)"
            : "0px",
      );
    };

    syncViewportVars();

    const rafs = [
      window.requestAnimationFrame(syncViewportVars),
      window.requestAnimationFrame(() => window.requestAnimationFrame(syncViewportVars)),
    ];

    const timers = [
      window.setTimeout(syncViewportVars, 60),
      window.setTimeout(syncViewportVars, 220),
      window.setTimeout(syncViewportVars, 650),
      window.setTimeout(syncViewportVars, 1200),
      window.setTimeout(syncViewportVars, 2200),
    ];

    const onVisibilityChange = () => {
      if (!document.hidden) syncViewportVars();
    };

    // Native keyboard lift is handled in the dedicated effect below.
    // Keep this viewport effect responsible only for shell sizing/safe-area vars.

    window.addEventListener("resize", syncViewportVars);
    window.addEventListener("orientationchange", syncViewportVars);
    window.addEventListener("pageshow", syncViewportVars);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.visualViewport?.addEventListener("resize", syncViewportVars);

    document.fonts?.ready?.then(syncViewportVars).catch(() => {});

    return () => {
      rafs.forEach((raf) => window.cancelAnimationFrame(raf));
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", syncViewportVars);
      window.removeEventListener("orientationchange", syncViewportVars);
      window.removeEventListener("pageshow", syncViewportVars);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.visualViewport?.removeEventListener("resize", syncViewportVars);
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const root = document.documentElement;
    let keyboardHeight = 0;
    let revealTimer: number | null = null;
    let disposed = false;
    const handles: Array<{ remove: () => Promise<void> | void }> = [];

    const isEditable = (value: Element | null): value is HTMLElement => {
      const el = value as HTMLElement | null;
      if (!el) return false;

      const tag = el.tagName?.toLowerCase();
      if (tag === "textarea" || tag === "select") return true;

      if (tag === "input") {
        const type = String((el as HTMLInputElement).type || "text").toLowerCase();
        return ![
          "button",
          "checkbox",
          "color",
          "file",
          "hidden",
          "image",
          "radio",
          "range",
          "reset",
          "submit",
        ].includes(type);
      }

      return el.isContentEditable === true;
    };

    const findScrollableAncestor = (start: HTMLElement): HTMLElement | null => {
      let node: HTMLElement | null = start.parentElement;

      while (node && node !== document.body && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScroll =
          /(auto|scroll|overlay)/.test(overflowY) &&
          node.scrollHeight > node.clientHeight + 2;

        if (canScroll) return node;
        node = node.parentElement;
      }

      return contentScrollRef.current;
    };

    const revealFocusedField = () => {
      if (disposed || keyboardHeight < 80) return;

      const active = document.activeElement;
      if (!isEditable(active)) return;

      const target =
        (active.closest('[data-roddo-keyboard-anchor="true"]') as HTMLElement | null) ||
        active;

      const keyboardTop = Math.max(0, window.innerHeight - keyboardHeight);
      const desiredBottom = keyboardTop - 16;
      const rect = target.getBoundingClientRect();
      const overlap = Math.ceil(rect.bottom - desiredBottom);

      if (overlap <= 0) return;

      const scroller = findScrollableAncestor(target);
      if (!scroller) return;

      // Match the manual gesture that already works on-device: move only the
      // current scroll view upward enough to expose the active field/composer.
      const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const nextTop = Math.min(maxScrollTop, scroller.scrollTop + overlap + 8);

      try {
        scroller.scrollTo({
          top: nextTop,
          left: scroller.scrollLeft,
          behavior: "smooth",
        });
      } catch {
        scroller.scrollTop = nextTop;
      }
    };

    const scheduleReveal = (delay = 40) => {
      if (revealTimer != null) window.clearTimeout(revealTimer);
      revealTimer = window.setTimeout(() => {
        revealTimer = null;
        revealFocusedField();
      }, delay);
    };

    const onFocusIn = () => {
      if (keyboardHeight >= 80) scheduleReveal(30);
    };

    const onKeyboardShow = (info: { keyboardHeight?: number }) => {
      keyboardHeight = Math.max(0, Number(info?.keyboardHeight || 0));
      root.style.setProperty("--roddo-native-keyboard-h", `${keyboardHeight}px`);
      root.style.setProperty("--roddo-keyboard-inset", `${keyboardHeight}px`);
      root.dataset.roddoKeyboardOpen = keyboardHeight >= 80 ? "true" : "false";

      // Run after the native animation settles enough for final field geometry.
      scheduleReveal(90);
    };

    const onKeyboardHide = () => {
      keyboardHeight = 0;
      root.style.removeProperty("--roddo-native-keyboard-h");
      root.style.setProperty("--roddo-keyboard-inset", "0px");
      root.dataset.roddoKeyboardOpen = "false";
    };

    document.addEventListener("focusin", onFocusIn);

    void (async () => {
      try {
        handles.push(await Keyboard.addListener("keyboardWillShow", onKeyboardShow));
        handles.push(await Keyboard.addListener("keyboardDidShow", onKeyboardShow));
        handles.push(await Keyboard.addListener("keyboardWillHide", onKeyboardHide));
        handles.push(await Keyboard.addListener("keyboardDidHide", onKeyboardHide));
      } catch (error) {
        console.warn("[keyboard] native listeners unavailable", error);
      }
    })();

    return () => {
      disposed = true;
      document.removeEventListener("focusin", onFocusIn);
      if (revealTimer != null) window.clearTimeout(revealTimer);
      root.style.removeProperty("--roddo-native-keyboard-h");

      handles.forEach((handle) => {
        try {
          void handle.remove();
        } catch {}
      });
    };
  }, []);


  const LEGAL_SCHEMA_VERSION = LEGAL_SCHEMA_VERSION_TEXT;
  const TOS_VERSION = TOS_VERSION_TEXT;
  const PRIVACY_VERSION = PRIVACY_VERSION_TEXT;

  const buildLegalAcceptancePatch = (opts: {
    fishiqContributionEnabled?: boolean;
    ageBracket: RoddoAgeBracket;
    parentalConsentReceived?: boolean;
  }): LegalAcceptancePatch & { ageBracket: RoddoAgeBracket; ageVerifiedAt: number } => {
    const now = Date.now();
    const ageBracket = opts.ageBracket;
    const isMinor = ageBracket !== "18_PLUS";
    const parentalRequired = isRoddoTeenBracket(ageBracket);
    const received = parentalRequired
      ? (opts?.parentalConsentReceived ?? false)
      : false;

    return {
      // Mirror for rules/callables that check a top-level flag
      fishiqContributionEnabled: opts.fishiqContributionEnabled ?? true,
      ageBracket,
      ageVerifiedAt: now,
      ageEligibility: buildRoddoAgeEligibilityRecord(ageBracket, now),
      legal: {
        schemaVersion: LEGAL_SCHEMA_VERSION,
        tos: { version: TOS_VERSION, acceptedAt: now },
        privacy: { version: PRIVACY_VERSION, acceptedAt: now },
        consents: {
          fishiqContribution: {
            enabled: opts.fishiqContributionEnabled ?? true,
            updatedAt: now,
          },
        },
        minor: {
          isMinor,
          ageBracket,
          parentalConsent: {
            required: parentalRequired,
            received,
            ...(parentalRequired && received
              ? { receivedAt: now, method: "other" as const }
              : {}),
          },
        },
      },
    };
  };

  const hasLegalAcceptanceTimestamp = (value: any): boolean => {
    if (typeof value === "number") {
      return Number.isFinite(value) && value > 0;
    }

    if (value && typeof value.toMillis === "function") {
      try {
        const millis = Number(value.toMillis());
        return Number.isFinite(millis) && millis > 0;
      } catch {}
    }

    if (value && typeof value.seconds === "number") {
      return Number.isFinite(Number(value.seconds)) && Number(value.seconds) > 0;
    }

    if (value instanceof Date) {
      return Number.isFinite(value.getTime()) && value.getTime() > 0;
    }

    if (typeof value === "string" && value.trim()) {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) && parsed > 0;
    }

    return false;
  };

  const hasAcceptedLegalV = (profile: any): boolean => {
    const legal = profile?.legal;
    return (
      legal?.schemaVersion === LEGAL_SCHEMA_VERSION &&
      legal?.tos?.version === TOS_VERSION &&
      legal?.privacy?.version === PRIVACY_VERSION &&
      hasLegalAcceptanceTimestamp(legal?.tos?.acceptedAt) &&
      hasLegalAcceptanceTimestamp(legal?.privacy?.acceptedAt)
    );
  };


  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const isAdmin =
    !!userProfile?.isAdmin ||
    !!(userProfile as any)?.roles?.includes?.("admin");

  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState<any>(null);
  useEffect(() => {
    if (!userProfile?.uid) return;

    const nextLanguage = resolveProfileLanguage(userProfile as any);
    const currentLanguage = normalizeRoddoLanguageCode(
      i18n.resolvedLanguage || i18n.language || "en",
    );

    if (currentLanguage !== nextLanguage) {
      void setRoddoLanguage(nextLanguage);
      return;
    }

    try {
      window.localStorage.setItem("roddo_language", nextLanguage);
      window.localStorage.setItem("i18nextLng", nextLanguage);
      document.documentElement.lang = nextLanguage;
    } catch {}
  }, [i18n.resolvedLanguage, i18n.language, userProfile?.uid, (userProfile as any)?.preferredLanguage, (userProfile as any)?.language, (userProfile as any)?.locale, (userProfile as any)?.appLanguage]);
    const [authBootTimedOut, setAuthBootTimedOut] = useState(false);

  // JS Firebase Auth mirror uid (native bridge target)
  const [jsAuthUid, setJsAuthUid] = useState<string | null>(
    auth.currentUser?.uid ?? null,
  );
  const [isAuthReady, setIsAuthReady] = useState(false);
  const IS_NATIVE = Capacitor.isNativePlatform();
  useEffect(() => {
    if (!IS_NATIVE) return;

    let disposed = false;
    let appUrlOpenHandle: { remove: () => Promise<void> } | null = null;

    const openNativeRoddoUrl = (rawUrl?: string | null) => {
      const value = String(rawUrl || "").trim();
      if (!value) return;

      try {
        const incoming = new URL(value);
        const host = incoming.hostname.toLowerCase();

        if (host !== "roddo.app" && host !== "www.roddo.app") return;

        const pathname = incoming.pathname.startsWith("/app")
          ? incoming.pathname
          : "/app";

        const inAppUrl =
          pathname +
          incoming.search +
          incoming.hash;

        window.history.replaceState({}, "", inAppUrl);
        window.dispatchEvent(new CustomEvent("roddo:native-url-open"));
      } catch (error) {
        console.warn("[deep-link] could not parse incoming native URL", {
          rawUrl: value,
          error,
        });
      }
    };

    void CapacitorApp.getLaunchUrl()
      .then((result) => {
        if (!disposed) openNativeRoddoUrl(result?.url);
      })
      .catch((error) =>
        console.warn("[deep-link] getLaunchUrl failed", error),
      );

    void CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      if (!disposed) openNativeRoddoUrl(url);
    })
      .then((handle) => {
        if (disposed) {
          void handle.remove();
          return;
        }
        appUrlOpenHandle = handle;
      })
      .catch((error) =>
        console.warn("[deep-link] appUrlOpen listener failed", error),
      );

    return () => {
      disposed = true;
      if (appUrlOpenHandle) void appUrlOpenHandle.remove();
    };
  }, [IS_NATIVE]);

  const [isJsBridgeReady, setIsJsBridgeReady] = useState<boolean>(!IS_NATIVE);
  const [jsBridgeError, setJsBridgeError] = useState<string | null>(null);
  const [accountSyncDebug, setAccountSyncDebug] = useState<string>("idle");
  const [isAccountSyncRetrying, setIsAccountSyncRetrying] = useState(false);
  const accountSyncRetryInFlightRef = useRef(false);
  const accountSyncAutoAttemptedForUidRef = useRef<string | null>(null);
  const [showNativeBridgeRecovery, setShowNativeBridgeRecovery] =
    useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [users, setUsers] = useState<Record<string, AppUser>>({});
  const [communityPosts, setCommunityPosts] = useState<
    Record<string, CommunityPost>
  >({});
  const [commentsByPost, setCommentsByPost] = useState<
    Record<string, Comment[]>
  >({});
  const [communityCommentPostIds, setCommunityCommentPostIds] = useState<
    Record<string, true>
  >({});
  const [followingIds, setFollowingIds] = useState<string[]>([]);
  const [communityBootstrapped, setCommunityBootstrapped] = useState(false);
  const [nativeNetDiag, setNativeNetDiag] = useState<any>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme =
      typeof window !== "undefined"
        ? window.localStorage.getItem("theme")
        : null;

    if (storedTheme === "dark") return true;
    if (storedTheme === "light") return false;

    // canonical default for all devices
    return false;
  });

  const FIRESTORE_TIMEOUT_MS = IS_NATIVE ? 30000 : 8000;
  // ---------------------------------------------------------------------------
  // Stripe return URL base (web vs native)
  // - web: window.location.origin
  // - native: MUST be a https(s) origin (not capacitor://localhost)
  //   Set VITE_STRIPE_RETURN_URL_BASE in .env.production/.env.development
  // ---------------------------------------------------------------------------
  const getStripeReturnUrlBase = (): string => {
    const envBase =
      (import.meta as any).env?.VITE_STRIPE_RETURN_URL_BASE ||
      (import.meta as any).env?.VITE_PUBLIC_WEB_ORIGIN ||
      "";

    if (IS_NATIVE) {
      if (typeof envBase === "string" && envBase.startsWith("http"))
        return envBase.replace(/\/+$/, "");
      return "https://roddo.app";
    }

    return String(window.location.origin || "").replace(/\/+$/, "");
  };
  // ---------------------------------------------------------------------------
  // Promise timeout helper (stable)
  // ---------------------------------------------------------------------------
  const withTimeout = <T,>(
    p: Promise<T>,
    ms: number,
    label: string,
  ): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const t = window.setTimeout(
        () => reject(new Error(`${label}_timeout_after_${ms}ms`)),
        ms,
      );
      p.then(
        (v) => {
          window.clearTimeout(t);
          resolve(v);
        },
        (e) => {
          window.clearTimeout(t);
          reject(e);
        },
      );
    });
  };

  // --------------------
  // Community pagination
  // --------------------
  const COMMUNITY_PAGE_SIZE = 5;

  const [communityHasMore, setCommunityHasMore] = useState(true);
  const [communityLoadingMore, setCommunityLoadingMore] = useState(false);

  // cursor for "load older"
  const communityCursorRef = useRef<QueryDocumentSnapshot | null>(null);
  const didBootstrapFishIQRef = useRef(false);
  const fishiqBootstrapInFlightRef = useRef<string | null>(null);
  // Log profile gating only when the gate state changes (avoids spam on native boot)
  const profileGateLogKeyRef = useRef<string | null>(null);

  const [marketplaceItems, setMarketplaceItems] = useState<
    Record<any, MarketplaceItem>
  >({});
  const [marketplaceTransactions, setMarketplaceTransactions] = useState<
    Record<string, MarketplaceTransaction>
  >({});
  const [escrowByItemId, setEscrowByItemId] = useState<
    Record<string, EscrowInfo>
  >({});

  const [tournaments, setTournaments] = useState<Record<string, Tournament>>(
    {},
  );
  const [tournamentBootReady, setTournamentBootReady] = useState(false);
  const [tournamentConfig, setTournamentConfig] = useState<TournamentPlatformConfig>(DEFAULT_TOURNAMENT_CONFIG);
  const [tournamentPreviewEntered, setTournamentPreviewEntered] = useState(false);
  const [userLicenses, setUserLicenses] = useState<UserLicense[]>([]);
  const [gearItems, setGearItems] = useState<TackleBoxItem[]>([]);

  const [catchDiaryEntries, setCatchDiaryEntries] = useState<CatchDiaryEntry[]>(
    [],
  );
 
  const [viewStack, setViewStack] = useState<{ view: View; state?: any }[]>([
    { view: View.Map },
  ]);
  const pendingPublicPostShareRef = useRef<string | null>(null);
  const pendingPublicPostShareStorageKey = "roddo_pending_public_post_share_id";
  const pendingRoddoShareRef = useRef<{
    kind: "tournament" | "bazaar" | "crew" | "crewPost" | "crewInvite" | "profile";
    tournamentId?: string;
    itemId?: string;
    crewId?: string;
    postId?: string;
    userId?: string;
  } | null>(null);
  const pendingRoddoShareStorageKey = "roddo_pending_share_destination_v1";
  const activeViewState = viewStack[viewStack.length - 1].state;

  const normalizedUserProfile = useMemo(
    () =>
      normalizeUserProfileReputation(userProfile as any) as UserProfile | null,
    [userProfile],
  );

  const normalizedUsers = useMemo(() => {
    const next: Record<string, AppUser> = { ...(users || {}) };

    Object.entries(next).forEach(([uid, value]) => {
      next[uid] = profileToAppUser({ uid, ...(value as any) });
    });

    if (normalizedUserProfile?.uid) {
      next[normalizedUserProfile.uid] = {
        ...(next[normalizedUserProfile.uid] as any),
        ...profileToAppUser(normalizedUserProfile as any),
      };
    }

    return next;
  }, [users, normalizedUserProfile]);

  const communityPostsForView = useMemo(
    () =>
      Object.values(communityPosts || {})
        .sort(
          (a: any, b: any) =>
            Number(b?.timestamp ?? 0) - Number(a?.timestamp ?? 0),
        )
        .map((post) =>
          withAuthorReputation(
            post as CommunityPost,
            normalizedUsers,
            normalizedUserProfile,
          ),
        ),
    [communityPosts, normalizedUsers, normalizedUserProfile],
  );

  const activeView = viewStack[viewStack.length - 1].view;
  const contentScrollRef = useRef<HTMLDivElement | null>(null);

  // Route presentation ownership must update before paint. Map/Bazaar can keep
  // background subscriptions/components warm, but visual portals may only paint
  // while their owning surface is active. This is intentionally layout-timed to
  // prevent one-frame cross-route ghosting.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.roddoMapActive = activeView === View.Map ? "true" : "false";
    root.dataset.roddoBazaarActive = activeView === View.Bazaar ? "true" : "false";
    root.dataset.roddoActiveView = String(activeView);

    return () => {
      root.dataset.roddoMapActive = "false";
      root.dataset.roddoBazaarActive = "false";
    };
  }, [activeView]);

  // Device-only crash breadcrumb consumed by index.tsx's native fatal overlay.
  // Deliberately writes a plain window field: no React state and no navigation side effects.
  useEffect(() => {
    try {
      const rawState = activeViewState as any;
      (window as any).__RODDO_ACTIVE_ROUTE__ = {
        view: String(activeView),
        state: rawState && typeof rawState === 'object'
          ? Object.fromEntries(
              Object.entries(rawState)
                .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value) || value == null)
                .slice(0, 16),
            )
          : rawState ?? null,
      };
    } catch {}
  }, [activeView, activeViewState]);

  useEffect(() => {
    const focusedPostId =
      (activeViewState as any)?.postId ||
      (activeViewState as any)?.communityPostId ||
      (activeViewState as any)?.focusPostId;

    // Keep intentional deep links/focused community posts anchored.
    if (activeView === View.Community && focusedPostId) return;

    // Primary app surfaces are session-persistent. Their own scroll position is
    // part of the warm environment, so route changes must not globally reset it.
    // A surface starts at scrollTop=0 on its first mount and resumes exactly where
    // the angler left it on later visits.
    if (PERSISTENT_APP_SURFACES.has(activeView)) return;

    const resetScroll = () => {
      const node = contentScrollRef.current;
      if (node) {
        node.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    resetScroll();
    const raf = window.requestAnimationFrame(resetScroll);
    const timer = window.setTimeout(resetScroll, 0);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [activeView, activeViewState]);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const [coords, setCoords] = useState<LatLon>(DEFAULT_COORDS_LA);
  const [hasShownBigBlueGlobe, setHasShownBigBlueGlobe] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.sessionStorage.getItem("roddo.bigBlueGlobe.seen.session") === "1"
    );
  });
  const [bigBlueGlobeAssetsReady, setBigBlueGlobeAssetsReady] = useState(
    () => hasShownBigBlueGlobe,
  );
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [tempUnit, setTempUnit] = useState<"Celsius" | "Fahrenheit">(
    "Fahrenheit",
  );

  const [isLoadingWeather, setIsLoadingWeather] = useState(false);

  const weatherBootReadyForMap = useMemo(() => {
    if (!weatherData || isLoadingWeather) return false;

    const weatherLat = Number((weatherData as any)?.coords?.lat);
    const weatherLon = Number((weatherData as any)?.coords?.lon);
    const currentLat = Number(coords?.lat);
    const currentLon = Number(coords?.lon);

    if (
      !Number.isFinite(weatherLat) ||
      !Number.isFinite(weatherLon) ||
      !Number.isFinite(currentLat) ||
      !Number.isFinite(currentLon)
    ) {
      return false;
    }

    return (
      Math.abs(weatherLat - currentLat) < 0.03 &&
      Math.abs(weatherLon - currentLon) < 0.03
    );
  }, [
    weatherData,
    isLoadingWeather,
    coords?.lat,
    coords?.lon,
  ]);

  const [mapWarmBoot, setMapWarmBoot] = useState({
    progress: 8,
    detail: "Preparing Big Blue…",
    ready: false,
  });

  const [showWelcome, setShowWelcome] = useState(false);
  const [guidedTourActive, setGuidedTourActive] = useState(false);
  // Incrementing this gives every first-run/replay walkthrough a fresh mounted
  // tour controller. This prevents a completed tour's internal step state from
  // leaking into a manual replay launched from Legal Settings.
  const [guidedTourSessionId, setGuidedTourSessionId] = useState(0);
  // Manual replay from Settings -> Legal must not be cancelled by the persisted
  // "tour already completed" profile flags. Keep replay as session-only state.
  const [guidedTourReplayMode, setGuidedTourReplayMode] = useState(false);
  const [isFirstRunSaving, setIsFirstRunSaving] = useState(false);
  const [isProfileHydrated, setIsProfileHydrated] = useState(false);

  // --- Access Gate (verified → full app; waitlisted/blocked → gate screen) ---
  type CanonicalAccessStatus = "member" | "waitlisted" | "blocked";

  type AccessGate = {
    accessStatus: CanonicalAccessStatus;
    memberNumber?: number;
    waitlistNumber?: number;
  };

  const normalizeAccessStatus = (
    raw: unknown,
  ): CanonicalAccessStatus | null => {
    const v = String(raw || "")
      .trim()
      .toLowerCase();
    if (v === "member" || v === "active" || v === "approved") return "member";
    if (v === "waitlisted" || v === "pending") return "waitlisted";
    if (v === "blocked" || v === "banned" || v === "denied") return "blocked";
    return null;
  };

  const resolveProfileAccessStatus = (
    profile: UserProfile | null | undefined,
  ): CanonicalAccessStatus | null => {
    if (!profile) return null;

    const p = profile as any;
    const direct = normalizeAccessStatus(p.accessStatus);

    // Hard-deny states always win.
    if (direct === "blocked" || p.blocked === true || p.banned === true || p.disabled === true) {
      return "blocked";
    }

    // Legacy-approved users can have stale accessStatus: "waitlisted" after a
    // bad no-code bootstrap. Prefer durable approval signals over waitlist
    // residue so an already-approved account can recover immediately.
    const memberNumber = Number(p.memberNumber);
    if (
      direct === "member" ||
      p.member === true ||
      p.active === true ||
      p.approved === true ||
      p.isApproved === true ||
      (Number.isFinite(memberNumber) && memberNumber > 0) ||
      !!p.accessGrantedAt ||
      !!p.accessApprovedAt ||
      !!p.memberNumberAssignedAt
    ) {
      return "member";
    }

    const waitlistNumber = Number(p.waitlistNumber);
    if (
      direct === "waitlisted" ||
      p.waitlisted === true ||
      !!p.waitlistedAt ||
      (Number.isFinite(waitlistNumber) && waitlistNumber > 0)
    ) {
      return "waitlisted";
    }

    return null;
  };

  const [accessGate, setAccessGate] = useState<AccessGate | null>(null);
  const [accessGateChecked, setAccessGateChecked] = useState(false);
  const [accessGateError, setAccessGateError] = useState<string | null>(null);
  // ---------------------------------------------------------------------------
  // Boot phase (prevents listener churn during auth/profile/legal sequencing)
  // ---------------------------------------------------------------------------
  const [legalAcceptedOverrideUid, setLegalAcceptedOverrideUid] = useState<string | null>(null);
  useEffect(() => {
    const uid = firebaseUser?.uid ?? null;
    setLegalAcceptedOverrideUid((current) =>
      current && current !== uid ? null : current,
    );
  }, [firebaseUser?.uid]);
  useEffect(() => {
    installWebGlobalErrorGuards();
  }, []);

  useEffect(() => {
    if (isAuthReady) {
      if (authBootTimedOut) setAuthBootTimedOut(false);
      return;
    }

    const t = window.setTimeout(
      () => {
        console.warn("[BOOT] auth readiness timeout; releasing loading guard");
        setAuthBootTimedOut(true);
        setIsAuthLoading(false);
      },
      IS_NATIVE ? 12000 : 8000,
    );

    return () => window.clearTimeout(t);
  }, [isAuthReady, authBootTimedOut, IS_NATIVE]);

  useEffect(() => {
    let cancelled = false;

    if (hasShownBigBlueGlobe) {
      setBigBlueGlobeAssetsReady(true);
      return () => {
        cancelled = true;
      };
    }

    setBigBlueGlobeAssetsReady(false);

    // Hard release: the app shell must never be held hostage by an image preload.
    const fallback = window.setTimeout(() => {
      if (!cancelled) {
        console.warn(
          "[BOOT] Big Blue globe preload timeout; continuing to app shell",
        );
        setBigBlueGlobeAssetsReady(true);
      }
    }, 6500);

    preloadBigBlueGlobeAssets()
      .catch((error) => {
        console.warn(
          "[BOOT] Big Blue globe preload failed; continuing to app shell",
          error,
        );
      })
      .finally(() => {
        window.clearTimeout(fallback);
        if (!cancelled) setBigBlueGlobeAssetsReady(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
    };
  }, [hasShownBigBlueGlobe]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const isNative = (() => {
      try {
        return (window as any)?.Capacitor?.isNativePlatform?.() === true;
      } catch {
        return false;
      }
    })();
    if (!isNative) return;

    (async () => {
      const url = `https://us-central1-roddo-official.cloudfunctions.net/netPing?cb=${Date.now()}`;
      console.debug("[netPing][app] GET start", url);
      try {
        const r = await fetch(url, { method: "GET", cache: "no-store" as any });
        const txt = await r.text();
        console.debug("[netPing][app] GET ok", {
          status: r.status,
          txt: txt.slice(0, 120),
        });
      } catch (e) {
        console.warn("[netPing][app] GET fail", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!IS_NATIVE) return;

    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);

    (async () => {
      try {
        const url =
          "https://us-central1-roddo-official.cloudfunctions.net/netPing";
        console.debug("[netPing] GET", url);
        const r = await fetch(url, {
          method: "GET",
          signal: ctrl.signal,
          cache: "no-store" as any,
        });
        const j = await r.json();
        console.debug("[netPing] ✅", { status: r.status, j });
      } catch (e) {
        console.warn("[netPing] ❌", e);
      } finally {
        clearTimeout(t);
      }
    })();

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [IS_NATIVE]);

  // Durable recovery for a legal acceptance that was accepted locally but missed
  // Firestore because the app was offline / backgrounded / timed out. The pending
  // patch is scoped to the exact UID and current legal versions, and is removed
  // only after Firestore accepts it.
  useEffect(() => {
    if (!isAuthReady || !firebaseUser?.uid || !isProfileHydrated) return;
    if (IS_NATIVE && jsAuthUid !== firebaseUser.uid) return;

    let cancelled = false;
    const uid = firebaseUser.uid;

    void (async () => {
      let raw = "";
      try {
        raw = localStorage.getItem("roddo_legal_accept_fallback_v1") || "";
      } catch {
        return;
      }
      if (!raw) return;

      let pending: any = null;
      try {
        pending = JSON.parse(raw);
      } catch {
        try { localStorage.removeItem("roddo_legal_accept_fallback_v1"); } catch {}
        return;
      }

      if (String(pending?.uid || "") !== uid || !pending?.patch) return;

      // Never replay acceptance for an obsolete legal version. A version bump
      // must show the legal gate again instead of silently accepting new terms.
      if (!hasAcceptedLegalV(pending.patch)) {
        try { localStorage.removeItem("roddo_legal_accept_fallback_v1"); } catch {}
        return;
      }

      try {
        await updateUserProfile(uid, pending.patch as any);
        if (cancelled) return;

        try {
          const latest = localStorage.getItem("roddo_legal_accept_fallback_v1");
          if (latest === raw) localStorage.removeItem("roddo_legal_accept_fallback_v1");
        } catch {}

        setLegalAcceptedOverrideUid(uid);
        console.info("[legal] recovered pending acceptance sync", { uid });
      } catch (error) {
        console.warn("[legal] pending acceptance sync still waiting", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthReady, firebaseUser?.uid, isProfileHydrated, IS_NATIVE, jsAuthUid]);

  // Firebase Auth is authoritative for whether an account is secured. If a
  // provider link succeeded but the profile metadata write failed, reconcile the
  // user document on the next authenticated hydration without changing UID/data.
  useEffect(() => {
    if (!isAuthReady || !firebaseUser?.uid || !isProfileHydrated || !userProfile) return;
    if (IS_NATIVE && jsAuthUid !== firebaseUser.uid) return;

    const identity = getRoddoAccountIdentity();
    if (!identity.uid || identity.uid !== firebaseUser.uid || identity.isGuest) return;

    const securedProviders = Array.from(
      new Set((identity.providers || []).filter(Boolean)),
    );
    if (!securedProviders.length && !identity.email) return;

    const storedProviders = Array.isArray((userProfile as any)?.authProviders)
      ? (userProfile as any).authProviders.map(String).filter(Boolean)
      : [];
    const mergedProviders = Array.from(new Set([...storedProviders, ...securedProviders]));
    const alreadyReconciled =
      (userProfile as any)?.accountType === "registered" &&
      mergedProviders.every((provider) => storedProviders.includes(provider));

    if (alreadyReconciled) return;

    void updateUserProfile(firebaseUser.uid, {
      accountType: "registered",
      authProviders: mergedProviders,
      accountUpgradedAt: (userProfile as any)?.accountUpgradedAt || Date.now(),
      updatedAt: Date.now(),
    } as any).catch((error) => {
      console.warn("[account] secured auth metadata reconciliation deferred", error);
    });
  }, [
    isAuthReady,
    firebaseUser?.uid,
    isProfileHydrated,
    userProfile,
    IS_NATIVE,
    jsAuthUid,
  ]);

  useEffect(() => {
    // Safe probe: skips fetch probes on native (capacitor://localhost)
    if (import.meta.env.DEV) {
      netProbeSafe().catch(() => {});
    }
  }, [IS_NATIVE]);

  useEffect(() => {
    const bracket = getRoddoVerifiedAgeBracket(userProfile as any);
    if (!bracket) return;
    try {
      window.localStorage.setItem(RODDO_AGE_GATE_PASSED_KEY, "1");
      window.localStorage.setItem(RODDO_AGE_BRACKET_KEY, bracket);
      window.localStorage.removeItem(RODDO_AGE_GATE_BLOCKED_KEY);
    } catch {}
  }, [userProfile]);

  const needsLegalNow =
    !!firebaseUser &&
    isProfileHydrated &&
    !!userProfile &&
    legalAcceptedOverrideUid !== firebaseUser.uid &&
    !hasAcceptedLegalV(userProfile);

  type BootPhase = "AUTH" | "PROFILE" | "LEGAL" | "READY";

  const bootPhase: BootPhase = useMemo(() => {
    if (!isAuthReady) return "AUTH";
    if (!firebaseUser) return "AUTH";
    if (!isProfileHydrated) return "PROFILE";
    if (needsLegalNow) return "LEGAL";
    return "READY";
  }, [isAuthReady, firebaseUser, isProfileHydrated, needsLegalNow]);

  const nativeUid = firebaseUser?.uid ?? null;
  const jsUid = jsAuthUid ?? null;

  // ✅ Native MUST have JS auth that matches native uid before ANY Firestore
  const isNativeJsAuthed = !IS_NATIVE || !nativeUid || jsUid === nativeUid;

  const profileAccessStatus = resolveProfileAccessStatus(userProfile);
  const resolvedAccessStatus: CanonicalAccessStatus | null =
    profileAccessStatus === "blocked"
      ? "blocked"
      : accessGate?.accessStatus === "member"
        ? "member"
        : profileAccessStatus ?? accessGate?.accessStatus ?? null;

  const hasAppAccess =
    bootPhase === "READY" &&
    !!firebaseUser?.uid &&
    resolvedAccessStatus === "member" &&
    isNativeJsAuthed;

  // Determinate warm-map boot overlay. Keep this derived state AFTER
  // hasAppAccess is initialized to avoid a temporal-dead-zone crash.
  // The authenticated shell stays mounted underneath so Header, BottomNav,
  // Leaflet, location, FishIQ, tournaments, and markers can warm before reveal.
  const shouldShowWarmAppBoot =
    hasAppAccess && activeView === View.Map && !mapWarmBoot.ready;

  const warmBootProgress = Math.max(
    60,
    Math.min(100, Number(mapWarmBoot.progress) || 60),
  );
  const warmBootDetail =
    String(mapWarmBoot.detail || "Preparing Big Blue…").trim() ||
    "Preparing Big Blue…";

  // Keep Bazaar inbox data warm at the app/session layer. BazaarView may unmount
  // freely, but its unread/message presentation can synchronously hydrate from
  // this store on the very first render instead of painting empty then popping in.
  useEffect(() => {
    const uid = hasAppAccess ? String(firebaseUser?.uid || "").trim() : "";
    startBazaarInboxStore(uid);
  }, [hasAppAccess, firebaseUser?.uid]);

  // Warm Dashboard's daily activity before the Home route mounts. This keeps the
  // first Dashboard frame identical to its settled Firestore state instead of
  // painting every mission as incomplete and flipping them after subscription.
  useEffect(() => {
    const uid = hasAppAccess ? String(firebaseUser?.uid || "").trim() : "";
    const dateKey = uid ? String(getDailyRewardPreview(userProfile as any)?.today || "") : "";
    startDashboardDailyStore(uid, dateKey);
  }, [
    hasAppAccess,
    firebaseUser?.uid,
    userProfile?.uid,
    (userProfile as any)?.dailyRewards?.timeZone,
    (userProfile as any)?.dailyRewards?.dailyTask?.date,
  ]);

  // ✅ Single gate for app data listeners. Waitlisted/blocked users should only
  // hydrate their own profile/access state, not the full app data graph.
  const canAttachPublicListeners = hasAppAccess;

  // Privacy-safe aggregate activity pulse. This is intentionally independent
  // from Crew Presence/location sharing and only runs for a fully authenticated
  // member session after Roddo's normal access gates are satisfied.
  useEffect(() => {
    const uid = String(firebaseUser?.uid || '').trim();
    if (!hasAppAccess || !uid) return;
    return startUserActivityPulse(uid);
  }, [hasAppAccess, firebaseUser?.uid]);

  // Canonical public profile cache.
  // Community posts intentionally store lightweight author snapshots, but the UI
  // should render the freshest privacy-safe publicProfiles/{uid} projection when it is available.
  useEffect(() => {
    if (!canAttachPublicListeners) return;

    const unsub = subscribeToUsers(
      (next) => setUsers(next || {}),
      (err) => console.error("[LISTENER] users", err),
    );

    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [canAttachPublicListeners]);

  const canAttachPrivateListeners = hasAppAccess;

  const onboardingNeeded =
    !!firebaseUser &&
    isProfileHydrated &&
    !!userProfile &&
    resolvedAccessStatus === "member" &&
    (userProfile as any)?.onboardingCompleted !== true;

  const [paymentItem, setPaymentItem] = useState<{
    name: string;
    price: number;
    type: "license" | "marketplace";
    item: LicenseInfo | MarketplaceItem;
  } | null>(null);

  const [currency] = useState("USD");

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [postLogInsight, setPostLogInsight] = useState<{
    catchEntry: LogEntry;
    nearbyCatchCount?: number;
    weatherText?: string | null;
    onDone: () => void;
  } | null>(null);
  const [challengeProgressMap, setChallengeProgressMap] = useState<
    Record<string, BackendChallengeProgress>
  >({});
  const [challengeCompletionMap, setChallengeCompletionMap] = useState<
    Record<string, BackendChallengeCompletion>
  >({});
  const [achievementMap, setAchievementMap] = useState<
    Record<string, BackendAchievement>
  >({});
  const addToast = useCallback((message: string, type: ToastType = "info") => {
    const id = `toast_${Date.now()}_${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      5000,
    );
  }, []);
  const [dailyRewardModalOpen, setDailyRewardModalOpen] = useState(false);
  const [dailyRewardBusy, setDailyRewardBusy] = useState(false);
  const [guestLogoutDialogOpen, setGuestLogoutDialogOpen] = useState(false);
  const [guestLogoutBusy, setGuestLogoutBusy] = useState(false);

  const dailyRewardPreview = useMemo(
    () => (userProfile?.uid ? getDailyRewardPreview(userProfile as any) : null),
    [
      userProfile?.uid,
      (userProfile as any)?.dailyRewards?.timeZone,
      (userProfile as any)?.dailyRewards?.lastClaimDate,
      (userProfile as any)?.dailyRewards?.streak,
      (userProfile as any)?.dailyRewards?.bestStreak,
      (userProfile as any)?.dailyRewards?.dailyTask?.date,
      (userProfile as any)?.dailyRewards?.dailyTask?.taskId,
      (userProfile as any)?.dailyRewards?.dailyTask?.completed,
    ],
  );

  const dailyRewardStorageKey = useMemo(() => {
    if (!userProfile?.uid || !dailyRewardPreview?.today) return null;
    return `roddo:dailyReward:dismissed:${userProfile.uid}:${dailyRewardPreview.today}`;
  }, [dailyRewardPreview?.today, userProfile?.uid]);

  const markDailyRewardDismissedForToday = useCallback(() => {
    if (!dailyRewardStorageKey) return;

    try {
      window.localStorage.setItem(dailyRewardStorageKey, "1");
    } catch {}
  }, [dailyRewardStorageKey]);

  const dismissDailyRewardModal = useCallback(() => {
    markDailyRewardDismissedForToday();
    setDailyRewardModalOpen(false);
  }, [markDailyRewardDismissedForToday]);

  const handleClaimDailyReward = useCallback(async () => {
    if (dailyRewardBusy) return;

    setDailyRewardBusy(true);

    try {
      const result = await claimDailyReward();

      markDailyRewardDismissedForToday();
      setDailyRewardModalOpen(false);

      if (result.alreadyClaimed) {
        addToast("Today's Cast already claimed.", "info");
        return;
      }

      const awardedShells = Math.max(0, Math.floor(Number(result.awardedShells || 0)));
      const awardedSearchLures = Math.max(0, Math.floor(Number(result.awardedSearchLures || 0)));
      const lureText = awardedSearchLures > 0 ? ` · +${awardedSearchLures} Search Lures` : "";
      const cosmeticName = String(result.cosmeticAward?.name || "").trim();
      const bonusShells = Math.max(0, Math.floor(Number(result.bonusShells || 0)));

      if (cosmeticName) {
        addToast(
          `Today's Cast complete — +${awardedShells} Shells${lureText} · unlocked ${cosmeticName}`,
          "success",
        );
      } else if (bonusShells > 0) {
        addToast(
          `Today's Cast complete — +${awardedShells} Shells${lureText}, including +${bonusShells} duplicate gear bonus`,
          "success",
        );
      } else {
        addToast(
          `Today's Cast complete — +${awardedShells} Shell${awardedShells === 1 ? "" : "s"}${lureText}`,
          "success",
        );
      }
    } catch (error: any) {
      console.warn("[DailyReward] claim failed", error);
      addToast(
        error?.message || "Couldn't claim Today's Cast. Please try again.",
        "error",
      );
    } finally {
      setDailyRewardBusy(false);
    }
  }, [addToast, dailyRewardBusy, markDailyRewardDismissedForToday]);
  const [pendingInviteCode, setPendingInviteCode] = useState<string>(() =>
    readStoredInviteCode(),
  );
  // Draft code = what the user is typing. Submitted code = the value we actually send to Functions.
  // Keeping these separate prevents the access bootstrap from firing/remounting the access screen on every keystroke.
  const [submittedInviteCode, setSubmittedInviteCode] = useState<string>(() =>
    readStoredInviteCode(),
  );
  const [inviteCodeHydrationNonce, setInviteCodeHydrationNonce] = useState(0);
  const [invitePromptOpen, setInvitePromptOpen] = useState(false);
  const [invitePromptError, setInvitePromptError] = useState<string | null>(
    null,
  );
  const [accessRetryNonce, setAccessRetryNonce] = useState(0);

  const readPendingDisplayName = useCallback(() => {
    try {
      return String(
        localStorage.getItem("roddo_pending_display_name") || "",
      ).trim();
    } catch {
      return "";
    }
  }, []);

  const resolveAuthDisplayName = useCallback(() => {
    const pending = readPendingDisplayName();
    const authName = String((firebaseUser as any)?.displayName || "").trim();
    return pending || authName || "Angler";
  }, [firebaseUser, readPendingDisplayName]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = (params.get("invite") || "").trim();
      if (code) {
        const cleanCode = code.toUpperCase();
        setPendingInviteCode(cleanCode);
        setSubmittedInviteCode(cleanCode);
        writeStoredInviteCode(cleanCode);
        setInviteCodeHydrationNonce((n) => n + 1);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const captureRoddoShareTargetFromLocation = () => {
    try {
      const path = String(window.location.pathname || "");
      const params = new URLSearchParams(window.location.search || "");

      // Public preview CTA sends users to /app?postId=:id. The old public
      // preview route is /app/p/:id, so keep both supported but never use
      // /app/p/:id as the in-app destination. Persist the id so OAuth/login
      // redirects cannot drop the post target.
      const pathMatch = path.match(/\/app\/p\/([^/?#]+)/i);
      const queryPostId =
        params.get("postId") ||
        params.get("communityPostId") ||
        params.get("focusPostId") ||
        "";
      const pathPostId = pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : "";
      const postId = String(queryPostId || pathPostId || "").trim();

      if (postId) {
        pendingPublicPostShareRef.current = postId;
        try {
          window.sessionStorage.setItem(pendingPublicPostShareStorageKey, postId);
        } catch {}
      } else {
        try {
          const stored = String(
            window.sessionStorage.getItem(pendingPublicPostShareStorageKey) || "",
          ).trim();
          if (stored) pendingPublicPostShareRef.current = stored;
        } catch {}
      }

      // Clean typed share routes. Legacy query-string links remain supported
      // below so links already in the wild do not break.
      const tournamentPathMatch = path.match(/^\/app\/t\/([^/?#]+)/i);
      const bazaarPathMatch = path.match(/^\/app\/b\/([^/?#]+)/i);
      const crewPostPathMatch = path.match(
        /^\/app\/crew\/([^/?#]+)\/post\/([^/?#]+)/i,
      );
      const crewPathMatch = path.match(/^\/app\/crew\/([^/?#]+)/i);
      const crewInvitePathMatch = path.match(
        /^\/app\/invite\/crew\/([^/?#]+)/i,
      );
      const profilePathMatch = path.match(/^\/app\/u\/([^/?#]+)/i);

      const tournamentId = String(
        (tournamentPathMatch?.[1]
          ? decodeURIComponent(tournamentPathMatch[1])
          : "") ||
          params.get("tournamentId") ||
          params.get("tournament") ||
          params.get("eventId") ||
          "",
      ).trim();
      const bazaarItemId = String(
        (bazaarPathMatch?.[1]
          ? decodeURIComponent(bazaarPathMatch[1])
          : "") ||
          params.get("bazaarItemId") ||
          params.get("listingId") ||
          "",
      ).trim();
      const crewId = String(
        (crewPostPathMatch?.[1]
          ? decodeURIComponent(crewPostPathMatch[1])
          : crewPathMatch?.[1]
            ? decodeURIComponent(crewPathMatch[1])
            : "") ||
          params.get("crewId") ||
          params.get("crew") ||
          "",
      ).trim();
      const crewPostId = String(
        (crewPostPathMatch?.[2]
          ? decodeURIComponent(crewPostPathMatch[2])
          : "") ||
          params.get("crewPostId") ||
          params.get("postIdInCrew") ||
          "",
      ).trim();
      const crewInviteUserId = String(
        crewInvitePathMatch?.[1]
          ? decodeURIComponent(crewInvitePathMatch[1])
          : "",
      ).trim();
      const profileUserId = String(
        profilePathMatch?.[1] ? decodeURIComponent(profilePathMatch[1]) : "",
      ).trim();

      const sharedDestination = tournamentId
        ? { kind: "tournament" as const, tournamentId }
        : bazaarItemId
          ? { kind: "bazaar" as const, itemId: bazaarItemId }
          : crewId && crewPostId
          ? { kind: "crewPost" as const, crewId, postId: crewPostId }
          : crewId
            ? { kind: "crew" as const, crewId }
            : crewInviteUserId
              ? { kind: "crewInvite" as const, userId: crewInviteUserId }
              : profileUserId
                ? { kind: "profile" as const, userId: profileUserId }
                : null;

      if (sharedDestination) {
        pendingRoddoShareRef.current = sharedDestination;
        try {
          window.sessionStorage.setItem(
            pendingRoddoShareStorageKey,
            JSON.stringify(sharedDestination),
          );
        } catch {}
      } else {
        try {
          const stored = window.sessionStorage.getItem(pendingRoddoShareStorageKey);
          if (stored) pendingRoddoShareRef.current = JSON.parse(stored);
        } catch {}
      }
    } catch {}
    };

    captureRoddoShareTargetFromLocation();
    window.addEventListener(
      "roddo:native-url-open",
      captureRoddoShareTargetFromLocation,
    );

    return () =>
      window.removeEventListener(
        "roddo:native-url-open",
        captureRoddoShareTargetFromLocation,
      );
  }, []);

  useEffect(() => {
    const hydrateInviteCode = (source: string, codeFromEvent?: string) => {
      const cleanCode = String(codeFromEvent || readStoredInviteCode() || "")
        .trim()
        .toUpperCase();
      if (!cleanCode) return;

      setPendingInviteCode(cleanCode);
      setSubmittedInviteCode(cleanCode);
      writeStoredInviteCode(cleanCode);
      accessBootstrappedRef.current = null;
      setAccessGateChecked(false);
      setAccessGate(null);
      setAccessGateError(null);
      setInvitePromptError(null);
      setInvitePromptOpen(false);
      setInviteCodeHydrationNonce((n) => n + 1);

      if (import.meta.env.DEV) {
        console.debug("[AccessGate] hydrated invite code", {
          source,
          hasCode: true,
        });
      }
    };

    hydrateInviteCode("mount");

    const onInviteReady = (event: Event) => {
      const detailCode = String((event as CustomEvent)?.detail?.code || "")
        .trim()
        .toUpperCase();
      hydrateInviteCode("event", detailCode);
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === RODDO_INVITE_CODE_KEY)
        hydrateInviteCode("storage", event.newValue || "");
    };

    window.addEventListener(
      "roddo:invite-code-ready",
      onInviteReady as EventListener,
    );
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener(
        "roddo:invite-code-ready",
        onInviteReady as EventListener,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!userProfile?.uid) {
      setShowWelcome(false);
      setGuidedTourActive(false);
      setGuidedTourReplayMode(false);
      return;
    }

    // A manual replay is intentionally independent of the persisted completion
    // version. Without this guard, a profile refresh immediately turns the tour
    // back off because the user has already completed version 2 once.
    if (guidedTourReplayMode) return;

    if (onboardingNeeded) {
      setGuidedTourActive(false);
      setShowWelcome(true);
      return;
    }

    const experienceVersion = Number((userProfile as any)?.onboardingExperienceVersion || 0);
    const completedVersion = Number((userProfile as any)?.welcomeTourCompletedVersion || 0);
    const shouldResumeGuidedTour =
      experienceVersion === RODDO_GUIDED_TOUR_VERSION &&
      completedVersion < RODDO_GUIDED_TOUR_VERSION;

    setShowWelcome(false);
    setGuidedTourActive(shouldResumeGuidedTour);
  }, [
    onboardingNeeded,
    guidedTourReplayMode,
    userProfile?.uid,
    (userProfile as any)?.onboardingExperienceVersion,
    (userProfile as any)?.welcomeTourCompletedVersion,
  ]);

  useEffect(() => {
    const uid = String(userProfile?.uid || "").trim();
    const memberNumber = Math.trunc(Number((userProfile as any)?.memberNumber || 0));
    const isGuest =
      (userProfile as any)?.accountType === "guest" ||
      ((userProfile as any)?.accountType !== "registered" &&
        (firebaseUser as any)?.isAnonymous === true);

    if (!uid || !isGuest || !Number.isFinite(memberNumber) || memberNumber <= 0) return;

    const guestLabel = `Angler Guest #${String(memberNumber).padStart(8, "0")}`;
    const currentNames = [
      String((userProfile as any)?.displayName || "").trim(),
      String((userProfile as any)?.name || "").trim(),
    ].filter(Boolean);
    const isGenericGuestName = (value: string) =>
      /^(angler|guest|angler guest(?: #\d+)?)$/i.test(value);

    // Give untouched guest profiles a stable public label, but never replace a
    // custom name chosen during onboarding or later in profile settings.
    if (currentNames.some((value) => !isGenericGuestName(value))) return;
    if (currentNames.length > 0 && currentNames.every((value) => value === guestLabel)) return;

    void updateUserProfile(uid, {
      name: guestLabel,
      displayName: guestLabel,
      updatedAt: Date.now(),
    } as any).catch((error) =>
      console.warn("[guest-label] could not persist public guest label", error),
    );
  }, [
    userProfile?.uid,
    (userProfile as any)?.accountType,
    (userProfile as any)?.memberNumber,
    (userProfile as any)?.displayName,
    (userProfile as any)?.name,
    (firebaseUser as any)?.isAnonymous,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (IS_NATIVE) return; // ✅ don’t probe from capacitor://localhost
  }, [IS_NATIVE]);
  const runNativeJsBridge = useCallback(
    async (nativeUid?: string | null, reason: string = "unknown") => {
      if (!IS_NATIVE) {
        setJsBridgeError(null);
        setIsJsBridgeReady(true);
        return true;
      }

      if (!nativeUid) {
        setJsBridgeError(null);
        setIsJsBridgeReady(true);
        return true;
      }

      setJsBridgeError(null);
      setIsJsBridgeReady(false);

      console.debug("[BOOT][bridge] start", {
        reason,
        nativeUid,
        jsUid: jsAuthUid ?? null,
        diag: getAuthBridgeDiag(),
      });

      try {
        const ok = await ensureJsAuthFromNative(reason);

        console.debug("[BOOT][bridge] done", {
          reason,
          ok,
          nativeUid,
          jsUid: jsAuthUid ?? null,
          diag: getAuthBridgeDiag(),
        });

        setIsJsBridgeReady(
          !!ok && (auth.currentUser?.uid ?? null) === nativeUid,
        );

        if (!ok) {
          setJsBridgeError(
            `Native→JS auth bridge failed (native=${nativeUid}, js=${auth.currentUser?.uid ?? "null"}).`,
          );
        } else {
          setJsBridgeError(null);
        }

        return !!ok;
      } catch (e: any) {
        console.warn(
          "[BOOT][bridge] threw",
          { reason, message: e?.message ?? String(e) },
          e,
        );
        setIsJsBridgeReady(false);
        setJsBridgeError(
          e?.message ? `Bridge error: ${e.message}` : "Bridge error",
        );
        return false;
      }
    },
    [IS_NATIVE],
  );

  const goTo = useCallback(
    (view: View, state?: any) => setViewStack([{ view, state }]),
    [],
  );
  const canNavigateTo = useCallback((view: View) => {
    if (view === View.FishIQ && !FEATURE.FISHIQ) return false;
    // Tournaments are launch-gated in UI, but the route must remain reachable for the Coming Soon page.

    // Launch/legal gate: keep Bazaar visible as the coming-soon shell, but block
    // all transactional marketplace account/payment routes while Bazaar is closed.
    if (!FEATURE.BAZAAR_ENABLED && !FEATURE.BAZAAR_LISTINGS_ONLY) {
      if (view === View.Bazaar || view === View.SellerDashboard) return false;
    }

    if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
      const blockedPaymentViews = new Set<View>([
        View.PurchaseHistory,
        View.Receipt,
        View.CheckoutSuccess,
        View.CheckoutCancel,
      ]);
      if (blockedPaymentViews.has(view)) return false;
    }

    return true;
  }, []);

  const navigateTo = useCallback(
    (view: View, state: Record<string, any> = {}) => {
      if (!canNavigateTo(view)) return;
      setViewStack((prev) => [...prev, { view, state }]);
    },
    [canNavigateTo],
  );

  const navigateToRoot = useCallback(
    (view: View, state: Record<string, any> = {}) => {
      if (!canNavigateTo(view)) return;
      setViewStack([{ view, state }]);
    },
    [canNavigateTo],
  );

  useEffect(() => {
    let postId = String(pendingPublicPostShareRef.current || "").trim();

    if (!postId) {
      try {
        postId = String(
          window.sessionStorage.getItem(pendingPublicPostShareStorageKey) || "",
        ).trim();
        if (postId) pendingPublicPostShareRef.current = postId;
      } catch {}
    }

    if (!postId) return;

    // Public post share links are app deep links. Wait until normal app gates
    // clear, then open Baleen Bay focused on that post. The id is also kept in
    // sessionStorage so Google/OAuth/login reloads do not lose the destination.
    if (
      !hasAppAccess ||
      onboardingNeeded ||
      showWelcome ||
      needsLegalNow
    ) {
      return;
    }

    pendingPublicPostShareRef.current = null;
    try {
      window.sessionStorage.removeItem(pendingPublicPostShareStorageKey);
    } catch {}

    navigateToRoot(View.Community, {
      focusPostId: postId,
      postId,
      communityPostId: postId,
      fromShareLink: true,
    });

    try {
      const params = new URLSearchParams(window.location.search);
      params.delete("postId");
      params.delete("communityPostId");
      params.delete("focusPostId");
      params.delete("fbclid");
      const nextUrl = `/app${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", nextUrl);
    } catch {}
  }, [
    hasAppAccess,
    onboardingNeeded,
    showWelcome,
    needsLegalNow,
    navigateToRoot,
  ]);

  useEffect(() => {
    let destination = pendingRoddoShareRef.current;

    if (!destination) {
      try {
        const stored = window.sessionStorage.getItem(pendingRoddoShareStorageKey);
        if (stored) destination = JSON.parse(stored);
      } catch {}
    }

    if (!destination) return;
    if (
      !hasAppAccess ||
      onboardingNeeded ||
      showWelcome ||
      needsLegalNow
    ) {
      return;
    }

    pendingRoddoShareRef.current = null;
    try {
      window.sessionStorage.removeItem(pendingRoddoShareStorageKey);
    } catch {}

    if (destination.kind === "tournament" && destination.tournamentId) {
      navigateToRoot(View.Events, {
        tournamentId: destination.tournamentId,
        fromShareLink: true,
      });
    } else if (destination.kind === "bazaar" && destination.itemId) {
      navigateToRoot(View.Bazaar, {
        itemId: destination.itemId,
        fromShareLink: true,
      });
    } else if (destination.crewId) {
      navigateToRoot(View.Crew, {
        crewId: destination.crewId,
        crewPostId: destination.kind === "crewPost" ? destination.postId || "" : "",
        fromShareLink: true,
      });
    } else if (destination.userId) {
      navigateToRoot(View.PublicProfile, {
        targetUserId: destination.userId,
        fromShareLink: true,
        fromCrewInvite: destination.kind === "crewInvite",
      });
    }

    try {
      const params = new URLSearchParams(window.location.search);
      params.delete("tournamentId");
      params.delete("tournament");
      params.delete("eventId");
      params.delete("bazaarItemId");
      params.delete("listingId");
      params.delete("crewId");
      params.delete("crew");
      params.delete("crewPostId");
      params.delete("postIdInCrew");
      params.delete("source");
      params.delete("fbclid");
      const nextUrl = `/app${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", nextUrl);
    } catch {}
  }, [
    hasAppAccess,
    onboardingNeeded,
    showWelcome,
    needsLegalNow,
    navigateToRoot,
  ]);

  useEffect(() => {
    if (!hasAppAccess || !isProfileHydrated || !userProfile?.uid || !dailyRewardPreview) {
      setDailyRewardModalOpen(false);
      return;
    }

    const guidedTourComplete =
      Number((userProfile as any)?.welcomeTourCompletedVersion || 0) >=
      RODDO_GUIDED_TOUR_VERSION;

    if (
      onboardingNeeded ||
      showWelcome ||
      guidedTourActive ||
      guidedTourReplayMode ||
      !guidedTourComplete ||
      needsLegalNow ||
      dailyRewardPreview.alreadyClaimed
    ) {
      setDailyRewardModalOpen(false);
      return;
    }

    if (dailyRewardStorageKey) {
      try {
        if (window.localStorage.getItem(dailyRewardStorageKey) === "1") return;
      } catch {}
    }

    const timer = window.setTimeout(() => {
      setDailyRewardModalOpen(true);
    }, 700);

    return () => window.clearTimeout(timer);
  }, [
    dailyRewardPreview,
    dailyRewardStorageKey,
    hasAppAccess,
    isProfileHydrated,
    needsLegalNow,
    onboardingNeeded,
    showWelcome,
    guidedTourActive,
    guidedTourReplayMode,
    userProfile?.uid,
    (userProfile as any)?.welcomeTourCompletedVersion,
  ]);

  const markBigBlueGlobeSeen = useCallback(() => {
    setHasShownBigBlueGlobe(true);
    try {
      window.sessionStorage.setItem("roddo.bigBlueGlobe.seen.session", "1");
    } catch {}
  }, []);

  useEffect(() => {
    if (hasShownBigBlueGlobe) return;
    if (activeView !== View.Map) return;
    if (
      !hasAppAccess ||
      onboardingNeeded ||
      showWelcome ||
      needsLegalNow
    )
      return;

    // First-run Big Blue should only open after BOTH presentation layers are warm.
    // The Earth model and the persistent Leaflet/FishIQ map initialize together
    // behind the opening loader. By the time the globe is shown, double-clicking
    // can fade straight into an already-ready user map instead of triggering a
    // second Big Blue loading pass.
    if (!bigBlueGlobeAssetsReady || !mapWarmBoot.ready) return;

    navigateToRoot(View.BigBlueGlobe, {
      launchMode: "firstOpen",
      initialTarget: {
        lat: Number(coords?.lat ?? 34.0522),
        lng: Number(coords?.lon ?? -118.2437),
        zoom: 5,
        label: "Current Waters",
      },
    });
  }, [
    activeView,
    bigBlueGlobeAssetsReady,
    coords?.lat,
    coords?.lon,
    hasAppAccess,
    hasShownBigBlueGlobe,
    mapWarmBoot.ready,
    navigateToRoot,
    needsLegalNow,
    onboardingNeeded,
    showWelcome,
  ]);

  useEffect(() => {
    if (hasShownBigBlueGlobe) return;
    if (activeView !== View.BigBlueGlobe) return;
    if ((activeViewState as any)?.launchMode !== "firstOpen") return;

    // Consume the first-run globe only after the route has actually mounted.
    // This keeps the loading guard honest while still preventing repeated
    // auto-launches later in the same session.
    const t = window.setTimeout(() => {
      markBigBlueGlobeSeen();
    }, 300);

    return () => window.clearTimeout(t);
  }, [activeView, activeViewState, hasShownBigBlueGlobe, markBigBlueGlobeSeen]);

  const navigateBack = useCallback(() => {
    setViewStack((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1)));
  }, []);

  const navigateBackSmart = useCallback(() => {
    setViewStack((prev) => {
      if (prev.length <= 1) return prev;

      const curr = prev[prev.length - 1];
      const prevEntry = prev[prev.length - 2];

      // default pop
      const popped = prev.slice(0, -1);

      // If we came from TackleBox Stats into Achievements/Challenges,
      // force the previous TackleBox state.tab = 'stats'
      if (
        (curr.view === (View as any).Challenges ||
          curr.view === (View as any).Achievements) &&
        curr.state?.from === View.TackleBox
      ) {
        const tab = curr.state?.tab ?? "stats";

        // if the previous entry is literally TackleBox, just update its state
        if (prevEntry?.view === View.TackleBox) {
          const fixedPrevEntry = {
            ...prevEntry,
            state: { ...(prevEntry.state || {}), tab },
          };
          return [...prev.slice(0, -2), fixedPrevEntry];
        }

        // fallback: if stack isn't what we expect, still land on TackleBox stats
        return [...popped, { view: View.TackleBox, state: { tab } }];
      }

      return popped;
    });
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!IS_NATIVE) return;

    let alive = true;

    (async () => {
      try {
        const res = await runNativeAuthBridgeNetDiag();
        if (!alive) return;
        setNativeNetDiag(res);
        console.debug("[auth-bridge][netdiag] result", res);
      } catch (e) {
        console.warn("[auth-bridge][netdiag] threw", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [IS_NATIVE]);

  useEffect(() => {
    let alive = true;

    const uid = firebaseUser?.uid ?? null;
    const jsUid = jsAuthUid ?? null;

    const canCheckAccess =
      isAuthReady === true &&
      isProfileHydrated === true &&
      !!uid &&
      isNativeJsAuthed;

    if (!canCheckAccess) {
      if (!firebaseUser?.uid) {
        accessBootstrappedRef.current = null;
        setAccessGateChecked(false);
        setAccessGate(null);
        setAccessGateError(null);
      }
      return;
    }

    const profileStatus = resolveProfileAccessStatus(userProfile);

    // AuthView is the only place where beta invite access is entered/redeemed.
    // During sign-up, Firebase auth can flip to signed-in before AuthView's
    // invite-access Cloud Function finishes. If App runs bootstrap with no code in
    // that gap, the user lands in the old two-phase waitlist flow. So App simply
    // waits for AuthView to either grant access (user doc becomes active/member) or
    // reject/sign out. App never asks for or submits an invite/access code.
    if (!profileStatus && isSignupAccessBootstrapPending()) {
      accessBootstrappedRef.current = null;
      setAccessGateChecked(false);
      setAccessGate(null);
      setAccessGateError(null);

      if (import.meta.env.DEV) {
        console.debug(
          "[AccessGate] waiting for AuthView signup access bootstrap",
          {
            firebaseUid: uid,
            jsUid,
            profileStatus,
          },
        );
      }

      return;
    }

    // Trust durable member/blocked states immediately. A stale waitlisted state
    // must be sent through bootstrap so open signup can heal it to active.
    if (profileStatus && profileStatus !== "waitlisted") {
      accessBootstrappedRef.current = `${uid}:${profileStatus}:profile`;
      setAccessGate({
        accessStatus: profileStatus,
        memberNumber: (userProfile as any)?.memberNumber,
        waitlistNumber: (userProfile as any)?.waitlistNumber,
      });
      setAccessGateChecked(true);
      setAccessGateError(null);
      setInvitePromptOpen(false);
      setInvitePromptError(null);
      return;
    }

    // Normal sign-in / open launch path.
    // If a signed-in user has no durable access signal yet, ask the backend to
    // create or refresh their member access. The callable is the source of truth:
    // it keeps blocked users blocked, preserves already-active users, and only
    // waitlists when access config explicitly requires that.
    const bootstrapKey = `${uid}:${profileStatus || "NO_STATUS"}:bootstrap`;

    // A profile snapshot can rerender App while the callable is still in flight.
    // Never convert that harmless rerender into a false "Check access" failure.
    if (accessBootstrapInFlightRef.current === bootstrapKey) {
      setAccessGateChecked(false);
      setAccessGate(null);
      setAccessGateError(null);
      return;
    }

    if (accessBootstrappedRef.current === bootstrapKey) {
      setAccessGateChecked(true);
      setAccessGate(null);
      setAccessGateError(
        'Roddo could not confirm access automatically. Check your connection and try again.',
      );
      setInvitePromptOpen(false);
      setInvitePromptError(null);
      return;
    }

    accessBootstrapInFlightRef.current = bootstrapKey;
    setAccessGateChecked(false);
    setAccessGate(null);
    setAccessGateError(null);
    setInvitePromptOpen(false);
    setInvitePromptError(null);

    void (async () => {
      try {
        const storedPromoCode = readStoredInviteCode();
        const res: any = await withTimeout(
          cfBootstrapMemberAndAccess(
            storedPromoCode ? { inviteCode: storedPromoCode } : {},
          ),
          20000,
          "access_bootstrap",
        );
        if ((auth.currentUser?.uid ?? null) !== uid) return;

        const data = res?.data || {};
        const nextStatus = normalizeAccessStatus(data?.accessStatus);

        if (nextStatus) {
          if (nextStatus === "member") {
            clearStoredInviteCode();
            clearStoredAuthIntent();
          }

          setAccessGate({
            accessStatus: nextStatus,
            memberNumber: data?.memberNumber,
            waitlistNumber: data?.waitlistNumber,
          });
          setAccessGateChecked(true);
          setAccessGateError(data?.error ? String(data.error) : null);
          return;
        }

        setAccessGateChecked(true);
        setAccessGate(null);
        setAccessGateError(
          'Access is still refreshing for this account. Tap Check access, or sign out and sign back in if it does not continue.',
        );
      } catch (e: any) {
        if ((auth.currentUser?.uid ?? null) !== uid) return;
        console.warn('[AccessGate] member bootstrap failed', e);
        accessBootstrappedRef.current = null;
        setAccessGateChecked(true);
        setAccessGate(null);
        setAccessGateError(
          e?.message || 'Roddo could not refresh access yet. Check your connection and try again.',
        );
      } finally {
        if (accessBootstrapInFlightRef.current === bootstrapKey) {
          accessBootstrapInFlightRef.current = null;
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    bootPhase,
    isAuthReady,
    firebaseUser?.uid,
    jsAuthUid,
    userProfile,
    needsLegalNow,
    accessRetryNonce,
    IS_NATIVE,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.debug("[BOOT][access-state]", {
      firebaseUid: firebaseUser?.uid ?? null,
      authReady: isAuthReady,
      authLoading: isAuthLoading,
      jsUid: jsAuthUid ?? null,
      nativeUid,
      profileHydrated: isProfileHydrated,
      accessGateChecked,
      accessGate: accessGate ?? null,
      accessGateError,
      profileAccessStatus: profileAccessStatus ?? null,
      userProfileAccessStatus: (userProfile as any)?.accessStatus ?? null,
      resolvedAccessStatus,
      hasAppAccess,
      shouldShowInitialAppLoading,
      firstRunGlobePending: !hasShownBigBlueGlobe,
      bigBlueGlobeAssetsReady,
      activeView,
      bootPhase,
      needsLegalNow,
    });
  }, [
    isAuthReady,
    isAuthLoading,
    firebaseUser?.uid,
    jsAuthUid,
    isProfileHydrated,
    profileAccessStatus,
    accessGate,
    accessGateChecked,
    accessGateError,
    needsLegalNow,
    bootPhase,
  ]);
  // ---------------------------------------------------------------------------
  // Baleen Bay / Community listeners (CANONICAL)
  // - One posts listener strategy: subscribeCommunityPosts (service)
  // - Comments subscribe only for top N posts (<=10) using stable postIds key
  // - Hard gate on native: do NOT touch Firestore until JS uid matches native uid
  // - Watchdog always settles communityBootstrapped so UI never spins forever
  // ---------------------------------------------------------------------------
  const communityUnsubRef = useRef<null | (() => void)>(null);
  const communityCommentsUnsubRef = useRef<null | (() => void)>(null);
  const communityEnteredRef = useRef(false);

  // Debug log once per Community-view entry
  useEffect(() => {
    if (activeView === View.Community) {
      if (!communityEnteredRef.current) {
        communityEnteredRef.current = true;
        console.debug("[community][enter]", {
          nativeUid,
          jsUid,
          canAttachPublicListeners,
          needsLegalNow,
          isAuthReady,
          bootPhase,
        });
        // entering: reset spinner, but never allow infinite spin (watchdogs below)
        setCommunityBootstrapped(false);
      }
    } else {
      communityEnteredRef.current = false;
    }
  }, [
    activeView,
    nativeUid,
    jsUid,
    canAttachPublicListeners,
    needsLegalNow,
    isAuthReady,
    bootPhase,
  ]);

  // Fallback: one-shot refresh (getDocs). Used by watchdog if snapshot never arrives.
  const refreshCommunityFeed = useCallback(async () => {
    if (!canAttachPublicListeners) {
      console.warn("[community] refresh blocked (gate=false)", {
        nativeUid,
        jsUid,
        canAttachPublicListeners,
        needsLegalNow,
        isAuthReady,
        bootPhase,
      });
      // settle so UI never spins forever
      setCommunityBootstrapped(true);
      return;
    }

    const startedAt = Date.now();
    console.debug("[community] refresh start");

    try {
      const qRef = query(
        collection(db as any, "communityPosts"),
        where("visibility", "==", "public"),
        orderBy("timestamp", "desc"),
        limit(COMMUNITY_PAGE_SIZE),
      );

      const snap = await withTimeout(
        getDocs(qRef),
        FIRESTORE_TIMEOUT_MS,
        "community:getDocs",
      );

      console.debug("[community] refresh OK", {
        ms: Date.now() - startedAt,
        size: snap.size,
      });

      setCommunityHasMore(snap.size === COMMUNITY_PAGE_SIZE);

      const next: Record<string, CommunityPost> = {};
      snap.forEach((docSnap) => {
        next[docSnap.id] = {
          id: docSnap.id,
          ...(docSnap.data() as any),
        } as any;
      });

      setCommunityPosts(next);
      communityCursorRef.current = snap.docs[snap.docs.length - 1] ?? null;

      setCommunityBootstrapped(true);
    } catch (e: any) {
      console.error(
        "[community] refresh FAIL",
        { code: e?.code, message: e?.message },
        e,
      );
      addToast(
        `Community refresh FAIL: ${e?.code || e?.message || "unknown"}`,
        "error",
      );
      setCommunityBootstrapped(true);
    }
  }, [
    canAttachPublicListeners,
    nativeUid,
    jsUid,
    needsLegalNow,
    isAuthReady,
    bootPhase,
    addToast,
    COMMUNITY_PAGE_SIZE,
    FIRESTORE_TIMEOUT_MS,
  ]);

  // Attach POSTS listener only while in Community view
  useEffect(() => {
    // Always cleanup when leaving the view
    if (activeView !== View.Community) {
      if (communityUnsubRef.current)
        console.debug("[community] detach POSTS listener (leave view)");
      try {
        communityUnsubRef.current?.();
      } catch {}
      communityUnsubRef.current = null;
      return;
    }

    // Gate
    if (!canAttachPublicListeners) {
      console.debug("[community] POSTS gated off", {
        nativeUid,
        jsUid,
        canAttachPublicListeners,
      });
      setCommunityBootstrapped(true);
      return;
    }

    // kill any prior listener
    try {
      communityUnsubRef.current?.();
    } catch {}
    communityUnsubRef.current = null;

    console.debug("[community] attach POSTS listener", { nativeUid, jsUid });

    let didSettle = false;
    const watchdog = window.setTimeout(() => {
      if (didSettle) return;
      console.warn(
        "[community] POSTS watchdog fired (no snapshot/error) — running refresh fallback",
      );
      refreshCommunityFeed().catch(() => {});
      setCommunityBootstrapped(true);
    }, 6000);

    const unsub = subscribeCommunityPosts(
      (postsMap) => {
        didSettle = true;
        try {
          clearTimeout(watchdog);
        } catch {}
        setCommunityPosts(postsMap || {});
        setCommunityBootstrapped(true);
      },
      (err) => {
        didSettle = true;
        try {
          clearTimeout(watchdog);
        } catch {}
        console.error("[community] POSTS listener error", err);
        // fallback refresh (best effort)
        refreshCommunityFeed().catch(() => {});
        setCommunityBootstrapped(true);
      },
      nativeUid || undefined, // enables “mine” query in service
    );

    communityUnsubRef.current = unsub as any;

    return () => {
      try {
        clearTimeout(watchdog);
      } catch {}
      if (communityUnsubRef.current)
        console.debug("[community] detach POSTS listener (cleanup)");
      try {
        communityUnsubRef.current?.();
      } catch {}
      communityUnsubRef.current = null;
    };
  }, [
    activeView,
    canAttachPublicListeners,
    nativeUid,
    jsUid,
    refreshCommunityFeed,
  ]);

  // Compute a reduced/stable post list for comment subscriptions
  const subscribedCommentPostIds = useMemo(() => {
    if (activeView !== View.Community) return [];

    const focusPostId =
      (activeViewState as any)?.focusPostId ||
      (activeViewState as any)?.targetId ||
      "";

    const sortedIds = Object.values(communityPosts || {})
      .sort(
        (a: any, b: any) =>
          Number(b?.timestamp ?? 0) - Number(a?.timestamp ?? 0),
      )
      .map((p: any) => String(p?.id || "").trim())
      .filter(Boolean);

    const requestedIds = Object.keys(communityCommentPostIds || {})
      .map((id) => String(id || "").trim())
      .filter(Boolean);

    const picked: string[] = [];
    const pushUnique = (id?: string | null) => {
      const clean = String(id || "").trim();
      if (clean && !picked.includes(clean)) picked.push(clean);
    };

    pushUnique(String(focusPostId || ""));
    requestedIds.forEach(pushUnique);

    // Keep the first page hot, then add posts the user explicitly opens.
    // This prevents the old "comment count but no visible comments" bug without
    // subscribing to every historical post in the feed.
    for (const id of sortedIds) {
      if (picked.length >= 12) break;
      pushUnique(id);
    }

    return picked.slice(0, 12);
  }, [activeView, activeViewState, communityPosts, communityCommentPostIds]);

  const subscribedCommentPostIdsKey = useMemo(
    () => subscribedCommentPostIds.join("|"),
    [subscribedCommentPostIds],
  );

  // Attach COMMENTS listeners only while in Community view
  useEffect(() => {
    if (activeView !== View.Community) {
      if (communityCommentsUnsubRef.current)
        console.debug("[community] detach COMMENTS listeners (leave view)");
      try {
        communityCommentsUnsubRef.current?.();
      } catch {}
      communityCommentsUnsubRef.current = null;
      setCommentsByPost({});
      setCommunityCommentPostIds({});
      return;
    }

    if (!canAttachPublicListeners) {
      try {
        communityCommentsUnsubRef.current?.();
      } catch {}
      communityCommentsUnsubRef.current = null;
      setCommentsByPost({});
      setCommunityBootstrapped(true);
      return;
    }

    try {
      communityCommentsUnsubRef.current?.();
    } catch {}
    communityCommentsUnsubRef.current = null;

    if (!subscribedCommentPostIds.length) {
      setCommentsByPost({});
      setCommunityBootstrapped(true);
      return;
    }

    console.debug("[community] attach COMMENTS listeners", {
      n: subscribedCommentPostIds.length,
    });

    const unsub = subscribeCommentsForPosts(
      subscribedCommentPostIds,
      (byPost) => setCommentsByPost(byPost || {}),
      { limitPerPost: 50 },
    );

    communityCommentsUnsubRef.current = unsub as any;

    return () => {
      if (communityCommentsUnsubRef.current)
        console.debug("[community] detach COMMENTS listeners (cleanup)");
      try {
        communityCommentsUnsubRef.current?.();
      } catch {}
      communityCommentsUnsubRef.current = null;
    };
  }, [activeView, canAttachPublicListeners, subscribedCommentPostIdsKey]);
  // ---------------------------------------------------------------------------
  // Achievements: backend achievement docs are the source of truth for the Achievements screen
  // ---------------------------------------------------------------------------
  const achievementRowsForView = React.useMemo(
    () => achievementMap || {},
    [achievementMap],
  );

  useEffect(() => {
    // Redirect auth is web-only; native uses @capacitor-firebase/authentication
    if (IS_NATIVE) return;

    (async () => {
      try {
        const res = await getRedirectResult(auth);
        if (res?.user) {
          console.debug("[AUTH] redirect result user:", res.user.uid);
        }
      } catch (e) {
        // benign if no redirect happened
        console.warn("[AUTH] getRedirectResult error (often ok):", e);
      }
    })();
  }, []);

  // ---------------------------------------------------------------------------
  // Auth (WEB uses firebase/auth; NATIVE uses @capacitor-firebase/authentication)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();

    // -----------------------
    // Native (iOS/Android)
    // -----------------------
    if (isNative) {
      let alive = true;
      let removeNative: null | (() => void) = null;

      const mapNativeUser = (u: any) => {
        if (!u?.uid) return null;
        return {
          uid: u.uid,
          email: u.email ?? null,
          displayName: u.displayName ?? null,
          photoURL: u.photoUrl ?? u.photoURL ?? null,
          providerId: u.providerId ?? "Firebase",
          isAnonymous: u.isAnonymous === true,
        } as any;
      };

      (async () => {
        try {
          const cur = await FirebaseAuthentication.getCurrentUser();
          if (!alive) return;

          const mapped = mapNativeUser(cur?.user);
          console.debug(
            "[BOOT][native] getCurrentUser ->",
            !!mapped,
            mapped?.uid,
          );

          setFirebaseUser(mapped);
          setIsAuthReady(true);
          setIsAuthLoading(false);

          // ✅ IMPORTANT:
          // If native is authed, JS bridge is REQUIRED. Start in "not ready" until it succeeds.
          if (mapped?.uid) {
            setIsJsBridgeReady(false);
            setJsBridgeError(null);
          } else {
            setIsJsBridgeReady(true);
            setJsBridgeError(null);
          }
        } catch (e) {
          console.warn("[BOOT][native] getCurrentUser failed", e);
          if (!alive) return;

          setFirebaseUser(null);
          setIsAuthReady(true);
          setIsAuthLoading(false);

          // no native user -> allow app to show Auth normally
          setJsBridgeError(null);
          setIsJsBridgeReady(true);
        }

        try {
          const sub = await FirebaseAuthentication.addListener(
            "authStateChange",
            async (state: any) => {
              if (!alive) return;

              const mapped = mapNativeUser(state?.user);
              console.debug(
                "[BOOT][native] authStateChange ->",
                !!mapped,
                mapped?.uid,
              );

              setFirebaseUser(mapped);
              setIsAuthReady(true);
              setIsAuthLoading(false);

              if (!mapped) {
                // Native auth can report null before Firebase JS auth has finished
                // hydrating from IndexedDB/localStorage, and email sign-in is JS-only.
                // Do not sign out JS here. Let the JS onAuthStateChanged listener own
                // the final logged-in/logged-out decision.
                const jsUser = auth.currentUser;

                if (jsUser?.uid) {
                  console.debug(
                    "[BOOT][native] null native user ignored because JS auth is active",
                    jsUser.uid,
                  );
                  setFirebaseUser(jsUser as any);
                  setJsAuthUid(jsUser.uid);
                }

                setIsAuthReady(true);
                setIsAuthLoading(false);
                setJsBridgeError(null);
                setIsJsBridgeReady(true);
                return;
              }

              // signed in natively -> require JS bridge unless JS already matches
              if (auth.currentUser?.uid === mapped.uid) {
                setJsAuthUid(mapped.uid);
                setJsBridgeError(null);
                setIsJsBridgeReady(true);
              } else {
                setJsBridgeError(null);
                setIsJsBridgeReady(false);
              }
            },
          );

          removeNative = () => sub.remove();
        } catch (e) {
          console.warn("[BOOT][native] addListener(authStateChange) failed", e);
        }
      })();

      return () => {
        alive = false;
        try {
          removeNative?.();
        } catch {}
      };
    }

    // -----------------------
    // Web
    // -----------------------
    const unsub = onAuthStateChanged(auth, (u) => {
      setFirebaseUser(u ?? null);
      setIsAuthReady(true);
      setIsAuthLoading(false);

      if (!u) {
        setUserProfile(null);
        setCommunityPosts({});
        setCommentsByPost({});
        setCommunityHasMore(false);
        return;
      }

      u.getIdToken(true).catch((e) =>
        console.warn("[auth] getIdToken failed (continuing)", e),
      );
    });

    return () => unsub();
  }, []);

  // ---------------------------------------------------------------------------
  // Native lifecycle recovery
  // - Repair JS/native auth after returning from camera, Settings, lock screen, etc.
  // - Refresh the Firebase token only when the SDK considers it stale.
  // - Broadcast a lightweight resume event for active screens to repair layout.
  // - Never navigates, reloads Big Blue, spends a Search Lure, or forces GPS.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let disposed = false;
    let handle: { remove: () => Promise<void> | void } | null = null;
    let resumeInFlight: Promise<void> | null = null;

    const recoverAfterResume = async () => {
      if (disposed || resumeInFlight) return resumeInFlight;

      resumeInFlight = (async () => {
        try {
          await ensureJsAuthFromNative("app-resume");
        } catch (error) {
          console.warn("[lifecycle] auth bridge resume repair skipped", error);
        }

        try {
          await auth.currentUser?.getIdToken(false);
        } catch (error) {
          // Offline resume is allowed. Firebase will refresh when connectivity returns.
          console.warn("[lifecycle] token refresh deferred", error);
        }

        if (disposed) return;
        try {
          window.dispatchEvent(
            new CustomEvent("roddo:app:resume", {
              detail: { ts: Date.now() },
            }),
          );
        } catch {}
      })();

      try {
        await resumeInFlight;
      } finally {
        resumeInFlight = null;
      }
    };

    void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void recoverAfterResume();
    }).then((listener) => {
      if (disposed) {
        void listener.remove();
        return;
      }
      handle = listener;
    }).catch((error) => {
      console.warn("[lifecycle] appStateChange listener unavailable", error);
    });

    return () => {
      disposed = true;
      try {
        void handle?.remove();
      } catch {}
      handle = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Hard reset auth/access/sync boot state on real sign-out
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (firebaseUser?.uid) return;

    accessBootstrappedRef.current = null;
    accountSyncAutoAttemptedForUidRef.current = null;
    accountSyncRetryInFlightRef.current = false;

    setAccessGate(null);
    setAccessGateChecked(false);
    setAccessGateError(null);
    setInvitePromptOpen(false);
    setInvitePromptError(null);
    setIsProfileHydrated(false);
    setJsBridgeError(null);
    setIsJsBridgeReady(!IS_NATIVE);
    setIsAccountSyncRetrying(false);
    setAccountSyncDebug("idle");
    setJsAuthUid(auth.currentUser?.uid ?? null);
  }, [firebaseUser?.uid, IS_NATIVE]);
  // ---------------------------------------------------------------------------
  // JS Auth mirror listener
  // Native Android uses @capacitor-firebase/authentication for the Google picker,
  // then signInWithCredential() creates the real Firebase JS session. This listener
  // must promote the JS user into App state; otherwise AuthView can complete Google
  // sign-in and still sit on "Loading Roddo..." forever.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setJsAuthUid(u?.uid ?? null);

      if (!IS_NATIVE) return;
      setIsAuthReady(true);
      setIsAuthLoading(false);

      if (u?.uid) {
        setFirebaseUser(u);
        setJsBridgeError(null);
        setIsJsBridgeReady(true);
        return;
      }

      // JS auth may emit null while its persistent WebView state is hydrating.
      // A durable native guest/provider session is authoritative during that
      // window and must not be erased, or the next tap on Guest creates a new UID.
      try {
        const nativeCurrent: any = await FirebaseAuthentication.getCurrentUser();
        const nativeUser = nativeCurrent?.user;
        if (nativeUser?.uid) {
          setFirebaseUser((prev: any) =>
            prev?.uid === nativeUser.uid
              ? prev
              : {
                  uid: nativeUser.uid,
                  email: nativeUser.email ?? null,
                  displayName: nativeUser.displayName ?? null,
                  photoURL: nativeUser.photoUrl ?? nativeUser.photoURL ?? null,
                  providerId: nativeUser.providerId ?? "Firebase",
                  isAnonymous: nativeUser.isAnonymous === true,
                },
          );
          setJsBridgeError(null);
          setIsJsBridgeReady(false);
          return;
        }
      } catch (error) {
        console.warn("[BOOT][js-mirror] native session check failed", error);
      }

      // A later auth callback may have restored the JS user while the native
      // lookup above was awaiting. Never let the older null callback erase it.
      if (auth.currentUser?.uid) {
        setFirebaseUser(auth.currentUser);
        setJsAuthUid(auth.currentUser.uid);
        setJsBridgeError(null);
        setIsJsBridgeReady(true);
        return;
      }

      setFirebaseUser(null);
      setUserProfile(null);
      setCommunityPosts({});
      setCommentsByPost({});
      setCommunityHasMore(false);
      setJsBridgeError(null);
      setIsJsBridgeReady(true);
    });

    return () => unsub();
  }, [IS_NATIVE]);

  // ---------------------------------------------------------------------------
  // Native → JS auth bridge trigger
  // Android clean mode:
  // - Native auth boots first.
  // - JS Firebase Auth is what Firestore listeners/rules use.
  // - If native uid exists and JS uid does not match, bridge ONCE automatically.
  // - Retry button remains recovery-only.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!IS_NATIVE) return;

    const nativeUid = firebaseUser?.uid ?? null;
    const currentJsUid = auth.currentUser?.uid ?? jsAuthUid ?? null;

    console.debug("[BOOT][bridge-effect] check", {
      nativeUid,
      jsUid: currentJsUid,
      isJsBridgeReady,
      inFlight: accountSyncRetryInFlightRef.current,
      attemptedUid: accountSyncAutoAttemptedForUidRef.current,
      mode: "auto-once",
    });

    if (!nativeUid) {
      accountSyncAutoAttemptedForUidRef.current = null;
      accountSyncRetryInFlightRef.current = false;
      setIsAccountSyncRetrying(false);
      setAccountSyncDebug("no native user");
      setIsJsBridgeReady(true);
      setJsBridgeError(null);
      return;
    }

    if (currentJsUid === nativeUid) {
      accountSyncAutoAttemptedForUidRef.current = nativeUid;
      accountSyncRetryInFlightRef.current = false;
      setIsAccountSyncRetrying(false);
      setAccountSyncDebug("already synced");
      setIsJsBridgeReady(true);
      setJsBridgeError(null);

      if (jsAuthUid !== nativeUid) {
        setJsAuthUid(nativeUid);
      }

      return;
    }

    setIsJsBridgeReady(false);

    if (accountSyncRetryInFlightRef.current) {
      setAccountSyncDebug("signing in...");
      return;
    }

    // One automatic bridge attempt per native uid per app session.
    // If it fails, the protected-screen fallback Retry remains available.
    if (accountSyncAutoAttemptedForUidRef.current === nativeUid) {
      setAccountSyncDebug("sync needed");
      setJsBridgeError(
        "Account sync needed. Tap Retry if it does not continue.",
      );
      return;
    }

    accountSyncAutoAttemptedForUidRef.current = nativeUid;
    accountSyncRetryInFlightRef.current = true;
    setIsAccountSyncRetrying(true);
    setAccountSyncDebug("signing in...");
    setJsBridgeError(null);

    let cancelled = false;

    void (async () => {
      try {
        const ok = await runNativeJsBridge(nativeUid, "boot-auto");

        const afterUid = auth.currentUser?.uid ?? null;
        const done = `auto bridge finished | ok=${String(ok)} native=${nativeUid} js=${afterUid || "null"}`;

        console.debug("[account-sync]", done);

        if (cancelled) return;

        setAccountSyncDebug(done);

        if (ok && afterUid === nativeUid) {
          setJsAuthUid(nativeUid);
          setIsJsBridgeReady(true);
          setJsBridgeError(null);
          setFirebaseUser((prev: any) => prev ?? auth.currentUser ?? null);
        } else {
          setIsJsBridgeReady(false);
          setJsBridgeError("Account sync failed. Tap Retry to try again.");
        }
      } catch (e: any) {
        const msg = `auto bridge threw | ${e?.message || String(e)}`;
        console.warn("[account-sync]", msg, e);

        if (!cancelled) {
          setAccountSyncDebug(msg);
          setIsJsBridgeReady(false);
          setJsBridgeError("Account sync failed. Tap Retry to try again.");
        }
      } finally {
        accountSyncRetryInFlightRef.current = false;
        if (!cancelled) setIsAccountSyncRetrying(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    IS_NATIVE,
    firebaseUser?.uid,
    jsAuthUid,
    isJsBridgeReady,
    runNativeJsBridge,
  ]);
  // ---------------------------------------------------------------------------
  // Profile listener + seed
  // ✅ On native: do NOT touch Firestore until JS auth is bridged.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthReady || !firebaseUser?.uid) return;

    const nativeUid = firebaseUser?.uid ?? null;
    const jsUid = jsAuthUid ?? null;
    const uid = firebaseUser.uid;

    // Native Firestore uses Firebase JS auth. Never attach the canonical
    // profile listener under a missing/different JS identity.
    if (IS_NATIVE && nativeUid && jsUid !== nativeUid) {
      const key = `${nativeUid}:${jsUid ?? "null"}`;
      if (profileGateLogKeyRef.current !== key) {
        profileGateLogKeyRef.current = key;
        console.warn(
          "[PROFILE] waiting for native -> JS auth uid match before hydration",
          { nativeUid, jsUid },
        );
      }

      setIsProfileHydrated(false);

      // If an account switch happened, do not let the previous account's
      // profile remain visible while the new native identity is bridging.
      setUserProfile((prev) =>
        prev?.uid && prev.uid !== nativeUid ? null : prev,
      );

      return;
    }

    if (IS_NATIVE && nativeUid && jsUid === nativeUid) {
      profileGateLogKeyRef.current = null;
    }

    // Start "not hydrated" for this authenticated identity.
    setIsProfileHydrated(false);

    // Remove stale profile state before seeding/subscribing for another UID.
    setUserProfile((prev) =>
      prev?.uid && prev.uid !== uid ? null : prev,
    );

    // Local seed is web-only / fully-bridged only.
    const starterName = resolveAuthDisplayName();
    setUserProfile(
      (prev) =>
        prev ??
        (normalizeUserProfileReputation({
          uid,
          name: starterName,
          displayName: starterName,
          onboardingCompleted: false,
          createdAt: Date.now(),
        } as any) as any),
    );

    // Safety: if Firestore listener is slow/blocked, don't freeze the whole app
    const t = window.setTimeout(() => {
      // Do not force the access gate after only 1.5s on first login. Native Apple/Google
      // can still be finishing bridge + callable + Firestore propagation here.
      setIsProfileHydrated(true);
    }, IS_NATIVE ? 12000 : 7000);

    const unsub = subscribeToUserProfile(
      uid,
      async (profile) => {
        if (profile) {
          window.clearTimeout(t);

          const normalizedProfile = normalizeUserProfileReputation(
            profile as any,
          ) as UserProfile;

          setUserProfile((prev) => {
            if (
              prev &&
              stableProfileSnapshotKey(prev) ===
                stableProfileSnapshotKey(normalizedProfile)
            ) {
              return prev;
            }
            return normalizedProfile;
          });
          setUsers((prev) => ({
            ...prev,
            [uid]: {
              ...(prev?.[uid] as any),
              ...profileToAppUser(normalizedProfile as any),
            },
          }));
          setIsProfileHydrated(true);

          // IMPORTANT: profile hydration must not independently open/close the
          // welcome card or guided tour. The canonical onboarding effect above
          // owns that UI state and intentionally honors guidedTourReplayMode.
          // Keeping a second decision path here caused Legal -> Replay tour to
          // close itself as soon as a normal Firestore profile snapshot arrived
          // for a user who had already completed the persisted tour version.
          return;
        }

        // Missing doc -> seed in firestore
        const starterName = resolveAuthDisplayName();
        const seed: Partial<UserProfile> = normalizeUserProfileReputation({
          uid,
          name: starterName,
          displayName: starterName,
          onboardingCompleted: false,
          createdAt: Date.now(),
        } as any) as any;

        if (IS_NATIVE && jsUid !== nativeUid) {
          console.warn(
            "[PROFILE] missing/blocked profile while JS auth is not bridged; skipping blank seed",
            { nativeUid, jsUid },
          );
          setIsProfileHydrated(true);
          return;
        }

        try {
          await updateUserProfile(uid, seed as any);
        } catch (e) {
          console.error("[PROFILE] seed failed", e);
          setIsProfileHydrated(true);
        }
      },
      (err) => {
        console.error("[LISTENER] userProfile", err);
        setIsProfileHydrated(true);
      },
    );

    return () => {
      window.clearTimeout(t);
      unsub();
    };
  }, [
    isAuthReady,
    firebaseUser?.uid,
    IS_NATIVE,
    isJsBridgeReady,
    jsAuthUid,
    resolveAuthDisplayName,
  ]);

  // ---------------------------------------------------------------------------
  // Access bootstrap (unchanged)
  // ---------------------------------------------------------------------------
  const didBootstrapAccessRef = useRef(false);
  const accessBootstrapInFlightRef = useRef<string | null>(null);
  const accessBootstrappedRef = useRef<string | null>(null);

  useEffect(() => {
    const resetAccessAfterAuthViewBootstrap = () => {
      clearStoredAuthIntent();
      accessBootstrappedRef.current = null;
      setAccessGateChecked(false);
      setAccessGate(null);
      setAccessGateError(null);
      setInvitePromptOpen(false);
      setInvitePromptError(null);
      setAccessRetryNonce((n) => n + 1);
    };

    window.addEventListener(
      "roddo:access-granted",
      resetAccessAfterAuthViewBootstrap,
    );
    window.addEventListener(
      "roddo:access-bootstrap-finished",
      resetAccessAfterAuthViewBootstrap,
    );

    return () => {
      window.removeEventListener(
        "roddo:access-granted",
        resetAccessAfterAuthViewBootstrap,
      );
      window.removeEventListener(
        "roddo:access-bootstrap-finished",
        resetAccessAfterAuthViewBootstrap,
      );
    };
  }, []);

  const retryAccessWithInviteCode = useCallback(() => {
    const code = (pendingInviteCode || "").trim().toUpperCase();

    setPendingInviteCode(code);
    setSubmittedInviteCode(code);
    writeStoredInviteCode(code);

    accessBootstrappedRef.current = null;
    setAccessGateChecked(false);
    setAccessGate(null);
    setAccessGateError(null);
    setInvitePromptError(null);
    setInvitePromptOpen(false);
    setAccessRetryNonce((n) => n + 1);
  }, [pendingInviteCode]);

  useEffect(() => {
    const uid = userProfile?.uid;
    if (!uid) return;

    if (didBootstrapAccessRef.current) return;
    if (accessBootstrapInFlightRef.current === uid) return;

    const key = `access_bootstrap_done_${uid}`;
    if (localStorage.getItem(key) === "1") {
      didBootstrapAccessRef.current = true;
      return;
    }

    const alreadyHas =
      Number.isFinite((userProfile as any)?.memberNumber) &&
      typeof (userProfile as any)?.accessStatus === "string" &&
      (userProfile as any).accessStatus.length > 0;

    if (alreadyHas) {
      localStorage.setItem(key, "1");
      didBootstrapAccessRef.current = true;
      return;
    }
  }, [userProfile?.uid, pendingInviteCode]);

  // ---------------------------------------------------------------------------
  // FishIQ bootstrap (unchanged)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const uid = userProfile?.uid;
    if (!uid) return;

    if (didBootstrapFishIQRef.current) return;
    if (fishiqBootstrapInFlightRef.current === uid) return;

    const key = `fishiq_bootstrap_done_${uid}`;
    if (localStorage.getItem(key) === "1") {
      didBootstrapFishIQRef.current = true;
      return;
    }

    fishiqBootstrapInFlightRef.current = uid;

    (async () => {
      try {
        const already = !!(userProfile as any)?.fishiq?.bootstrappedAt;
        if (already) {
          localStorage.setItem(key, "1");
          didBootstrapFishIQRef.current = true;
          return;
        }

        const res = await fishIQBootstrapUser({});
        if (res?.data?.ok) {
          localStorage.setItem(key, "1");
          didBootstrapFishIQRef.current = true;
        }
      } catch (e) {
        console.warn("[FishIQ] bootstrap failed (non-fatal)", e);
      } finally {
        fishiqBootstrapInFlightRef.current = null;
      }
    })();
  }, [userProfile?.uid]);

  // ---------------------------------------------------------------------------
  // Catch log listener (normalize shape)
  // ---------------------------------------------------------------------------
  const normalizeCatchLogEntry = (e: any): LogEntry => {
    const toMillisSafe = (value: any): number => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      if (value && typeof value.toMillis === "function") {
        try {
          const millis = value.toMillis();
          if (typeof millis === "number" && Number.isFinite(millis))
            return millis;
        } catch {}
      }
      if (value && typeof value.toDate === "function") {
        try {
          const dt = value.toDate();
          if (dt instanceof Date && Number.isFinite(dt.getTime()))
            return dt.getTime();
        } catch {}
      }
      if (value instanceof Date && Number.isFinite(value.getTime()))
        return value.getTime();
      if (typeof value === "string" && value.trim()) {
        const parsed = Date.parse(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return 0;
    };

    const timestamp =
      toMillisSafe(e?.timestamp) ||
      toMillisSafe(e?.createdAt) ||
      toMillisSafe(e?.updatedAt) ||
      Date.now();

    const lat = Number(e?.lat ?? e?.coords?.lat);
    const lng = Number(e?.lng ?? e?.lon ?? e?.coords?.lng ?? e?.coords?.lon);

    return {
      id: String(e?.id ?? `log_${timestamp}`),
      species: String(e?.species ?? ""),
      weight: String(e?.weight ?? ""),
      date:
        typeof e?.date === "string" && e.date
          ? e.date
          : new Date(timestamp).toISOString().slice(0, 10),
      time:
        typeof e?.time === "string" && e.time
          ? e.time
          : new Date(timestamp).toTimeString().slice(0, 5),
      location: String(e?.location ?? e?.locationName ?? ""),
      timestamp,
      image:
        e?.image ??
        e?.imageUrl ??
        e?.imageUrls?.[0] ??
        e?.images?.[0] ??
        undefined,
      ...(Array.isArray(e?.images) ? { images: e.images } : {}),
      ...(Array.isArray(e?.imageUrls) ? { imageUrls: e.imageUrls } : {}),
      ...(e?.imageUrl ? { imageUrl: e.imageUrl } : {}),
      ...(e?.source ? { source: e.source } : {}),
      ...(e?.createdFrom ? { createdFrom: e.createdFrom } : {}),
      ...(e?.sourceId ? { sourceId: e.sourceId } : {}),
      ...(e?.catchId ? { catchId: e.catchId } : {}),
      ...(e?.communityPostId ? { communityPostId: e.communityPostId } : {}),
      rod: e?.rod ?? "",
      reel: e?.reel ?? "",
      lureOrBait: e?.lureOrBait ?? undefined,
      lureOrBaitType: e?.lureOrBaitType ?? undefined,
      technique: e?.technique ?? "",
      weather: e?.weather ?? undefined,
      ...(e?.rodId ? { rodId: e.rodId } : {}),
      ...(e?.reelId ? { reelId: e.reelId } : {}),
      ...(e?.lureOrBaitId ? { lureOrBaitId: e.lureOrBaitId } : {}),
      ...(Number.isFinite(lat) ? { lat } : {}),
      ...(Number.isFinite(lng) ? { lng } : {}),
      ...(Number.isFinite(lat) && Number.isFinite(lng)
        ? { coords: { lat, lon: lng } }
        : {}),
    } as any;
  };

  const [catchLog, setCatchLog] = useState<LogEntry[]>([]);
  useEffect(() => {
    if (!canAttachPrivateListeners) {
      setCatchLog([]);
      return;
    }

    const unsub = subscribeToCatchLog(
      firebaseUser.uid,
      (entries) =>
        setCatchLog(
          (Array.isArray(entries) ? entries : []).map(normalizeCatchLogEntry),
        ),
      (err) => console.error("[LISTENER] catchLog", err),
    );

    return () => unsub();
  }, [canAttachPrivateListeners, firebaseUser?.uid]);

  // ---------------------------------------------------------------------------
  // Catch diary fetch
  // ---------------------------------------------------------------------------
  // Use the service-level transitional reader so the private Roddo Log and the
  // server projection see the same canonical + legacy diary inputs. Canonical
  // users/{uid}/catchDiary records overwrite matching legacy diary ids there.
  useEffect(() => {
    if (!canAttachPrivateListeners || !userProfile?.uid) {
      setCatchDiaryEntries([]);
      return;
    }

    return subscribeToCatchDiaryEntries(
      userProfile.uid,
      (entries) => setCatchDiaryEntries(entries as any),
      (err) => console.error("[LISTENER] catchDiary", err),
    );
  }, [canAttachPrivateListeners, userProfile?.uid]);

  // ---------------------------------------------------------------------------
  useEffect(() => subscribeTournamentConfig(setTournamentConfig), []);

  // Tournaments listener
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const canReadTournamentCollection =
      isAuthReady && (isAdmin || tournamentConfig.publicAccessEnabled === true);

    setTournamentBootReady(false);

    if (!canReadTournamentCollection) {
      setTournaments({});
      setTournamentBootReady(true);
      return;
    }

    const unsubscribe = subscribeToTournaments(
      (tournamentMap) => {
        setTournaments(tournamentMap || {});
        setTournamentBootReady(true);
      },
      { includeAdminAll: isAdmin },
    );

    return () => unsubscribe();
  }, [isAuthReady, isAdmin, tournamentConfig.publicAccessEnabled]);

  // ---------------------------------------------------------------------------
  // Licenses listener
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canAttachPrivateListeners) {
      setUserLicenses([]);
      return;
    }

    const unsub = subscribeToUserLicenses(
      firebaseUser.uid,
      (licenses) => setUserLicenses(licenses ?? []),
      (err) => console.error("[LISTENER] licenses", err),
    );

    return () => unsub();
  }, [canAttachPrivateListeners]);

  // ---------------------------------------------------------------------------
  // Marketplace items listener (service) — tolerant keying
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canAttachPublicListeners) {
      setMarketplaceItems({});
      return;
    }

    const unsub = subscribeMarketplaceItems(
      (items: any) => {
        const list: any[] = Array.isArray(items)
          ? items
          : Object.values(items || {});
        const next: Record<any, MarketplaceItem> = {};

        (list || []).forEach((it: any) => {
          const raw =
            it?.id ??
            it?.itemId ??
            it?.legacyDocId ??
            it?.docId ??
            `item_${Date.now()}_${Math.random().toString(36).slice(2)}`;

          const n = Number(raw);
          const key = Number.isFinite(n) ? n : String(raw);

          next[key] = it;
        });

        setMarketplaceItems(next as any);
      },
      { max: 200 },
    );

    return () => unsub();
  }, [canAttachPublicListeners]);

  // ---------------------------------------------------------------------------
  // Marketplace transactions listener
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canAttachPrivateListeners || !FEATURE.BAZAAR_PAYMENTS_ENABLED) {
      setMarketplaceTransactions({});
      setEscrowByItemId({});
      return;
    }

    const unsub = subscribeMarketplaceTransactionsForUser(
      firebaseUser.uid,
      (txs) => {
        const nextTx: Record<string, MarketplaceTransaction> = {};
        const nextEscrow: Record<string, EscrowInfo> = {};

        (txs || []).forEach((tx: any) => {
          if (!tx?.id) return;
          nextTx[tx.id] = tx;

          const itemId = String(tx.itemId || '').trim();
          if (!itemId) return;

          const cents =
            typeof tx.amountTotal === "number"
              ? tx.amountTotal
              : typeof tx.amountCents === "number"
                ? tx.amountCents
                : typeof tx.amount === "number"
                  ? tx.amount
                  : 0;

          nextEscrow[itemId] = {
            transactionId: tx.id,
            amountTotal: cents,
            currency: tx.currency ?? "usd",
          };
        });

        setMarketplaceTransactions(nextTx);
        setEscrowByItemId(nextEscrow);
      },
      { role: "any", max: 50 },
    );

    return () => unsub();
  }, [canAttachPrivateListeners, firebaseUser?.uid]);

  // ---------------------------------------------------------------------------
  // Notifications listener
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canAttachPrivateListeners) {
      setNotifications([]);
      return;
    }

    const unsub = subscribeToNotifications(
      firebaseUser.uid,
      (notifs) => setNotifications(notifs ?? []),
      (err) => console.error("[LISTENER] notifications", err),
      { max: 50 },
    );

    return () => unsub();
  }, [canAttachPrivateListeners, firebaseUser?.uid]);

  useEffect(() => {
    if (!canAttachPrivateListeners || !userProfile?.uid) {
      setChallengeProgressMap({});
      setChallengeCompletionMap({});
      setAchievementMap({});
      return;
    }

    const unsubA = subscribeToChallengeProgress(
      userProfile.uid,
      setChallengeProgressMap,
    );
    const unsubB = subscribeToChallengeCompletions(
      userProfile.uid,
      setChallengeCompletionMap,
    );
    const unsubC = subscribeToAchievements(userProfile.uid, setAchievementMap);

    return () => {
      try {
        unsubA?.();
      } catch {}
      try {
        unsubB?.();
      } catch {}
      try {
        unsubC?.();
      } catch {}
    };
  }, [canAttachPrivateListeners, userProfile?.uid]);

  const seenCompletionKeysRef = useRef<
    Record<string, BackendChallengeCompletion>
  >({});
  const seenAchievementKeysRef = useRef<Record<string, BackendAchievement>>({});
  const seenProgressRef = useRef<Record<string, BackendChallengeProgress>>({});
  const seenLevelRef = useRef<number | null>(null);
  const seenRewardBalanceRef = useRef<{ xp: number; shells: number } | null>(null);
  const rewardBalanceBootGraceUntilRef = useRef<number>(0);
  const didHydrateChallengeCompletionsRef = useRef(false);
  const didHydrateAchievementsRef = useRef(false);

  const activeProgressionUidRef = useRef<string | null>(null);
  const seenProgressionToastIdsRef = React.useRef<Record<string, boolean>>({});
  const pendingProgressionToastsRef = useRef<
    Array<{ id: string; message: string; type: Toast["type"] }>
  >([]);
  const progressionToastFlushTimerRef = useRef<number | null>(null);

  const progressionToastStorageKey = useMemo(
    () =>
      userProfile?.uid
        ? `roddo:seenProgressionToasts:${userProfile.uid}`
        : null,
    [userProfile?.uid],
  );

  useEffect(() => {
    const nextUid = userProfile?.uid || null;
    if (activeProgressionUidRef.current === nextUid) return;

    activeProgressionUidRef.current = nextUid;

    seenCompletionKeysRef.current = {};
    seenAchievementKeysRef.current = {};
    seenProgressRef.current = {};
    seenLevelRef.current = null;
    seenRewardBalanceRef.current = null;
    rewardBalanceBootGraceUntilRef.current = nextUid ? Date.now() + 3000 : 0;
    didHydrateChallengeCompletionsRef.current = false;
    didHydrateAchievementsRef.current = false;
    pendingProgressionToastsRef.current = [];

    if (progressionToastFlushTimerRef.current != null) {
      window.clearTimeout(progressionToastFlushTimerRef.current);
      progressionToastFlushTimerRef.current = null;
    }
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!progressionToastStorageKey) {
      seenProgressionToastIdsRef.current = {};
      return;
    }

    try {
      const raw = window.localStorage.getItem(progressionToastStorageKey);
      seenProgressionToastIdsRef.current = raw ? JSON.parse(raw) : {};
    } catch {
      seenProgressionToastIdsRef.current = {};
    }
  }, [progressionToastStorageKey]);

  const markProgressionToastSeen = useCallback(
    (toastId: string) => {
      if (!toastId) return;

      if (seenProgressionToastIdsRef.current[toastId]) return;

      const next = {
        ...seenProgressionToastIdsRef.current,
        [toastId]: true,
      };

      seenProgressionToastIdsRef.current = next;

      if (!progressionToastStorageKey) return;

      try {
        window.localStorage.setItem(
          progressionToastStorageKey,
          JSON.stringify(next),
        );
      } catch {}
    },
    [progressionToastStorageKey],
  );

  const flushProgressionToastQueue = useCallback(() => {
    if (progressionToastFlushTimerRef.current != null) {
      window.clearTimeout(progressionToastFlushTimerRef.current);
      progressionToastFlushTimerRef.current = null;
    }

    const queued = pendingProgressionToastsRef.current;
    pendingProgressionToastsRef.current = [];

    if (!queued.length) return;

    const unique = queued.filter(
      (item, index, arr) => arr.findIndex((x) => x.id === item.id) === index,
    );

    if (unique.length === 1) {
      addToast(unique[0].message, unique[0].type);
      return;
    }

    if (unique.length === 2) {
      unique.forEach((item) => addToast(item.message, item.type));
      return;
    }

    const achievementCount = unique.filter((item) =>
      item.id.includes("achievement:"),
    ).length;
    const challengeCount = unique.filter((item) =>
      item.id.includes("challenge:"),
    ).length;
    const levelCount = unique.filter((item) => item.id.includes("level:"))
      .length;

    const parts = [
      challengeCount
        ? `${challengeCount} challenge${challengeCount === 1 ? "" : "s"}`
        : "",
      achievementCount
        ? `${achievementCount} achievement${achievementCount === 1 ? "" : "s"}`
        : "",
      levelCount ? `${levelCount} level-up${levelCount === 1 ? "" : "s"}` : "",
    ].filter(Boolean);

    addToast(
      parts.length
        ? `Nice work — ${parts.join(", ")} completed.`
        : `Nice work — ${unique.length} Roddo rewards completed.`,
      "success",
    );
  }, [addToast]);

  useEffect(() => {
    return () => {
      if (progressionToastFlushTimerRef.current != null) {
        window.clearTimeout(progressionToastFlushTimerRef.current);
        progressionToastFlushTimerRef.current = null;
      }
    };
  }, []);

  const toastProgressionOnce = useCallback(
    (toastId: string, message: string, type: Toast["type"]) => {
      if (!toastId || seenProgressionToastIdsRef.current[toastId]) return;

      // Mark before showing so completed rewards do not replay after reload.
      markProgressionToastSeen(toastId);

      pendingProgressionToastsRef.current = [
        ...pendingProgressionToastsRef.current,
        { id: toastId, message, type },
      ];

      if (progressionToastFlushTimerRef.current != null) {
        window.clearTimeout(progressionToastFlushTimerRef.current);
      }

      progressionToastFlushTimerRef.current = window.setTimeout(() => {
        flushProgressionToastQueue();
      }, 450);
    },
    [flushProgressionToastQueue, markProgressionToastSeen],
  );

  useEffect(() => {
    const next = achievementMap || {};

    if (!didHydrateAchievementsRef.current) {
      seenAchievementKeysRef.current = next;
      didHydrateAchievementsRef.current = true;
      return;
    }

    const prev = seenAchievementKeysRef.current || {};
    const newKeys = diffNewKeys(prev, next);

    newKeys.forEach((key) => {
      const row = next[key];
      if (!row) return;

      const rewardXp = Number(row.rewardXp ?? 0);
      const title = String(row.title || "Achievement unlocked");
      const achievementToastId = `achievement:${userProfile?.uid || "anon"}:${key}`;

      toastProgressionOnce(
        achievementToastId,
        `Achievement unlocked: ${title}`,
        "success",
      );
    });

    seenAchievementKeysRef.current = next;
  }, [achievementMap, toastProgressionOnce, userProfile?.uid]);

  useEffect(() => {
    const next = challengeCompletionMap || {};

    if (!didHydrateChallengeCompletionsRef.current) {
      seenCompletionKeysRef.current = next;
      didHydrateChallengeCompletionsRef.current = true;
      return;
    }

    const prev = seenCompletionKeysRef.current || {};
    const newKeys = diffNewKeys(prev, next);

    newKeys.forEach((key) => {
      const row = next[key];
      if (!row) return;

      const title =
        String((row as any).title || "").trim() ||
        String(
          (defaultChallenges as any[]).find(
            (challenge: any) => String(challenge?.id || "") === key,
          )?.title || "",
        ).trim() ||
        "Challenge";

      const rewardXp = Number((row as any).rewardXp ?? 0);
      const challengeToastId = `challenge:${userProfile?.uid || "anon"}:${key}:completed`;

      toastProgressionOnce(
        challengeToastId,
        `Challenge completed: ${title}`,
        "success",
      );
    });

    seenCompletionKeysRef.current = next;
  }, [challengeCompletionMap, toastProgressionOnce, userProfile?.uid]);

  useEffect(() => {
    const next = challengeProgressMap || {};
    const prev = seenProgressRef.current || {};

    Object.entries(next).forEach(([key, row]) => {
      const prevRow = prev[key];
      if (!prevRow) return;

      const wasCompleted = !!prevRow.completed;
      const isCompleted = !!row.completed;

      if (!wasCompleted && isCompleted) {
        toastProgressionOnce(
          `challenge:${userProfile?.uid || "anon"}:${key}:completed`,
          `Challenge completed: ${row.title}`,
          "success",
        );
      }
    });

    seenProgressRef.current = next;
  }, [challengeProgressMap, toastProgressionOnce, userProfile?.uid]);

  useEffect(() => {
    const currentLevel = Number((userProfile as any)?.level ?? 1);

    if (!Number.isFinite(currentLevel)) return;

    if (seenLevelRef.current == null) {
      seenLevelRef.current = currentLevel;
      return;
    }

    if (currentLevel > seenLevelRef.current) {
      toastProgressionOnce(
        `level:${userProfile?.uid || "anon"}:${currentLevel}`,
        `Level up! You reached Level ${currentLevel}`,
        "success",
      );
    }

    seenLevelRef.current = currentLevel;
  }, [userProfile, toastProgressionOnce]);

  useEffect(() => {
    if (!userProfile?.uid) {
      seenRewardBalanceRef.current = null;
      return;
    }

    const currentXp = Number((userProfile as any)?.xp ?? 0);
    const currentShells = Number((userProfile as any)?.shells ?? 0);

    if (!Number.isFinite(currentXp) || !Number.isFinite(currentShells)) return;

    const previous = seenRewardBalanceRef.current;
    seenRewardBalanceRef.current = { xp: currentXp, shells: currentShells };

    // Hydrate silently so existing accounts do not get a reward popup on boot.
    // Also suppress the first live-profile replacement window, where cached user data
    // can be replaced by fresher Firestore data and look like newly earned XP/Shells.
    if (!previous) return;
    if (Date.now() < rewardBalanceBootGraceUntilRef.current) return;

    // First-run gates can replace a cached/recomputed profile with fresh balances.
    // Treat those balance changes as hydration, not as newly earned rewards.
    if (onboardingNeeded || showWelcome) return;

    // Launch quiet pass: routine XP/Shell balance changes are reflected in the UI.
    // Specific moments like Today's Cast, daily tasks, catch logging, and errors still toast elsewhere.
  }, [addToast, onboardingNeeded, showWelcome, userProfile?.uid, (userProfile as any)?.xp, (userProfile as any)?.shells]);

  // Following listener
  useEffect(() => {
    if (!canAttachPrivateListeners) {
      setFollowingIds([]);
      return;
    }

    const unsub = subscribeToFollowing(
      firebaseUser.uid,
      (ids) => setFollowingIds(ids ?? []),
      (err) => console.error("[LISTENER] following", err),
    );

    return () => unsub();
  }, [canAttachPrivateListeners]);

  // Gear listener
  useEffect(() => {
    if (!canAttachPrivateListeners) {
      setGearItems([]);
      return;
    }

    const unsub = subscribeToGear(firebaseUser.uid, setGearItems, (err) =>
      console.error("[LISTENER] gear", err),
    );

    return () => unsub();
  }, [canAttachPrivateListeners]);

  // Theme persistence + deterministic root theme application
  useEffect(() => {
    if (typeof document === "undefined") return;

    const themeMode = isDarkMode ? "dark" : "light";
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle("dark", isDarkMode);
    root.dataset.theme = themeMode;
    root.style.colorScheme = themeMode;

    if (body) {
      body.dataset.theme = themeMode;
      body.style.colorScheme = themeMode;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem("theme", themeMode);
    }
  }, [isDarkMode]);

  // ---------------------------------------------------------------------------
  // Weather (REAL + AI insight, coordinate-safe + freshness-safe)
  // Cache is keyed by coordinate + unit so Pomona can never display cached
  // weather from Los Angeles, the coast, or a previous map target.
  const lastWeatherCoordsRef = useRef<LatLon | null>(null);
  const lastWeatherFetchRef = useRef(0);
  const lastInsightFetchRef = useRef(0);
  const lastGeoNameFetchRef = useRef(0);

  useEffect(() => {
    const rawCoords = coords || DEFAULT_COORDS_LA;
    const effectiveCoords: LatLon = {
      lat: Number.isFinite(Number(rawCoords?.lat))
        ? Number(rawCoords.lat)
        : DEFAULT_COORDS_LA.lat,
      lon: Number.isFinite(Number(rawCoords?.lon))
        ? Number(rawCoords.lon)
        : DEFAULT_COORDS_LA.lon,
    };

    const now = Date.now();
    const MIN_DISTANCE_M = 250;
    const CACHE_MAX_AGE_MS = 60 * 60_000;
    const cacheKey =
      `roddo.weather.v3.${tempUnit}.` +
      `${effectiveCoords.lat.toFixed(3)}.${effectiveCoords.lon.toFixed(3)}`;

    // A new searched area gets one weather fetch. Searching the same area again
    // within one hour reuses its coordinate-specific cache instead of pinging
    // the provider again.
    if (lastWeatherCoordsRef.current) {
      const dist = distanceMeters(
        lastWeatherCoordsRef.current,
        effectiveCoords,
      );
      if (
        dist < MIN_DISTANCE_M &&
        now - lastWeatherFetchRef.current < CACHE_MAX_AGE_MS
      ) {
        return;
      }
    }

    lastWeatherCoordsRef.current = effectiveCoords;

    let cancelled = false;
    let cachedWeather: any = null;

    const run = async () => {
      setIsLoadingWeather(true);

      try {
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            const cacheAge = now - Number(parsed?.savedAt || 0);
            const cachedCoords = parsed?.data?.coords;
            const sameCoords =
              Math.abs(Number(cachedCoords?.lat) - effectiveCoords.lat) < 0.02 &&
              Math.abs(Number(cachedCoords?.lon) - effectiveCoords.lon) < 0.02;

            if (
              parsed?.data &&
              sameCoords &&
              cacheAge >= 0 &&
              cacheAge <= CACHE_MAX_AGE_MS
            ) {
              cachedWeather = parsed.data;
              lastWeatherFetchRef.current = Number(parsed.savedAt || now);
              if (!cancelled) setWeatherData(parsed.data);

              // Fresh cached weather for this searched area is enough.
              // Do not make another provider request until the hour expires.
              return;
            }
          }
        } catch (cacheErr) {
          console.warn("[weather] cache read failed", cacheErr);
        }

        console.debug("[weather] fetching for coords", effectiveCoords);

        const real = await fetchRealWeather(effectiveCoords, tempUnit);
        if (cancelled) return;

        lastWeatherFetchRef.current = Date.now();
        setWeatherData(real as any);

        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              savedAt: Date.now(),
              data: real,
            }),
          );

          // Remove the old global cache that could mix locations.
          localStorage.removeItem(`roddo.weather.${tempUnit}`);
        } catch (cacheErr) {
          console.warn("[weather] cache write failed", cacheErr);
        }

      } catch (err) {
        console.warn("[weather] soft fail", err);

        if (!cancelled) {
          if (!cachedWeather) setWeatherData(null);
          if (!locationName) setLocationName("Current water");
        }
      } finally {
        if (!cancelled) setIsLoadingWeather(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [coords, tempUnit]);

  // ---------------------------------------------------------------------------
  // Location label / reverse geocode
  // Keep this separate from weather so:
  // - boot weather may load before Firebase Auth is ready without causing a 401
  // - cached weather never skips location-name hydration
  // - location labels retry automatically once the authenticated JS session exists
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const rawCoords = coords || DEFAULT_COORDS_LA;
    const effectiveCoords: LatLon = {
      lat: Number.isFinite(Number(rawCoords?.lat))
        ? Number(rawCoords.lat)
        : DEFAULT_COORDS_LA.lat,
      lon: Number.isFinite(Number(rawCoords?.lon))
        ? Number(rawCoords.lon)
        : DEFAULT_COORDS_LA.lon,
    };

    const callableUid = auth.currentUser?.uid ?? null;
    const expectedUid = firebaseUser?.uid ?? null;

    // Cloud callable auth is supplied by Firebase JS Auth. On native, wait until
    // the bridge has made the JS uid match the native/current Roddo identity.
    if (!isAuthReady || !expectedUid || !callableUid) {
      return;
    }
    if (IS_NATIVE && callableUid !== expectedUid) {
      return;
    }

    let cancelled = false;
    const coordKey = `${effectiveCoords.lat.toFixed(3)}:${effectiveCoords.lon.toFixed(3)}`;
    const runAt = Date.now();

    void reverseGeocode(effectiveCoords)
      .then((name) => {
        if (cancelled) return;
        setLocationName(name || "Current water");
        lastGeoNameFetchRef.current = runAt;

        if (import.meta.env.DEV) {
          console.debug("[geocode] location label ready", {
            coordKey,
            uid: callableUid,
            label: name || "Current water",
          });
        }
      })
      .catch((geoErr) => {
        console.warn("[geocode] reverse geocode soft fail", geoErr);
        if (!cancelled) setLocationName((prev) => prev || "Current water");
      });

    return () => {
      cancelled = true;
    };
  }, [
    coords,
    isAuthReady,
    firebaseUser?.uid,
    jsAuthUid,
    IS_NATIVE,
  ]);

  const [authDiagOpen, setAuthDiagOpen] = useState(false);
  const [authDiagBusy, setAuthDiagBusy] = useState(false);
  const [authDiagData, setAuthDiagData] = useState<any>(null);

  const runAuthDiag = useCallback(async () => {
    if (!IS_NATIVE) return;

    setAuthDiagBusy(true);
    try {
      const out: any = {
        at: new Date().toISOString(),
        diag: getAuthBridgeDiag(),
        js: {
          uid: jsAuthUid ?? null,
          hasUser: !!auth.currentUser,
        },
        native: null,
        nativeIdToken: { ok: false, keys: [] as string[] },
        callable: {
          ok: false,
          code: null as any,
          message: null as any,
          hasCustomToken: false,
        },
      };

      // native user
      let nativeUid: string | null = null;
      try {
        const native = await FirebaseAuthentication.getCurrentUser();
        nativeUid = native?.user?.uid ?? null;
        out.native = {
          uid: nativeUid,
          email: native?.user?.email ?? null,
        };
      } catch (e: any) {
        out.native = { error: e?.message ?? String(e) };
      }

      // native idToken (force refresh)
      let idToken: string | null = null;
      try {
        const tokRes: any = await FirebaseAuthentication.getIdToken({
          forceRefresh: true,
        } as any);
        out.nativeIdToken.keys = Object.keys(tokRes || {});
        idToken =
          tokRes?.token ?? tokRes?.idToken ?? tokRes?.accessToken ?? null;
        out.nativeIdToken.ok = !!idToken;
      } catch (e: any) {
        out.nativeIdToken = {
          ok: false,
          error: e?.message ?? String(e),
          keys: [],
        };
      }

      if (idToken) {
        const diagId = `diag_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        out.callable.diagId = diagId;

        try {
          const mint = httpsCallable(functions, "authBridgeCustomToken");

          console.debug("[auth-bridge][diag] calling authBridgeCustomToken", {
            diagId,
            projectId: (auth as any)?.app?.options?.projectId,
            appId: (auth as any)?.app?.options?.appId,
            region: "us-central1",
            nativeUid,
          });

          const resp: any = await withTimeout(
            mint({ idToken, diagId }),
            8000,
            "callable_authBridge_diag",
          );

          const customToken =
            resp?.data?.customToken ??
            resp?.result?.customToken ??
            resp?.customToken ??
            null;

          out.callable.ok = true;
          out.callable.hasCustomToken =
            typeof customToken === "string" && customToken.length > 20;

          console.debug("[auth-bridge][diag] callable resolved", {
            diagId,
            hasCustomToken: out.callable.hasCustomToken,
            respKeys: Object.keys(resp || {}),
            dataKeys: Object.keys(resp?.data || {}),
          });
        } catch (e: any) {
          out.callable.ok = false;
          out.callable.code = e?.code ?? null;
          out.callable.message = e?.message ?? String(e);

          console.warn("[auth-bridge][diag] callable failed", {
            diagId,
            code: out.callable.code,
            message: out.callable.message,
          });
        }
      }

      setAuthDiagData(out);
    } finally {
      setAuthDiagBusy(false);
    }
  }, [IS_NATIVE, functions]);

  // ---------------------------------------------------------------------------
  // Notifications helpers
  // ---------------------------------------------------------------------------
  const pushNotification = useCallback(
    (
      partial: Omit<Notification, "id" | "read" | "timestamp"> & {
        id?: string;
      },
    ) => {
      if (!firebaseUser) return;

      const id =
        partial.id ??
        `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      const resolvedLink = buildNotificationLink({
        link: (partial as any).link,
        view: (partial as any).view,
        targetId: (partial as any).targetId,
        subTargetId: (partial as any).subTargetId,
        entity: (partial as any).entity,
        type: partial.type as any,
      });

      createNotification({
        id,
        userId: firebaseUser.uid,
        type: partial.type as any,
        title: (partial as any).title,
        body: (partial as any).body,
        preview: (partial as any).preview,
        message: partial.message,
        actor: (partial as any).actor,
        actorName: (partial as any).actorName,
        entity: (partial as any).entity,
        link: resolvedLink,
        timestamp: Date.now(),
        read: false,
      }).catch((err) =>
        console.error("Failed to create notification doc:", err),
      );
    },
    [firebaseUser],
  );

  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    if (!firebaseUser) return;
    markAllNotificationsReadForUser(firebaseUser.uid).catch((err) =>
      console.error("Failed to mark all notifications read:", err),
    );
  }, [firebaseUser]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    if (!firebaseUser) return;
    clearAllNotificationsForUser(firebaseUser.uid).catch((err) =>
      console.error("Failed to clear notifications:", err),
    );
  }, [firebaseUser]);

  const notificationActor = useMemo(
    () => ({
      uid: firebaseUser?.uid || "",
      name:
        (userProfile as any)?.displayName ||
        (userProfile as any)?.username ||
        userProfile?.name ||
        (firebaseUser as any)?.displayName ||
        "Angler",
      avatar: userProfile?.avatar || "",
    }),
    [firebaseUser, userProfile],
  );

  // ---------------------------------------------------------------------------
  // Notification navigation (ONE place that knows how to open links)
  // ---------------------------------------------------------------------------
  const openNotification = useCallback(
    (n: any) => {
      if (!n) return;

      // mark read optimistic
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
      );

      if (firebaseUser?.uid) {
        markNotificationRead(firebaseUser.uid, String(n.id)).catch(() => {});
      }

      // delegate routing to notification router
      routeNotification(n, (view, params) => {
        navigateToRoot(view, params || {});
      });
    },
    [firebaseUser?.uid, navigateToRoot],
  );

  // ---------------------------------------------------------------------------
  // Marketplace checkout + escrow
  // ---------------------------------------------------------------------------
  const handlePurchaseMarketplaceItem = useCallback(
    async (item: MarketplaceItem) => {
      if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
        addToast(
          "Roddo Checkout is not enabled yet. Arrange payment directly with the seller.",
          "info",
        );
        navigateToRoot(View.Bazaar);
        return;
      }

      if (!firebaseUser) {
        addToast("You must be logged in to purchase an item.", "error");
        return;
      }

      const rawId = (item as any)?.id ?? (item as any)?.itemId;
      const itemIdStr = String(rawId ?? "").trim();

      if (!itemIdStr) {
        console.error("[checkout] invalid itemId", { rawId, item });
        addToast("This listing has an invalid item id.", "error");
        return;
      }

      try {
        const result = await redirectToMarketplaceCheckout({
          itemId: itemIdStr,
          returnUrlBase: getStripeReturnUrlBase(),
        });

        if (result?.url) {
          window.location.href = result.url;
          return;
        }
        console.error("[checkout] missing url in response", result);
        addToast("Failed to start checkout.", "error");
      } catch (err: any) {
        const code = err?.code || err?.details?.code;
        const message = err?.message || err?.details?.message || String(err);

        console.error("Error starting marketplace checkout:", {
          code,
          message,
          err,
        });

        if (code === "failed-precondition")
          addToast(message || "Item cannot be purchased right now.", "error");
        else if (code === "unauthenticated")
          addToast("Please sign in again and retry.", "error");
        else addToast("Unable to start checkout.", "error");
      }
    },
    [firebaseUser, addToast, navigateToRoot],
  );

  const handleReleaseEscrow = useCallback(
    async ({ transactionId }: { transactionId: string; itemId: string }) => {
      if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
        addToast(
          "Bazaar escrow tools are closed while we finish launch review.",
          "info",
        );
        return;
      }

      try {
        await releaseMarketplaceEscrow(transactionId);
        addToast(
          t(
            "marketplace.escrowReleased",
            "Escrow funds released for this item.",
          ),
          "success",
        );
      } catch (err: any) {
        console.error("Failed to release escrow:", err);
        addToast(
          t(
            "marketplace.escrowReleaseFailed",
            "Failed to release escrow. Please try again.",
          ),
          "error",
        );
      }
    },
    [addToast, t],
  );

  const handleRequestPayout = useCallback(async () => {
    if (!userProfile) return;
    if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
      addToast("Bazaar payouts are not enabled yet.", "info");
      return;
    }
    try {
      const result: any = await requestSellerPayout();
      const totalCents = Number(result?.totalAmountCents ?? 0);
      const amount = Number.isFinite(totalCents)
        ? (totalCents / 100).toFixed(2)
        : "0.00";
      const cur = String(result?.currency || "usd").toUpperCase();
      addToast(
        `Payout request created for $${amount} ${cur}. It will be processed shortly.`,
        "success",
      );
    } catch (err: any) {
      console.error("Error requesting payout:", err);
      addToast(
        err?.message || "Unable to request payout. Please try again later.",
        "error",
      );
    }
  }, [userProfile, toastProgressionOnce]);

  const handleCheckoutResult = useCallback(
    (
      status: "success" | "cancel",
      itemIdParam?: string | null,
      stripeSessionId?: string | null,
    ) => {
      if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
        navigateToRoot(View.Bazaar);
        return;
      }

      const itemId = String(itemIdParam || "").trim() || null;

      if (status === "success") {
        // Receipt resolves only from server-authored transaction/session state.
        navigateToRoot(View.Receipt, {
          txId: null,
          stripeSessionId: stripeSessionId ?? null,
          itemId,
          returnTo: View.Bazaar,
        });
        return;
      }

      navigateToRoot(View.CheckoutCancel, { itemId });
    },
    [navigateToRoot],
  );

  // ---------------------------------------------------------------------------
  // Community handlers
  // ---------------------------------------------------------------------------
  const communityCreateInFlightRef = useRef<Set<string>>(new Set());
  const communityCommentInFlightRef = useRef<Set<string>>(new Set());

  const handleCreateCommunityPost = useCallback(
    async (post: CommunityPost) => {
      const startedAt = Date.now();
      const postId = String((post as any)?.id || '').trim();
      if (!postId) throw new Error('Missing community post id');
      if (communityCreateInFlightRef.current.has(postId)) return;
      communityCreateInFlightRef.current.add(postId);

      try {
        // ✅ On native, block until JS auth is truly signed in (custom token done)
        if (IS_NATIVE && !auth.currentUser?.uid) {
          addToast("Finishing sign-in… try again in a moment.", "info");
          throw new Error("JS auth not ready (native bridge pending)");
        }

        await withTimeout(
          createCommunityPost({
            ...(post as any),
            visibility: (post as any).visibility ?? "public",
            status: (post as any).status ?? "visible",
          } as any),
          FIRESTORE_TIMEOUT_MS,
          "community:createCommunityPost",
        );

        console.debug("[community] create OK", {
          ms: Date.now() - startedAt,
          jsUid: jsAuthUid ?? null,
          nativeUid: firebaseUser?.uid ?? null,
        });

        // ✅ If listener is stalled, force-fetch so user sees the post
        refreshCommunityFeed().catch(() => {});
        return;
      } catch (err: any) {
        console.error(
          "[community] create FAIL",
          { code: err?.code, message: err?.message },
          err,
        );
        addToast(
          `Could not create post: ${err?.code || err?.message || "unknown"}`,
          "error",
        );
        throw err; // ✅ critical: let CommunityView react
      } finally {
        communityCreateInFlightRef.current.delete(postId);
      }
    },
    [
      IS_NATIVE,
      addToast,
      firebaseUser?.uid,
      refreshCommunityFeed,
      FIRESTORE_TIMEOUT_MS,
    ],
  );

  const handlePostEdited = useCallback(
    async (postId: string, updatedData: Partial<CommunityPost>) => {
      try {
        await updateCommunityPost(postId, updatedData);
      } catch (err) {
        console.error("Failed to update community post:", err);
        addToast("Could not update post. Please try again.", "error");
      }
    },
    [addToast],
  );

  const handlePostDeleted = useCallback(async (postId: string) => {
    try {
      await deleteCommunityPost(postId);
    } catch (err) {
      console.error("[App] Failed to delete post", err);
    }
  }, []);

  const handleCommentAdded = useCallback(
    async (postId: string, comment: Comment) => {
      if (!firebaseUser || !userProfile) {
        addToast("You must be signed in to comment.", "error");
        return;
      }

      const enriched: Comment = {
        ...comment,
        authorId: firebaseUser.uid as any,
        authorName:
          userProfile.name || (firebaseUser as any).displayName || "Angler",
        authorAvatar: userProfile.avatar || "",
        timestamp: (comment as any).timestamp ?? Date.now(),

        // ✅ REQUIRED for the collectionGroup listener
        postId,
        createdAt:
          (comment as any).createdAt ??
          (comment as any).timestamp ??
          Date.now(),
      };

      const commentKey = `${postId}::${String((enriched as any)?.id || '').trim()}`;
      if (communityCommentInFlightRef.current.has(commentKey)) return;
      communityCommentInFlightRef.current.add(commentKey);

      try {
        const targetPost = communityPosts[postId];
        await addCommentToPost(postId, enriched);

        setCommunityPosts((prev) => {
          const existing = prev[postId];
          if (!existing) return prev;
          return {
            ...prev,
            [postId]: {
              ...existing,
              comments: [...(existing.comments || []), enriched],
            },
          };
        });

        // Community comment notifications are emitted by functions/src/notifications.ts
        // from the Firestore comment-created trigger. Keep App.tsx out of cross-user
        // notification writes so users do not receive duplicate notices.
      } catch (err) {
        console.error("Failed to add comment:", err);
        addToast("Could not add comment. Please try again.", "error");
      } finally {
        communityCommentInFlightRef.current.delete(commentKey);
      }
    },
    [
      firebaseUser,
      userProfile,
      addToast,
      communityPosts,
    ],
  );

  const handleCommentEdited = useCallback(
    async (postId: string, commentId: string, newText: string) => {
      try {
        await updateCommentOnPost(postId, commentId, newText);

        setCommunityPosts((prev) => {
          const existing = prev[postId];
          if (!existing) return prev;

          const updatedComments = (existing.comments || []).map((c) =>
            c.id === commentId ? { ...c, text: newText } : c,
          );

          return {
            ...prev,
            [postId]: { ...existing, comments: updatedComments },
          };
        });
      } catch (err) {
        console.error("Failed to edit comment:", err);
        addToast("Could not edit comment. Please try again.", "error");
      }
    },
    [addToast],
  );

  const handleCommentDeleted = useCallback(
    async (postId: string, commentId: string) => {
      try {
        await deleteCommentFromPost(postId, commentId);

        setCommunityPosts((prev) => {
          const existing = prev[postId];
          if (!existing) return prev;

          const updatedComments = (existing.comments || []).filter(
            (c) => c.id !== commentId,
          );
          return {
            ...prev,
            [postId]: { ...existing, comments: updatedComments },
          };
        });
      } catch (err) {
        console.error("Failed to delete comment:", err);
        addToast("Could not delete comment. Please try again.", "error");
      }
    },
    [addToast],
  );

  const handleToggleLike = useCallback(
    async (postId: string) => {
      if (!firebaseUser) return;

      const targetPost = communityPosts[postId];
      const wasLiked = Array.isArray((targetPost as any)?.likes)
        ? (targetPost as any).likes.includes(firebaseUser.uid)
        : false;

      try {
        await togglePostLike(postId, firebaseUser.uid);

        // Post-like notifications are emitted by functions/src/notifications.ts
        // from the Firestore like-created trigger.
      } catch (err) {
        console.error("Failed to toggle post like:", err);
        addToast("Could not update like. Please try again.", "error");
      }
    },
    [
      firebaseUser,
      addToast,
      communityPosts,
    ],
  );

  const handleToggleCommentLike = useCallback(
    async (postId: string, commentId: string) => {
      if (!firebaseUser) return;

      const commentsForPost =
        commentsByPost[postId] || communityPosts[postId]?.comments || [];
      const targetComment = commentsForPost.find(
        (c: any) => c?.id === commentId,
      );
      const wasLiked = Array.isArray((targetComment as any)?.likedBy)
        ? (targetComment as any).likedBy.includes(firebaseUser.uid)
        : false;

      try {
        await toggleCommentLike(postId, commentId, firebaseUser.uid);

        // Comment-like notifications are emitted by functions/src/notifications.ts
        // from the Firestore comment-like-created trigger.
      } catch (err) {
        console.error("Failed to toggle comment like:", err);
        addToast("Could not like this comment. Please try again.", "error");
      }
    },
    [
      firebaseUser,
      addToast,
      commentsByPost,
      communityPosts,
    ],
  );

  const handleCommunityCommentsOpened = useCallback((postId: string) => {
    const id = String(postId || "").trim();
    if (!id) return;
  
    // Keep the actively opened historical thread hot.
    // Recent posts are already filled in separately by subscribedCommentPostIds.
    setCommunityCommentPostIds({ [id]: true });
  }, []);

  const loadMoreCommunityPosts = useCallback(async () => {
    if (!canAttachPublicListeners) return;
    if (!communityBootstrapped) return;
    if (communityLoadingMore) return;
    if (!communityHasMore) return;

    const cursor = communityCursorRef.current;
    if (!cursor) {
      setCommunityHasMore(false);
      return;
    }

    setCommunityLoadingMore(true);
    try {
      const q = query(
        collection(db, "communityPosts"),
        where("visibility", "==", "public"),
        orderBy("timestamp", "desc"),
        startAfter(cursor),
        limit(COMMUNITY_PAGE_SIZE),
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setCommunityHasMore(false);
        return;
      }

      communityCursorRef.current = snap.docs[snap.docs.length - 1] ?? cursor;
      if (snap.size < COMMUNITY_PAGE_SIZE) setCommunityHasMore(false);

      setCommunityPosts((prev) => {
        const next = { ...prev };
      
        snap.forEach((docSnap) => {
          const data = docSnap.data() as any;
          const embeddedId = String(data?.id || "").trim();
          const realId = String(docSnap.id || "").trim();
      
          next[realId] = {
            ...data,
      
            // Legacy posts may contain an embedded id that does not match
            // the actual Firestore document id. Subcollection writes MUST
            // always use the real Firestore document id.
            legacyId:
              embeddedId && embeddedId !== realId
                ? embeddedId
                : data?.legacyId,
      
            docId: realId,
            id: realId,
      
            comments: prev[realId]?.comments ?? [],
          };
        });
      
        return next;
      });
    } catch (e) {
      console.error("[community] loadMore failed", e);
      addToast(`Load more failed: ${(e as any)?.code || "unknown"}`, "error");
    } finally {
      setCommunityLoadingMore(false);
    }
  }, [
    canAttachPublicListeners,
    communityBootstrapped,
    communityLoadingMore,
    communityHasMore,
    addToast,
  ]);

  const resetCommunityFeed = useCallback(() => {
    setCommunityPosts({});
    setCommunityHasMore(true);
    setCommunityBootstrapped(false);
    communityCursorRef.current = null;
  }, []);
  const handleAddToCatchDiary = useCallback(
    async (post: CommunityPost) => {
      if (!userProfile) return;
      try {
        await addDiaryEntryFromPost(userProfile.uid, post);
        addToast("Catch added to your diary.", "success");
      } catch (err) {
        console.error("Failed to add catch to diary:", err);
        addToast(
          "Could not add catch to your diary. Please try again.",
          "error",
        );
      }
    },
    [userProfile, addToast],
  );

  // ---------------------------------------------------------------------------
  // Payment success handler (non-stripe in-app)
  // ---------------------------------------------------------------------------
  const handlePaymentSuccess = useCallback(async () => {
    if (!paymentItem) return;

    try {
      if (paymentItem.type === "license") {
        const license = paymentItem.item as LicenseInfo;
        setUserLicenses((prev) =>
          prev.map((l) =>
            l.id === license.id ? ({ ...(l as any), active: true } as any) : l,
          ),
        );
      } else {
        // Marketplace payment completion is server-authoritative. Never mark a
        // Bazaar listing sold from the simulated/in-app PaymentGateway callback.
        addToast(
          FEATURE.BAZAAR_PAYMENTS_ENABLED
            ? "Waiting for Roddo Checkout confirmation."
            : "Roddo Checkout is not enabled yet.",
          "info",
        );
      }
    } catch (err) {
      console.error("Error finalizing payment:", err);
      addToast(
        t("app.paymentFinalizeError", "Payment completed but sync failed."),
        "error",
      );
    } finally {
      setPaymentItem(null);
    }
  }, [paymentItem, addToast, t]);

  const performLogout = useCallback(async () => {
    try {
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        try {
          await FirebaseAuthentication.signOut();
        } catch (e) {
          console.warn("[logout] native signOut failed", e);
        }
      }

      try {
        await signOut(auth);
      } catch (e) {
        console.warn("[logout] JS signOut failed", e);
      }

      accountSyncAutoAttemptedForUidRef.current = null;
      accountSyncRetryInFlightRef.current = false;
      accessBootstrappedRef.current = null;

      setUserProfile(null);
      setFirebaseUser(null);
      setJsAuthUid(null);
      setIsBanned(false);
      setIsProfileHydrated(false);
      setAccessGate(null);
      setAccessGateChecked(false);
      setAccessGateError(null);
      setJsBridgeError(null);
      setIsJsBridgeReady(!IS_NATIVE);
      setIsAccountSyncRetrying(false);
      setAccountSyncDebug("idle");
      setViewStack([{ view: View.Map }]);
    } catch (err) {
      console.error("Error signing out:", err);
      addToast("Unable to sign out. Please try again.", "error");
    }
  }, [addToast, IS_NATIVE]);

  const isGuestAccount =
    (userProfile as any)?.accountType === "guest" ||
    ((userProfile as any)?.accountType !== "registered" &&
      (firebaseUser as any)?.isAnonymous === true);

  const handleLogout = useCallback(async () => {
    if (isGuestAccount) {
      setGuestLogoutDialogOpen(true);
      return;
    }

    await performLogout();
  }, [isGuestAccount, performLogout]);

  // ---------------------------------------------------------------------------
  // First-run welcome + guided app tour
  // ---------------------------------------------------------------------------
  const handleFirstRunStart = useCallback(
    async (displayName: string) => {
      if (!userProfile?.uid || isFirstRunSaving) return;

      const cleanName = String(displayName || userProfile.name || 'Angler').trim() || 'Angler';
      const patch = guidedTourReplayMode
        ? ({
            name: cleanName,
            displayName: cleanName,
            updatedAt: Date.now(),
          } as any)
        : ({
            name: cleanName,
            displayName: cleanName,
            onboardingCompleted: true,
            onboardingExperienceVersion: RODDO_GUIDED_TOUR_VERSION,
            updatedAt: Date.now(),
          } as any);

      setIsFirstRunSaving(true);
      try {
        try {
          localStorage.removeItem('roddo_pending_display_name');
        } catch {}

        await updateUserProfile(userProfile.uid, patch);
        setUserProfile((prev) => (prev ? ({ ...prev, ...patch } as any) : prev));
        setShowWelcome(false);
        setGuidedTourSessionId((value) => value + 1);
        setGuidedTourActive(true);
        trackRoddoEvent('tour_started', { source: 'first_run', replay: false });
        navigateToRoot(View.BigBlueGlobe, { launchMode: 'firstOpen', source: 'guided-tour' });
      } catch (error) {
        console.error('[onboarding] first-run save failed', error);
        addToast('Could not start Roddo yet. Please try again.', 'error');
      } finally {
        setIsFirstRunSaving(false);
      }
    },
    [userProfile, isFirstRunSaving, guidedTourReplayMode, navigateToRoot, addToast],
  );

  const replayGuidedTour = useCallback(() => {
    // Legal Settings replay is a true session override. Do not reopen the
    // first-run welcome card and do not touch persisted completion flags.
    // Start a fresh tour controller directly over Big Blue instead.
    setDailyRewardModalOpen(false);
    setShowWelcome(false);
    setGuidedTourReplayMode(true);
    setGuidedTourSessionId((value) => value + 1);
    setGuidedTourActive(true);
    trackRoddoEvent('tour_started', { source: 'legal_settings', replay: true });
    navigateToRoot(View.BigBlueGlobe, {
      launchMode: 'firstOpen',
      source: 'guided-tour-replay',
    });
  }, [navigateToRoot]);

  const finishGuidedTour = useCallback(
    async (skipped = false) => {
      if (!userProfile?.uid) return;

      // Manual replays are session-only. The user already completed onboarding,
      // so finishing or skipping a replay should simply close it cleanly.
      if (guidedTourReplayMode) {
        trackRoddoEvent(skipped ? 'tour_skipped' : 'tour_completed', { source: 'legal_settings', replay: true, skipped });
        setShowWelcome(false);
        setGuidedTourActive(false);
        setGuidedTourReplayMode(false);
        navigateToRoot(View.Map);
        addToast(
          skipped
            ? 'Tour closed. You can replay it anytime from Legal Settings.'
            : 'Tour complete. Back to Big Blue.',
          'success',
        );
        return;
      }

      trackRoddoEvent(skipped ? 'tour_skipped' : 'tour_completed', { source: 'first_run', replay: false, skipped });

      const patch = {
        onboardingCompleted: true,
        onboardingExperienceVersion: RODDO_GUIDED_TOUR_VERSION,
        welcomeTourCompletedVersion: RODDO_GUIDED_TOUR_VERSION,
        guidedTourCompletedAt: Date.now(),
        updatedAt: Date.now(),
      } as any;

      setGuidedTourActive(false);
      setUserProfile((prev) => (prev ? ({ ...prev, ...patch } as any) : prev));
      navigateToRoot(View.Map);

      try {
        await updateUserProfile(userProfile.uid, patch);
      } catch (error) {
        console.warn('[onboarding] guided tour completion sync failed', error);
      }

      addToast(skipped ? 'Tour skipped. You can explore Roddo anytime.' : 'You’re ready. Welcome to Roddo.', 'success');
    },
    [userProfile?.uid, guidedTourReplayMode, navigateToRoot, addToast],
  );

  // ---------------------------------------------------------------------------
  // Legal gate acceptance (REAL schema + optimistic local patch)
  // ---------------------------------------------------------------------------
  const [isSavingLegal, setIsSavingLegal] = useState(false);

  // keep a simple local alias so JSX uses a defined name
  const needsLegal = needsLegalNow;

  const analyticsAppOpenRef = useRef(false);
  const analyticsAuthUidRef = useRef<string | null>(null);
  const analyticsLegalShownRef = useRef(false);
  const analyticsWelcomeShownRef = useRef(false);
  const analyticsBigBlueShownRef = useRef(false);

  useEffect(() => {
    if (analyticsAppOpenRef.current) return;
    analyticsAppOpenRef.current = true;
    trackRoddoEvent('app_open');
  }, []);

  useEffect(() => {
    // AdMob is feature-gated. With ADS_ENABLED=false this resolves disabled
    // without importing the native plugin. When enabled, run once per app
    // session after Roddo's required legal gate/welcome flow is out of the way.
    if (!isProfileHydrated || needsLegal || showWelcome) return;
    void initializeRoddoAdMob();
  }, [isProfileHydrated, needsLegal, showWelcome]);

  useEffect(() => {
    const uid = String(firebaseUser?.uid || userProfile?.uid || '').trim();
    if (!uid || !isProfileHydrated || analyticsAuthUidRef.current === uid) return;
    analyticsAuthUidRef.current = uid;
    trackRoddoEvent('auth_ready', { account_type: isGuestAccount ? 'guest' : 'registered' });
  }, [firebaseUser?.uid, userProfile?.uid, isProfileHydrated, isGuestAccount]);

  useEffect(() => {
    if (!needsLegal) { analyticsLegalShownRef.current = false; return; }
    if (analyticsLegalShownRef.current) return;
    analyticsLegalShownRef.current = true;
    trackRoddoEvent('legal_gate_shown', { account_type: isGuestAccount ? 'guest' : 'registered' });
  }, [needsLegal, isGuestAccount]);

  useEffect(() => {
    if (!showWelcome || needsLegal) { if (!showWelcome) analyticsWelcomeShownRef.current = false; return; }
    if (analyticsWelcomeShownRef.current) return;
    analyticsWelcomeShownRef.current = true;
    trackRoddoEvent('welcome_shown', { account_type: isGuestAccount ? 'guest' : 'registered' });
  }, [showWelcome, needsLegal, isGuestAccount]);

  useEffect(() => {
    if (activeView !== View.Map || needsLegal || showWelcome) return;
    if (analyticsBigBlueShownRef.current) return;
    analyticsBigBlueShownRef.current = true;
    trackRoddoEvent('big_blue_opened', { source: 'app', account_type: isGuestAccount ? 'guest' : 'registered' });
  }, [activeView, needsLegal, showWelcome, isGuestAccount]);

  const handleAcceptLegal = useCallback(
    async (opts: {
      fishiqContributionEnabled: boolean;
      ageBracket: RoddoAgeBracket;
      parentalConsentReceived: boolean;
    }) => {
      if (!userProfile?.uid) return;

      setIsSavingLegal(true);

      // ✅ Build the exact schema that hasAcceptedLegalV() checks
      const legalPatch = buildLegalAcceptancePatch({
        fishiqContributionEnabled: !!opts.fishiqContributionEnabled,
        ageBracket: opts.ageBracket,
        parentalConsentReceived: !!opts.parentalConsentReceived,
      });

      try {
        // Legal acceptance must be durable before the gate disappears. That makes
        // the same acceptance available on every device tied to this Firebase UID.
        await withTimeout(
          updateUserProfile(userProfile.uid, legalPatch as any),
          FIRESTORE_TIMEOUT_MS,
          "legal_save_timeout",
        );

        setUserProfile((prev) =>
          prev ? ({ ...(prev as any), ...(legalPatch as any) } as any) : prev,
        );
        setLegalAcceptedOverrideUid(userProfile.uid);

        try {
          localStorage.removeItem("roddo_legal_accept_fallback_v1");
          localStorage.setItem(RODDO_AGE_GATE_PASSED_KEY, "1");
          localStorage.setItem(RODDO_AGE_BRACKET_KEY, opts.ageBracket);
          localStorage.removeItem(RODDO_AGE_GATE_BLOCKED_KEY);
        } catch {}

        trackRoddoEvent('legal_accepted', { account_type: isGuestAccount ? 'guest' : 'registered', sync_status: 'cloud' });
        addToast("Consent saved ✅", "success");
      } catch (e: any) {
        console.warn("[legal] cloud save failed; keeping legal gate open", e);

        // Keep a retry payload on this device, but do not treat it as accepted
        // until Firestore confirms the account-level write.
        try {
          localStorage.setItem(
            "roddo_legal_accept_fallback_v1",
            JSON.stringify({ uid: userProfile.uid, patch: legalPatch, at: Date.now() }),
          );
        } catch {}

        trackRoddoEvent('legal_accepted', { account_type: isGuestAccount ? 'guest' : 'registered', sync_status: 'cloud_failed' });
        addToast(
          e?.message === "legal_save_timeout"
            ? "Could not sync your agreement yet. Check your connection and try again."
            : "Could not save your agreement. Please try again.",
          "error",
        );
      } finally {
        setIsSavingLegal(false);
      }
    },
    [userProfile, addToast],
  );

  // ---------------------------------------------------------------------------
  // Unified catch history
  // ---------------------------------------------------------------------------
  const unifiedCatchEntries = useMemo(
    () =>
      buildCatchTimelineEntries({
        legacyLogEntries: catchLog || [],
        diaryEntries: catchDiaryEntries,
      }),
    [catchLog, catchDiaryEntries],
  );

  const mapCatchEntries = useMemo(() => {
    const merged = [
      ...(catchLog || []),
      ...((catchDiaryEntries as any[]) || []),
    ];
    const seen = new Set<string>();

    return merged.filter((entry: any, index: number) => {
      const key = String(entry?.id || `catch_${index}`).trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);

      const lat = Number(entry?.lat ?? entry?.coords?.lat);
      const lng = Number(
        entry?.lng ?? entry?.lon ?? entry?.coords?.lng ?? entry?.coords?.lon,
      );
      return Number.isFinite(lat) && Number.isFinite(lng);
    });
  }, [catchDiaryEntries, catchLog]);

  // ---------------------------------------------------------------------------
  // Big Blue -> exact catch history bridge
  // ---------------------------------------------------------------------------
  // MapView works with raw map records, while CatchHistoryView renders the
  // normalized/deduped timeline. Resolve the raw map id to the exact timeline
  // entry before navigating so legacy/canonical copies cannot strand the user
  // at the top of History instead of opening the catch they selected.
  const handleOpenMapCatchHistory = useCallback(
    (payload?: {
      targetId?: string | null;
      catchId?: string | null;
      source?: "log" | "diary" | "all";
      species?: string | null;
    }) => {
      const requestedId = String(
        payload?.targetId ?? payload?.catchId ?? "",
      ).trim();

      const rawMapEntry = requestedId
        ? (mapCatchEntries as any[]).find((entry: any) =>
            [entry?.id, entry?.catchId, entry?.sourceId].some(
              (value) => String(value || "").trim() === requestedId,
            ),
          )
        : null;

      const candidateIds = new Set(
        [
          requestedId,
          rawMapEntry?.id,
          rawMapEntry?.catchId,
          rawMapEntry?.sourceId,
          rawMapEntry?.sourcePostId,
          rawMapEntry?.communityPostId,
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      );

      let resolved = (unifiedCatchEntries as any[]).find((entry: any) => {
        const raw = entry?.raw || {};
        return [
          entry?.id,
          raw?.id,
          raw?.catchId,
          raw?.sourceId,
          raw?.sourcePostId,
          raw?.communityPostId,
        ].some((value) => candidateIds.has(String(value || "").trim()));
      });

      // Last-resort identity match for old duplicate records whose canonical and
      // legacy documents have different ids. Timestamp + species + coordinates
      // is specific enough for a private catch selection and avoids guessing by
      // species alone.
      if (!resolved && rawMapEntry) {
        const mapTs = Number(rawMapEntry?.timestamp || 0);
        const mapSpecies = String(
          rawMapEntry?.species ?? rawMapEntry?.speciesName ?? "",
        )
          .trim()
          .toLowerCase();
        const mapLat = Number(rawMapEntry?.lat ?? rawMapEntry?.coords?.lat);
        const mapLng = Number(
          rawMapEntry?.lng ??
            rawMapEntry?.lon ??
            rawMapEntry?.coords?.lng ??
            rawMapEntry?.coords?.lon,
        );

        resolved = (unifiedCatchEntries as any[]).find((entry: any) => {
          const raw = entry?.raw || {};
          const entryTs = Number(
            raw?.timestamp ?? entry?.createdAt?.getTime?.() ?? 0,
          );
          const entrySpecies = String(
            entry?.speciesName ?? raw?.species ?? raw?.speciesName ?? "",
          )
            .trim()
            .toLowerCase();
          const entryLat = Number(raw?.lat ?? raw?.coords?.lat);
          const entryLng = Number(
            raw?.lng ?? raw?.lon ?? raw?.coords?.lng ?? raw?.coords?.lon,
          );

          const timeMatches =
            mapTs > 0 && entryTs > 0 && Math.abs(mapTs - entryTs) <= 60_000;
          const speciesMatches =
            !!mapSpecies && !!entrySpecies && mapSpecies === entrySpecies;
          const coordsMatch =
            Number.isFinite(mapLat) &&
            Number.isFinite(mapLng) &&
            Number.isFinite(entryLat) &&
            Number.isFinite(entryLng) &&
            Math.abs(mapLat - entryLat) < 0.0002 &&
            Math.abs(mapLng - entryLng) < 0.0002;

          return speciesMatches && (timeMatches || coordsMatch);
        });
      }

      const targetId = String(resolved?.id || requestedId).trim();

      navigateTo(View.CatchHistory, {
        ...(payload || {}),
        targetId: targetId || null,
        catchId: targetId || requestedId || null,
        source: resolved?.source || payload?.source || "all",
        from: View.Map,
      });
    },
    [mapCatchEntries, navigateTo, unifiedCatchEntries],
  );

  // ---------------------------------------------------------------------------
  // Public profile stats
  // ---------------------------------------------------------------------------
  // PublicProfileView subscribes to publicProfiles/{uid}, which is maintained by
  // server-side catch-inventory triggers. Do not manufacture a second
  // catch_inventory_v1 object here and do not issue a duplicate client refresh
  // write for every timeline change.
  const publicProfileCurrentUser = normalizedUserProfile as any;

  // ---------------------------------------------------------------------------
  // Safari/WebKit login loop fix (web only)
  // - Safari often loses redirect "state" due to storage partitioning.
  // - Popup-first is the most reliable fix on Mac Safari.
  // ---------------------------------------------------------------------------
  const isWebKitBrowser = (() => {
    try {
      if (IS_NATIVE) return false;
      const ua = navigator.userAgent || "";
      return /AppleWebKit/i.test(ua) && !/Chrome|CriOS|Edg|OPR/i.test(ua);
    } catch {
      return false;
    }
  })();

  const SafariAuthGate: React.FC = () => {
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const doLogin = async () => {
      setErr(null);
      setBusy(true);

      try {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });

        // ✅ Safari desktop: popup-first
        try {
          await signInWithPopup(auth, provider);
          return;
        } catch (e: any) {
          const code = String(e?.code || "");
          const msg = String(e?.message || "").toLowerCase();

          // Popup blocked/cancelled → fallback to redirect
          const popupBlocked =
            code.includes("popup-blocked") ||
            msg.includes("popup") ||
            msg.includes("blocked");

          if (!popupBlocked) throw e;
        }

        await signInWithRedirect(auth, provider);
      } catch (e: any) {
        console.warn("[AUTH][safari] login failed", e);
        setErr(e?.message ? String(e.message) : "Login failed");
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="min-h-[var(--roddo-vh)] w-screen flex items-center justify-center bg-[#f7f2e8] dark:bg-[#090B13] px-6">
        <div className="w-full max-w-[420px] rounded-3xl bg-white/80 dark:bg-[#151427]/95 backdrop-blur border border-black/10 dark:border-[#9F8CDC]/20 shadow-[0_16px_0_rgba(0,0,0,0.12)] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-2xl bg-black/5 dark:bg-[#9278EC]/10 flex items-center justify-center">
              <RoddoFishIcon className="h-7 w-7" />
            </div>
            <div>
              <div className="text-lg font-extrabold text-black/90 dark:text-white">
                Sign in to Roddo
              </div>
              <div className="text-xs text-black/60 dark:text-white/60">
                Safari requires a popup sign-in to avoid redirect loops.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={doLogin}
            disabled={busy}
            className="w-full rounded-2xl bg-emerald-400 px-4 py-3 text-sm font-extrabold text-emerald-950 shadow-[0_10px_0_rgba(0,0,0,0.25)] disabled:opacity-60 dark:bg-[#7657E8] dark:text-white dark:shadow-[0_10px_24px_rgba(118,87,232,0.24)]"
          >
            {busy ? "Opening Google…" : "Continue with Google"}
          </button>

          {err ? (
            <div className="mt-3 text-xs text-red-700 dark:text-red-300">
              {err}
            </div>
          ) : null}

          <div className="mt-4 text-[11px] text-black/50 dark:text-white/50">
            Tip: If you still loop, clear Safari website data for{" "}
            <b>roddo.app</b>, <b>google.com</b>, and <b>firebaseapp.com</b>.
          </div>
        </div>
      </div>
    );
  };

  const AccountSyncUnavailableView: React.FC<{
    surface?: string;
    isRetrying?: boolean;
    autoRetry?: boolean;
    onRetry?: () => void;
    onOpenMap?: () => void;
  }> = ({
    surface = "account",
    isRetrying = false,
    autoRetry = false,
    onRetry,
    onOpenMap,
  }) => {
    const prettySurface = surface || "account";
    useEffect(() => {
      if (!autoRetry || isRetrying || !onRetry) return;

      const t = window.setTimeout(() => {
        onRetry();
      }, 450);

      return () => window.clearTimeout(t);
    }, [autoRetry, isRetrying, onRetry]);

    return (
      <div className="min-h-full px-5 pb-[calc(var(--roddo-nav-h,72px)+24px)] pt-6 text-slate-900 dark:text-[#F4F1FF]">
        <div className="mx-auto flex min-h-[calc(100%-24px)] max-w-[430px] flex-col items-center justify-center text-center">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[34px] bg-indigo-300/20 blur-2xl dark:bg-[#7657E8]/20" />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-[30px] border-2 border-slate-900/10 bg-white/80 shadow-[0_14px_0_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-[#9F8CDC]/20 dark:bg-[#151427]/95 dark:shadow-[0_14px_28px_rgba(0,0,0,0.30)]">
              <RoddoFishIcon className="h-14 w-14 animate-pulse" />
            </div>
          </div>

          <div className="mt-7 rounded-full border border-slate-900/10 bg-white/70 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-indigo-700 shadow-sm dark:border-[#9F8CDC]/20 dark:bg-[#9278EC]/10 dark:text-[#DCD3FF]">
            Syncing account
          </div>

          <h1 className="mt-4 text-[30px] font-black leading-tight tracking-[-0.04em] text-slate-950 dark:text-white">
            Your {prettySurface} is safe.
          </h1>

          <p className="mt-3 max-w-[330px] text-sm font-semibold leading-6 text-slate-600 dark:text-[#B8B0CC]">
            Roddo can see your Android sign-in, but this build is still linking
            it to the web account layer. Private account data is hidden here so
            we never show a blank Angler profile or empty history by mistake.
          </p>

          <div className="mt-6 w-full rounded-[28px] border border-amber-900/10 bg-amber-50/80 p-4 text-left shadow-[0_10px_0_rgba(120,53,15,0.10)] dark:border-amber-200/10 dark:bg-amber-950/28 dark:shadow-[0_10px_0_rgba(0,0,0,0.22)]">
            <div className="text-sm font-black text-amber-950 dark:text-amber-100">
              Account sync unavailable on this build
            </div>
            <div className="mt-1 text-xs font-semibold leading-5 text-amber-900/70 dark:text-amber-100/70">
              Big Blue, GPS, and map recentering can keep working. Profile,
              Roddo Log, The Hold, licenses, and notifications unlock after JS
              auth matches native auth.
            </div>
          </div>

          <div className="mt-6 flex w-full max-w-[330px] gap-3">
            <button
              type="button"
              onClick={onOpenMap}
              className="flex-1 rounded-2xl border-2 border-slate-900 bg-slate-950 px-4 py-3 text-sm font-black text-white shadow-[0_8px_0_rgba(15,23,42,0.28)] active:translate-y-[1px] dark:border-[#9278EC] dark:bg-[#7657E8] dark:text-white"
            >
              Back to Big Blue
            </button>
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying}
              className={`rounded-2xl border-2 border-slate-900/10 bg-white px-5 py-3 text-sm font-extrabold text-slate-700 shadow-[0_6px_0_rgba(15,23,42,0.14)] transition active:translate-y-1 active:shadow-none dark:border-[#9F8CDC]/20 dark:bg-[#1B1830] dark:text-[#F4F1FF] ${
                isRetrying ? "cursor-wait opacity-60" : ""
              }`}
            >
              {isRetrying ? "Syncing…" : "Retry"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const NativeBridgeSplash: React.FC<{
    showRecovery: boolean;
    onRetry: () => void;
    onSignOut: () => void;
    protectGuest?: boolean;
    error?: string | null;
  }> = ({ showRecovery, onRetry, onSignOut, protectGuest = false, error }) => {
    return (
      <div className="relative min-h-[var(--roddo-vh)] w-screen overflow-hidden bg-[#f7f2e8] dark:bg-[#090B13]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.14),_transparent_40%),radial-gradient(circle_at_bottom,_rgba(45,212,191,0.12),_transparent_34%)] dark:bg-[radial-gradient(circle_at_top,_rgba(118,87,232,0.24),_transparent_38%),radial-gradient(circle_at_bottom,_rgba(146,120,236,0.09),_transparent_32%)]" />

        <div className="relative flex min-h-[var(--roddo-vh)] flex-col items-center justify-center px-7 text-center">
          <div className="flex h-28 w-28 items-center justify-center rounded-[30px] bg-[linear-gradient(180deg,#9278EC_0%,#7657E8_58%,#6847D1_100%)] shadow-[0_22px_60px_rgba(79,70,229,0.26),inset_0_1px_0_rgba(255,255,255,0.28)] ring-1 ring-white/25">
            <div className="flex h-20 w-20 items-center justify-center rounded-[24px] bg-white/10 backdrop-blur-sm ring-1 ring-white/20 animate-pulse">
              <RoddoFishIcon className="h-12 w-12" />
            </div>
          </div>

          <div className="mt-8 text-[31px] font-semibold tracking-[0.08em] text-black/80 dark:text-white/90">
            Loading Roddo
          </div>
          <div className="mt-3 max-w-[320px] text-base leading-7 text-black/55 dark:text-white/60">
            Syncing your map, FishIQ, and sign-in.
          </div>

          <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.42em] text-black/35 dark:text-white/35">
            <span className="h-2 w-2 rounded-full bg-[#4f46e5] animate-bounce [animation-delay:-0.24s]" />
            <span className="h-2 w-2 rounded-full bg-[#6d73ff] animate-bounce [animation-delay:-0.12s]" />
            <span className="h-2 w-2 rounded-full bg-[#8b91ff] animate-bounce" />
          </div>

          {showRecovery ? (
            <div className="mt-10 w-full max-w-[352px] rounded-[28px] border border-black/8 bg-white/74 p-5 text-left shadow-[0_18px_40px_rgba(0,0,0,0.10)] backdrop-blur-xl dark:border-[#9F8CDC]/20 dark:bg-[#151427]/92">
              <div className="text-sm font-semibold text-black/80 dark:text-white/90">
                Still linking sign-in
              </div>
              <div className="mt-2 text-sm leading-6 text-black/55 dark:text-white/60">
                This usually clears on its own. If it hangs for more than a
                moment, retry once.
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  className="flex-1 rounded-2xl bg-black/90 px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] transition-transform duration-150 active:scale-[0.98] dark:bg-[#7657E8] dark:text-white"
                  onClick={onRetry}
                >
                  Retry
                </button>
                {!protectGuest ? (
                  <button
                    type="button"
                    className="flex-1 rounded-2xl bg-black/[0.06] px-4 py-3 text-sm font-semibold text-black/65 transition-transform duration-150 active:scale-[0.98] dark:bg-[#9278EC]/10 dark:text-[#D8D0F2]"
                    onClick={onSignOut}
                  >
                    Sign out
                  </button>
                ) : null}
              </div>

              {protectGuest ? (
                <div className="mt-3 text-xs font-semibold leading-5 text-amber-800 dark:text-amber-200">
                  Guest sign-out is disabled here because it would make this unlinked profile unrecoverable. Retry the account sync instead.
                </div>
              ) : null}

              {import.meta.env.DEV && error ? (
                <div className="mt-4 text-[11px] leading-5 text-black/40 dark:text-white/40">
                  {error}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };
  // ---------------------------------------------------------------------------
  // Auth guards (NO hooks below this point)
  // ---------------------------------------------------------------------------
  const hasFirebaseUser = !!firebaseUser?.uid;
  const shouldShowAuthBoot = isAuthLoading && !authBootTimedOut;
  const shouldShowBanned = isBanned;
  const shouldShowAuth = !hasFirebaseUser;
  const shouldShowProfileBoot = hasFirebaseUser && !isProfileHydrated;
  const shouldShowAccessBoot =
    hasFirebaseUser &&
    isProfileHydrated &&
    !resolvedAccessStatus &&
    !accessGateChecked &&
    !accessGateError;
  const accountSyncBlocked = IS_NATIVE && !!nativeUid && jsUid !== nativeUid;
  const showNativeBridgeGate =
    accountSyncBlocked && !!jsBridgeError && !isAccountSyncRetrying;

  const firstRunGlobePending = !hasShownBigBlueGlobe;

  const shouldShowInitialAppLoading =
    hasAppAccess &&
    firstRunGlobePending &&
    !needsLegal &&
    !onboardingNeeded &&
    !showWelcome &&
    !bigBlueGlobeAssetsReady;

  const shouldShowUnifiedBootLoading =
    shouldShowAuthBoot ||
    (shouldShowProfileBoot && !showNativeBridgeGate) ||
    shouldShowAccessBoot;

  const earlyBootProgress = shouldShowAuthBoot
    ? 14
    : shouldShowProfileBoot
      ? 34
      : shouldShowAccessBoot
        ? 52
        : 60;

  const earlyBootDetail = shouldShowAuthBoot
    ? "Checking your session…"
    : shouldShowProfileBoot
      ? "Loading your angler profile…"
      : shouldShowAccessBoot
        ? "Confirming Roddo access…"
        : "Preparing Big Blue…";

  if (shouldShowUnifiedBootLoading) {
    return (
      <LoadingScreen
        label="Opening Roddo…"
        detail="Preparing your session"
        progress={earlyBootProgress}
      />
    );
  }

  if (shouldShowBanned) return <BannedView />;

  // ✅ Auth must win before profile/access spinners.
  if (shouldShowAuth) {
    if (!IS_NATIVE && isWebKitBrowser) return <SafariAuthGate />;
    return <AuthView />;
  }

  const shouldShowAccessRecovery =
    hasFirebaseUser &&
    isProfileHydrated &&
    !resolvedAccessStatus &&
    (accessGateChecked || !!accessGateError);

  if (shouldShowAccessRecovery) {
    const recoveryProfile =
      userProfile ||
      ({
        uid: firebaseUser?.uid,
        waitlistNumber: accessGate?.waitlistNumber,
      } as any);

    return (
      <div className="min-h-[var(--roddo-vh)] bg-[#f7f2e8] dark:bg-[#090B13]">
        <WaitlistView
          userProfile={recoveryProfile as any}
          inviteError={
            accessGateError ||
            "Access is not active yet for this account. If this is your review/demo account, confirm it is marked active, then tap Check access."
          }
          onSignOut={handleLogout}
          onGoHome={() => { window.location.href = "/"; }}
          onRefreshAccess={() => {
            accessBootstrappedRef.current = null;
            setAccessGateChecked(false);
            setAccessGate(null);
            setAccessGateError(null);
            setAccessRetryNonce((n) => n + 1);
          }}
        />
      </div>
    );
  }

  if (resolvedAccessStatus === "waitlisted") {
    const waitlistProfile =
      userProfile ||
      ({
        uid: firebaseUser?.uid,
        waitlistNumber: accessGate?.waitlistNumber,
      } as any);

    return (
      <div className="min-h-[var(--roddo-vh)] bg-[#f7f2e8] dark:bg-[#090B13]">
        <WaitlistView
          userProfile={waitlistProfile as any}
          inviteError={invitePromptError}
          onSignOut={handleLogout}
          onGoHome={() => { window.location.href = "/"; }}
          onRefreshAccess={() => {
            accessBootstrappedRef.current = null;
            setAccessGateChecked(false);
            setAccessGate(null);
            setAccessGateError(null);
            setAccessRetryNonce((n) => n + 1);
          }}
        />
        {accessGateError ? (
          <div className="px-6 pb-6 text-xs text-black/60 dark:text-[#B8B0CC]">
            Access check: {accessGateError}
          </div>
        ) : null}
      </div>
    );
  }

  if (resolvedAccessStatus === "blocked") {
    return <BannedView />;
  }

  if (showNativeBridgeGate) {
    const retry = async () => {
      try {
        console.debug("[BOOT][bridge-gate] retry click", { nativeUid, jsUid });
        await runNativeJsBridge(nativeUid, "bridge-gate-retry");
      } catch (e) {
        console.warn("[BOOT][bridge-gate] retry threw", e);
      }
    };

    const hardSignOut = async () => {
      try {
        console.debug("[BOOT][bridge-gate] hard signOut");
      } catch {}
      try {
        await FirebaseAuthentication.signOut();
      } catch {}
      try {
        await signOut(auth);
      } catch {}
      try {
        setFirebaseUser(null);
      } catch {}
    };

    return (
      <NativeBridgeSplash
        showRecovery={showNativeBridgeRecovery}
        onRetry={() => {
          void retry();
        }}
        onSignOut={() => {
          void hardSignOut();
        }}
        protectGuest={
          (userProfile as any)?.accountType === "guest" ||
          (firebaseUser as any)?.isAnonymous === true
        }
        error={jsBridgeError}
      />
    );
  }

  if (needsLegal) {
    return (
      <div className="relative min-h-[var(--roddo-vh)] w-screen bg-surface/80 dark:bg-[#090B13]">
        <LegalGate
          tosVersion={TOS_VERSION}
          privacyVersion={PRIVACY_VERSION}
          preverifiedAgeBracket={getRoddoVerifiedAgeBracket(userProfile as any)}
          isSaving={isSavingLegal}
          isDarkMode={isDarkMode}
          onAccept={handleAcceptLegal}
        />
      </div>
    );
  }

  // Persistent Big Blue is mounted before the first globe reveal. During the
  // opening loader it resolves location, FishIQ, tournaments and marker layout;
  // the globe is not shown until mapWarmBoot.ready is true.
  const mapNode = (
    <RoutePortalProvider targetId="roddo-map-portal-host">
      <Suspense
      fallback={<LoadingScreen label="Opening the map…" detail="Preparing map engine…" progress={18} mode="overlay" />}
    >
      <MapView
        userProfile={
          {
            ...(normalizedUserProfile as any),
            tackleBox: gearItems,
            licenses: userLicenses,
            log: unifiedCatchEntries as any,
            catchLog: unifiedCatchEntries as any,
            catchEntries: unifiedCatchEntries as any,
            catchDiaryEntries: catchDiaryEntries as any,
            totalCatches: unifiedCatchEntries.length,
            completedChallengesCount: Object.keys(challengeCompletionMap || {})
              .length,
            completedChallengesMap: challengeCompletionMap,
          } as any
        }
        catchDiaryEntries={catchDiaryEntries as any}
        coords={coords as any}
        setCoords={setCoords as any}
        tempUnit={tempUnit}
        addToast={addToast}
        isDarkMode={isDarkMode}
        weatherData={weatherData as any}
        isLoadingWeather={isLoadingWeather as any}
        locationName={locationName}
        onBootProgress={(state: { progress: number; detail: string; ready: boolean }) => {
          setMapWarmBoot((prev) =>
            prev.progress === state.progress &&
            prev.detail === state.detail &&
            prev.ready === state.ready
              ? prev
              : state,
          );
        }}
        bootDependenciesReady={{
          weather: weatherBootReadyForMap,
          tournaments: tournamentBootReady,
        }}
        onChallengesClick={() => navigateTo(View.Challenges)}
        tournaments={Object.values(tournaments || {}) as any}
        marketplaceItems={(marketplaceItems || {}) as any}
        onBazaarClick={(payload?: any) =>
          navigateTo(View.Bazaar, {
            itemId: payload?.itemId || undefined,
            from: payload?.from || 'big_blue_bazaar_hub',
          })
        }
        onFishIQClick={(payload?: any) =>
          navigateTo(View.FishIQ, payload ?? null)
        }
        onBitePlanClick={() => navigateToRoot(View.Home)}
        onCrewClick={(payload?: { crewId?: string; from?: string }) =>
          navigateTo(
            View.Crew,
            payload?.crewId
              ? { crewId: payload.crewId, from: payload.from || View.Map }
              : {},
          )
        }
        onTackleBoxClick={() =>
          navigateTo(View.TackleBox, {
            tab: "avatar",
            focus: "searchLures",
          })
        }
        onEventsClick={(payload?: any) =>
          navigateTo(View.Events, payload ?? undefined)
        }
        onTournamentClick={(tournamentId: string) =>
          navigateTo(View.Events, {
            tournamentId,
            from: "big_blue_tournament_hub",
          })
        }
        onAchievementsClick={() => navigateTo(View.Achievements)}
        onLogCatchClick={(payload?: any) =>
          navigateTo(View.LogCatch, {
            ...(payload ?? {}),
            fromMap: true,
          })
        }
        catchLog={mapCatchEntries as any}
        onOpenCatchHistory={handleOpenMapCatchHistory}
        isActive={activeView === View.Map}
        viewState={
          activeView === View.Map ? (activeViewState as any) : undefined
        }
        onOpenGlobe={(payload: any) => {
          const lat = Number(payload?.lat ?? coords?.lat ?? 34.0522);
          const lng = Number(
            payload?.lng ?? payload?.lon ?? coords?.lon ?? -118.2437,
          );
          navigateToRoot(View.BigBlueGlobe, {
            launchMode: "zoomOut",
            initialTarget: {
              lat: Number.isFinite(lat) ? lat : 34.0522,
              lng: Number.isFinite(lng) ? lng : -118.2437,
              zoom: 5,
              label: "Current View",
            },
          });
        }}
      />
      </Suspense>
    </RoutePortalProvider>
  );

  // ---------------------------------------------------------------------------
  // Render helpers / view renderer
  // ---------------------------------------------------------------------------
  
  const renderActiveView = () => {
    if (accountSyncBlocked) {
      const retryAccountSync = () => {
        if (accountSyncRetryInFlightRef.current) {
          const msg = "sync already in progress";
          console.warn("[account-sync]", msg);
          setAccountSyncDebug(msg);
          return;
        }

        const msg = `manual retry tapped | view=${String(activeView)} native=${nativeUid || "null"} js=${jsUid || "null"} current=${auth.currentUser?.uid ?? "null"}`;
        console.debug("[account-sync]", msg);
        setAccountSyncDebug(msg);

        if (!nativeUid) {
          const missing = "retry blocked | missing nativeUid";
          console.warn("[account-sync]", missing);
          setAccountSyncDebug(missing);
          addToast?.(
            "Still waiting for Android sign-in. Try reopening Roddo.",
            "error",
          );
          return;
        }

        accountSyncRetryInFlightRef.current = true;
        setIsAccountSyncRetrying(true);
        setAccountSyncDebug("manual sync starting...");
        setJsBridgeError(null);

        void (async () => {
          try {
            const ok = await runNativeJsBridge(
              nativeUid,
              `manual-${String(activeView)}`,
            );

            const after = auth.currentUser?.uid ?? null;
            const done = `manual bridge finished | ok=${String(ok)} native=${nativeUid} js=${after || "null"}`;
            console.debug("[account-sync]", done);
            setAccountSyncDebug(done);

            if (ok && after === nativeUid) {
              accountSyncAutoAttemptedForUidRef.current = nativeUid;
              setJsAuthUid(nativeUid);
              setIsJsBridgeReady(true);
              setJsBridgeError(null);
              addToast?.("Account synced.", "success");
            } else {
              setIsJsBridgeReady(false);
              setJsBridgeError("Account sync still failed.");
              addToast?.(
                "Account sync still failed. Private data is protected.",
                "error",
              );
            }
          } catch (e: any) {
            const errMsg = `manual bridge threw | ${e?.name || "Error"}: ${e?.message || String(e)}`;
            console.error("[account-sync]", errMsg, e);
            setAccountSyncDebug(errMsg);
            setIsJsBridgeReady(false);
            setJsBridgeError(errMsg);
            addToast?.(
              "Account sync failed. Private data is protected.",
              "error",
            );
          } finally {
            accountSyncRetryInFlightRef.current = false;
            setIsAccountSyncRetrying(false);
          }
        })();
      };

      const accountSyncFallback = (surface: string) => {
        return (
          <AccountSyncUnavailableView
            surface={`${surface} · ${isAccountSyncRetrying ? "signing in..." : accountSyncDebug}`}
            isRetrying={isAccountSyncRetrying}
            autoRetry={false}
            onRetry={retryAccountSync}
            onOpenMap={() => navigateToRoot(View.Map)}
          />
        );
      };

      switch (activeView) {
        case View.Profile:
        case View.Crew:
        case View.CrewSettings:
        case View.EditProfile:
        case View.Account:
          return accountSyncFallback("profile");
        case View.LogCatch:
        case View.CatchHistory:
          return accountSyncFallback("Roddo Log");
        case View.TackleBox:
        case View.MyPage:
          return accountSyncFallback("The Hold");
        case View.LicenseDetail:
          return accountSyncFallback("licenses");
        case View.NotificationSettings:
        case View.Notifications:
          return accountSyncFallback("notifications");
        case View.PurchaseHistory:
        case View.Receipt:
        case View.SellerDashboard:
          return accountSyncFallback("Bazaar account data");
        case View.Community:
          return accountSyncFallback("Baleen Bay");
        case View.Achievements:
        case View.Challenges:
          return accountSyncFallback("progress");
        default:
          break;
      }
    }

    switch (activeView) {
      case View.Home:
        return (
          <Dashboard
            userProfile={normalizedUserProfile || userProfile}
            catchLog={unifiedCatchEntries as any}
            catchDiaryEntries={catchDiaryEntries as any}
            progressByKey={challengeProgressMap as any}
            completionsByKey={challengeCompletionMap as any}
            achievementMap={achievementMap as any}
            isDarkMode={isDarkMode}
            onOpenMap={() => navigateToRoot(View.Map)}
            onOpenFishIQ={() => navigateTo(View.FishIQ)}
            onOpenLogCatch={() => goTo(View.LogCatch)}
            onOpenCommunity={() => navigateTo(View.Community)}
            onOpenChallenges={() => navigateTo(View.Challenges)}
            onOpenAchievements={() => navigateTo(View.Achievements)}
            onOpenHold={() => navigateTo(View.TackleBox, { tab: 'avatar' })}
            onBack={() => navigateToRoot(View.Map)}
          />
        );

      case View.BigBlueGlobe:
        return (
          <BigBlueGlobeView
            isDarkMode={isDarkMode}
            coords={coords as any}
            launchMode={(activeViewState as any)?.launchMode || "firstOpen"}
            initialTarget={(activeViewState as any)?.initialTarget || null}
            currentUserProfile={(normalizedUserProfile as any) || (userProfile as any) || null}
            profile={(normalizedUserProfile as any) || (userProfile as any) || null}
            profilePhotoUrl={
              String(
                (normalizedUserProfile as any)?.avatar ||
                  (normalizedUserProfile as any)?.avatarUrl ||
                  (normalizedUserProfile as any)?.photoURL ||
                  (normalizedUserProfile as any)?.photoUrl ||
                  (normalizedUserProfile as any)?.profilePhotoUrl ||
                  (normalizedUserProfile as any)?.imageUrl ||
                  (userProfile as any)?.avatar ||
                  (userProfile as any)?.avatarUrl ||
                  (userProfile as any)?.photoURL ||
                  (userProfile as any)?.photoUrl ||
                  (userProfile as any)?.profilePhotoUrl ||
                  (userProfile as any)?.imageUrl ||
                  "",
              ).trim() || null
            }
            onDive={(target) => {
              const lat = Number(target?.lat);
              const lng = Number((target as any)?.lng ?? (target as any)?.lon);

              if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                addToast("Could not read that globe target.", "error");
                return;
              }

              markBigBlueGlobeSeen();
              setCoords({ lat, lon: lng } as any);
              navigateToRoot(View.Map, {
                globeTarget: {
                  lat,
                  lng,
                  zoom: Number(target?.zoom ?? 10),
                  label: target?.label || "Globe Target",
                },
              });
            }}
            onOpenMap={() => {
              markBigBlueGlobeSeen();
              navigateToRoot(View.Map, {});
            }}
          />
        );

      case View.Map:
        return null;

      case View.PublicProfile:
        return (
          <PublicProfileView
            currentUser={
              (publicProfileCurrentUser as any) ||
              (normalizedUserProfile as any) ||
              (userProfile as any)
            }
            isDarkMode={isDarkMode}
            targetUserId={String(
              (activeViewState as any)?.targetUserId ||
                (activeViewState as any)?.userId ||
                "",
            )}
            users={normalizedUsers as any}
            posts={communityPostsForView as any}
            followingIds={followingIds as any}
            addToast={addToast as any}
            onBack={() => {
              const returnCrewId = String((activeViewState as any)?.returnCrewId || "").trim();

              if (returnCrewId) {
                setViewStack((prev) => {
                  const withoutProfile = prev.slice(0, -1);
                  const prior = withoutProfile[withoutProfile.length - 1];

                  if (prior?.view === View.Crew) {
                    return [
                      ...withoutProfile.slice(0, -1),
                      {
                        ...prior,
                        state: {
                          ...(prior.state || {}),
                          crewId: returnCrewId,
                        },
                      },
                    ];
                  }

                  return [
                    ...withoutProfile,
                    {
                      view: View.Crew,
                      state: { crewId: returnCrewId },
                    },
                  ];
                });
                return;
              }

              if (viewStack.length > 1) navigateBack();
              else navigateToRoot(View.Profile);
            }}
          />
        );

      case View.Profile:
        return (
          <ProfileView
            userProfile={
              {
                ...(normalizedUserProfile as any),
                tackleBox: gearItems,
                licenses: userLicenses,
                log: unifiedCatchEntries as any,
                catchLog: unifiedCatchEntries as any,
                catchEntries: unifiedCatchEntries as any,
                catchDiaryEntries: catchDiaryEntries as any,
                totalCatches: unifiedCatchEntries.length,
                completedChallengesCount: Object.keys(
                  challengeCompletionMap || {},
                ).length,
                completedChallengesMap: challengeCompletionMap,
              } as any
            }
            isDarkMode={isDarkMode}
            userLicenses={userLicenses as any}
            onLicenseClick={(license: any) =>
              navigateTo(View.LicenseDetail, { licenseId: license.id })
            }
            onAddLicense={() =>
              navigateTo(View.LicenseDetail, { mode: "create" })
            }
            onEditProfile={() => navigateTo(View.EditProfile)}
            onNotificationSettings={() => navigateTo(View.NotificationSettings)}
            onManageGear={() => navigateTo(View.MyPage)}
            navigateTo={navigateTo}
            users={normalizedUsers as any}
            posts={communityPostsForView as any}
            addToast={addToast as any}
            marketplaceItems={Object.values(marketplaceItems) as any}
            onPurchaseItem={handlePurchaseMarketplaceItem as any}
            gearItems={gearItems as any} // ✅ add this
          />
        );

      case View.Crew:
        return (
          <CrewView
            currentUser={(normalizedUserProfile as any) || (userProfile as any)}
            users={normalizedUsers as any}
            navigateTo={navigateTo as any}
            addToast={addToast as any}
            isDarkMode={isDarkMode}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Map))}
            onManageCrew={() => navigateTo(View.CrewSettings)}
            directCrewBackToMap={
              String((activeViewState as any)?.from || "") === String(View.Map) ||
              String((activeViewState as any)?.from || "").toLowerCase() === "map"
            }
            initialCrewId={String((activeViewState as any)?.crewId || "")}
            initialCrewPostId={String((activeViewState as any)?.crewPostId || "")}
            initialDmId={String((activeViewState as any)?.dmId || "")}
            initialDmUid={String((activeViewState as any)?.dmUid || "")}
          />
        );

      case View.CrewSettings:
        return (
          <CrewSettingsView
            currentUser={(normalizedUserProfile as any) || (userProfile as any)}
            users={normalizedUsers as any}
            navigateTo={navigateTo as any}
            addToast={addToast as any}
            isDarkMode={isDarkMode}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Crew))}
          />
        );

      case View.EditProfile:
        return (
          <EditProfileView
            userProfile={
              {
                ...(normalizedUserProfile as any),
                tackleBox: gearItems,
                licenses: userLicenses,
                log: unifiedCatchEntries as any,
                catchLog: unifiedCatchEntries as any,
                catchEntries: unifiedCatchEntries as any,
                catchDiaryEntries: catchDiaryEntries as any,
                totalCatches: unifiedCatchEntries.length,
                completedChallengesCount: Object.keys(
                  challengeCompletionMap || {},
                ).length,
                completedChallengesMap: challengeCompletionMap,
              } as any
            }
            onProfileUpdate={async (updated: Partial<UserProfile>) => {
              if (!userProfile) return;

              const avatar = String(
                (updated as any)?.avatar ||
                  (updated as any)?.avatarUrl ||
                  (updated as any)?.photoURL ||
                  (updated as any)?.photoUrl ||
                  (updated as any)?.profilePhotoUrl ||
                  (updated as any)?.imageUrl ||
                  "",
              ).trim();

              const avatarPatch = avatar
                ? {
                    avatar,
                    avatarUrl: avatar,
                    photoURL: avatar,
                    photoUrl: avatar,
                    profilePhotoUrl: avatar,
                    imageUrl: avatar,
                    avatarUpdatedAt: Number(
                      (updated as any)?.avatarUpdatedAt || Date.now(),
                    ),
                  }
                : {};

              const optimisticPatch = {
                ...(updated as any),
                ...avatarPatch,
                updatedAt: Number((updated as any)?.updatedAt || Date.now()),
              } as any;

              setUserProfile((prev) =>
                prev
                  ? (normalizeUserProfileReputation({
                      ...(prev as any),
                      ...optimisticPatch,
                    } as any) as any)
                  : prev,
              );
              setUsers((prev) => ({
                ...(prev || {}),
                [userProfile.uid]: {
                  ...((prev || {})[userProfile.uid] as any),
                  ...profileToAppUser({
                    ...(userProfile as any),
                    ...optimisticPatch,
                  }),
                } as any,
              }));

              await updateUserProfile(userProfile.uid, optimisticPatch as any);

              setUserProfile((prev) =>
                prev
                  ? (normalizeUserProfileReputation({
                      ...(prev as any),
                      ...optimisticPatch,
                    } as any) as any)
                  : prev,
              );
              setUsers((prev) => ({
                ...(prev || {}),
                [userProfile.uid]: {
                  ...((prev || {})[userProfile.uid] as any),
                  ...profileToAppUser({
                    ...(userProfile as any),
                    ...optimisticPatch,
                  }),
                } as any,
              }));

              goTo(View.Profile, null);
            }}
            onCancel={() => goTo(View.Profile, null)}
            addToast={addToast as any}
            isDarkMode={isDarkMode}
          />
        );

      case View.Account:
        return (
          <AccountInfoView
            userProfile={(normalizedUserProfile as any) || (userProfile as any)}
            isDarkMode={isDarkMode}
            addToast={addToast as any}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
            onLogout={handleLogout}
          />
        );

      case View.Devices:
        return (
          <SettingsShell
            title="Devices"
            subtitle="Manage Bluetooth gear, rods, reels, and future Roddo gadgets."
            isDarkMode={isDarkMode}
          >
            <DevicesView
              userProfile={userProfile as any}
              isDarkMode={isDarkMode as any}
              onUpgradeToPro={() =>
                addToast(t("app.upgradeSoon", "Upgrades coming soon!"), "info")
              }
            />
          </SettingsShell>
        );

      case View.Events: {
        const canPreviewTournaments =
          tournamentConfig.publicAccessEnabled === true ||
          (isAdmin && tournamentConfig.adminPreviewEnabled !== false && tournamentPreviewEntered);

        if (canPreviewTournaments) {
          if (!userProfile) {
            return <Spinner />;
          }

          const openedFromBigBlueTournamentHub =
            String((activeViewState as any)?.from || "") ===
            "big_blue_tournament_hub";

          return (
            <TournamentView
              userProfile={userProfile}
              tournaments={tournaments}
              config={tournamentConfig}
              onBack={() => {
                if (openedFromBigBlueTournamentHub) {
                  // Return to the already-warm Big Blue map without force-opening
                  // the tournament hub again. The user intentionally left the Dock.
                  navigateToRoot(View.Map);
                  return;
                }

                if (viewStack.length > 1) navigateBack();
                else navigateToRoot(View.Map);
              }}
              addToast={addToast}
              initialTournamentId={(activeViewState as any)?.tournamentId || null}
              isDarkMode={isDarkMode}
              onOpenUserProfile={(targetUserId, tournamentId) => {
                if (!canNavigateTo(View.PublicProfile)) return;

                setViewStack((prev) => {
                  const next = [...prev];
                  const currentIndex = next.length - 1;
                  const current = next[currentIndex];

                  if (current?.view === View.Events && tournamentId) {
                    next[currentIndex] = {
                      ...current,
                      state: {
                        ...(current.state || {}),
                        tournamentId,
                      },
                    };
                  }

                  return [
                    ...next,
                    {
                      view: View.PublicProfile,
                      state: {
                        targetUserId,
                        fromTournament: true,
                        returnTournamentId: tournamentId || null,
                      },
                    },
                  ];
                });
              }}
              onLogTournamentCatch={(tournament) =>
                goTo(View.LogCatch, {
                  tournamentContext: {
                    tournamentId: tournament.id,
                    tournamentName: tournament.name,
                    startDate: tournament.startDate,
                    endDate: tournament.endDate,
                    scoringMode: tournament.scoringMode,
                    measurementUnit: tournament.measurementUnit,
                    eligibleSpecies: tournament.eligibleSpecies || [],
                    minQualifyingSize: tournament.minQualifyingSize || 0,
                    catchLimit: tournament.catchLimit || 5,
                    captureMode: tournament.captureMode,
                    tournamentCodeRequired:
                      tournament.tournamentCodeRequired === true,
                    tournamentCode: tournament.tournamentCode || null,
                    lengthBoardRequired: tournament.lengthBoardRequired === true,
                    scaleRequired: tournament.scaleRequired === true,
                    releaseVideoRequired:
                      tournament.releaseVideoRequired === true,
                    locationVerification:
                      tournament.locationVerification || 'approximate',
                  },
                })
              }
            />
          );
        }
        return (
          <TournamentsComingSoonView
            onBack={() => goTo(View.Map)}
            onEnterPreview={isAdmin ? () => {
              if (!tournamentConfig.adminPreviewEnabled) {
                addToast("Tournament admin preview is disabled in AdminView.", "info");
                return;
              }
              setTournamentPreviewEntered(true);
            } : undefined}
            isDarkMode={isDarkMode}
            logoNode={
              <RoddoFishIcon className="h-[104px] w-[84px]" mode="png" animated={false} />
            }
          />
        );
      }

      // If you have a logo component already, import it too:
      // import RoddoFishIcon from "./src/components/RoddoFishIcon"; // example path
      //case View.Bazaar:
      //  return (
      //    <BazaarView
      //      marketplaceItems={(marketplaceItems || {}) as any}
      //      currentUserProfile={userProfile as any}
      //      addToast={addToast as any}
      //      onBack={() => goTo(View.Map)}
      //    />
      //  );

      // ...
      case View.Bazaar:
        if (!FEATURE.BAZAAR_ENABLED && !FEATURE.BAZAAR_LISTINGS_ONLY) {
          return (
            <BazaarComingSoonView
              onBack={() => goTo(View.Map)}
              logoNode={
                <RoddoFishIcon
                  className="h-[104px] w-[84px]"
                  mode="png"
                  animated={false}
                />
              }
            />
          );
        }

        return (
          <Suspense fallback={<Spinner />}>
            <BazaarView
              marketplaceItems={(marketplaceItems || {}) as any}
              currentUserProfile={(normalizedUserProfile || userProfile) as any}
              addToast={addToast as any}
              isDarkMode={isDarkMode}
              currentCoords={coords as any}
              initialItemId={activeViewState?.itemId != null ? String(activeViewState.itemId) : undefined}
              initialThreadId={activeViewState?.threadId != null ? String(activeViewState.threadId) : undefined}
            />
          </Suspense>
        );

      //  case View.Bazaar:
      //  return (
      //    <Suspense fallback={<Spinner />}>
      //      <BazaarView
      //       goTo={goTo}
      //       currentUserProfile={userProfile as any}
      //       marketplaceItems={(marketplaceItems || {}) as any}
      //       currency={(userProfile as any)?.currency || 'USD'}
      //       onItemCreated={async () => {}}
      //       onItemDeleted={async () => {}}
      //       onOfferSubmitted={async () => {}}
      //       onPurchaseItem={handlePurchaseMarketplaceItem as any}
      //       addToast={addToast as any}
      //       onOpenSellerHub={() => goTo(View.SellerDashboard, null)}
      //       onOpenPurchases={() => goTo(View.PurchaseHistory, null)}
      //  />
      //  </Suspense>
      //  );

      case View.PurchaseHistory:
        if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
          return (
            <BazaarComingSoonView
              onBack={() => goTo(View.Map)}
              logoNode={
                <RoddoFishIcon
                  className="h-[104px] w-[84px]"
                  mode="png"
                  animated={false}
                />
              }
            />
          );
        }

        return (
          <Suspense fallback={<Spinner />}>
            <PurchaseHistoryView
              goTo={goTo}
              addToast={addToast as any}
              viewState={activeViewState as any}
              transactions={marketplaceTransactions as any}
              marketplaceTransactions={marketplaceTransactions as any}
              marketplaceItems={marketplaceItems as any}
              userProfile={userProfile as any}
              isDarkMode={isDarkMode}
            />
          </Suspense>
        );

      case View.Receipt:
        if (!FEATURE.BAZAAR_PAYMENTS_ENABLED) {
          return (
            <BazaarComingSoonView
              onBack={() => goTo(View.Map)}
              logoNode={
                <RoddoFishIcon
                  className="h-[104px] w-[84px]"
                  mode="png"
                  animated={false}
                />
              }
            />
          );
        }

        return (
          <Suspense fallback={<Spinner />}>
            <ReceiptView
              goTo={goTo}
              addToast={addToast as any}
              viewState={activeViewState as any}
              transactions={marketplaceTransactions as any}
              marketplaceItems={marketplaceItems as any}
              isDarkMode={isDarkMode}
            />
          </Suspense>
        );

      case View.SellerDashboard: {
        if (!FEATURE.BAZAAR_ENABLED && !FEATURE.BAZAAR_LISTINGS_ONLY) {
          return (
            <BazaarComingSoonView
              onBack={() => goTo(View.Map)}
              logoNode={
                <RoddoFishIcon
                  className="h-[104px] w-[84px]"
                  mode="png"
                  animated={false}
                />
              }
            />
          );
        }

        // ✅ SellerDashboardView expects string keys
        const escrowByItemIdStr: Record<string, EscrowInfo> =
          Object.fromEntries(
            Object.entries(escrowByItemId || {}).map(([k, v]) => [
              String(k),
              v,
            ]),
          ) as any;

        return (
          <SellerDashboardShell>
            <SellerDashboardView
              userProfile={userProfile as any}
              marketplaceItems={Object.values(marketplaceItems || {}) as any}
              onItemUpdated={async (itemId: any, patch: any) => {
                await updateMarketplaceItem(String(itemId), patch as any);
                // (optional) optimistic local patch:
                setMarketplaceItems((prev) => ({
                  ...prev,
                  [itemId]: prev[itemId]
                    ? ({ ...(prev as any)[itemId], ...patch } as any)
                    : prev[itemId],
                }));
              }}
              onItemDeleted={async (itemId: any) => {
                await deleteMarketplaceItem(itemId as any);
                setMarketplaceItems((prev) => {
                  const next = { ...(prev as any) };
                  delete next[itemId];
                  return next;
                });
              }}
              onOfferAction={async (
                itemId: any,
                offerId: string,
                action: "accept" | "reject" | "counter",
                payload?: { message?: string; amountCents?: number },
              ) => {
                if (!firebaseUser) return;

                const itemIdStr = String(itemId);
                const offerRef = doc(
                  db as any,
                  "marketplaceItems",
                  itemIdStr,
                  "offers",
                  offerId,
                );

                const snap = await getDoc(offerRef as any);
                if (!snap.exists()) throw new Error("Offer not found");

                const offer = snap.data() as any;
                const buyerId = offer?.buyerId;
                if (!buyerId) throw new Error("Offer missing buyerId");

                const basePatch: any = {
                  updatedAt: Date.now(),
                  respondedAt: Date.now(),
                };

                if (action === "accept") {
                  basePatch.status = "accepted";
                  basePatch.sellerAction = "accept";
                } else if (action === "reject") {
                  basePatch.status = "rejected";
                  basePatch.sellerAction = "reject";
                } else {
                  // counter: keep status pending, attach seller response fields
                  basePatch.status = "pending";
                  basePatch.sellerAction = "counter";
                  if (payload?.message)
                    basePatch.sellerMessage = payload.message;
                  if (typeof payload?.amountCents === "number")
                    basePatch.sellerAmountCents = payload.amountCents;
                }

                await updateDoc(offerRef as any, basePatch);

                // notify buyer (panel supports OFFER_* types)
                const type =
                  action === "accept"
                    ? NotificationType.OFFER_ACCEPTED
                    : action === "reject"
                      ? NotificationType.OFFER_DECLINED
                      : NotificationType.OFFER_COUNTERED;

                await createNotification({
                  userId: buyerId,
                  type,
                  title:
                    action === "accept"
                      ? "Offer accepted"
                      : action === "reject"
                        ? "Offer rejected"
                        : "Counter-offer",
                  preview:
                    action === "counter"
                      ? payload?.message || "Seller sent a counter-offer"
                      : `On ${offer?.itemTitle || "a bazaar item"}`,
                  link: {
                    // harmless now; future buyer UX can read target/subTarget
                    view: View.Bazaar,
                    targetId: itemIdStr,
                    subTargetId: offerId,
                  } as any,
                  actor: notificationActor,
                  entity: { itemId: itemIdStr, offerId } as any,
                  timestamp: Date.now(),
                  read: false,
                } as any);
              }}
              currency={(userProfile as any)?.currency || "USD"}
              escrowByItemId={FEATURE.BAZAAR_PAYMENTS_ENABLED ? escrowByItemIdStr : {}}
              onReleaseEscrow={FEATURE.BAZAAR_PAYMENTS_ENABLED ? (handleReleaseEscrow as any) : undefined}
              addToast={addToast as any}
              viewState={activeViewState as any}
              onCancel={navigateBack}
              isDarkMode={isDarkMode}
            />
          </SellerDashboardShell>
        );
      }

      case View.Community:
        return (
          <CommunityView
            currentUserProfile={normalizedUserProfile as any}
            userProfile={
              {
                ...(normalizedUserProfile as any),
                tackleBox: gearItems,
                licenses: userLicenses,
                log: unifiedCatchEntries as any,
                catchLog: unifiedCatchEntries as any,
                catchEntries: unifiedCatchEntries as any,
                catchDiaryEntries: catchDiaryEntries as any,
                totalCatches: unifiedCatchEntries.length,
                completedChallengesCount: Object.keys(
                  challengeCompletionMap || {},
                ).length,
                completedChallengesMap: challengeCompletionMap,
              } as any
            }
            users={normalizedUsers as any}
            isDarkMode={isDarkMode}
            posts={communityPostsForView as any}
commentsByPost={commentsByPost as any}
onCommentsOpened={handleCommunityCommentsOpened}
followingIds={followingIds as any}
            addToast={addToast as any}
            onCreatePost={handleCreateCommunityPost as any}
            onEditPost={handlePostEdited as any}
            onDeletePost={handlePostDeleted as any}
            onAddComment={handleCommentAdded as any}
            onEditComment={handleCommentEdited as any}
            onDeleteComment={handleCommentDeleted as any}
            onToggleLike={handleToggleLike as any}
            onToggleCommentLike={handleToggleCommentLike as any}
            onLoadMorePosts={loadMoreCommunityPosts as any}
            hasMorePosts={communityHasMore}
            isLoadingMorePosts={communityLoadingMore}
            onAddToCatchDiary={handleAddToCatchDiary as any}
            navigateTo={navigateTo as any}
            viewState={activeViewState as any}
            focusPostId={(activeViewState as any)?.focusPostId}
            focusCommentId={(activeViewState as any)?.focusCommentId}
            onOpenUserProfile={(targetUserId: string) =>
              navigateTo(View.PublicProfile, { targetUserId })
            }
          />
        );

      case View.TackleBox:
        return (
          <TackleBoxView
            userProfile={
              {
                ...(userProfile as any),
                totalCatches: unifiedCatchEntries.length,
                log: unifiedCatchEntries as any,
                catchLog: unifiedCatchEntries as any,
                catchEntries: unifiedCatchEntries as any,
                catchDiaryEntries: catchDiaryEntries as any,
                catchDiary: catchDiaryEntries as any,
                tackleBox: gearItems,
                licenses: userLicenses,
                completedChallengesCount: Object.keys(
                  challengeCompletionMap || {},
                ).length,
                completedChallengesMap: challengeCompletionMap,
              } as any
            }
            navigateTo={navigateTo as any} // ✅ PUSH so back works
            addToast={addToast as any}
            onOpenCatchHistory={() => navigateTo(View.CatchHistory)}
            viewState={activeViewState as any}
            isDarkMode={isDarkMode}
          />
        );

      case View.LicenseDetail: {
        if (!userProfile) return <Spinner />;

        return (
          <LicenseDetailShell>
            <LicenseDetailView
              userProfile={userProfile as any}
              userLicenses={userLicenses as any}
              isDarkMode={isDarkMode}
              onLicensesChange={(next: any) => {
                setUserLicenses(next);
              }}
              onBack={navigateBackSmart}
              onStartPurchase={() => {}}
            />
          </LicenseDetailShell>
        );
      }
      case View.CatchHistory:
        return (
          <CatchHistoryView
            entries={unifiedCatchEntries as any}
            userId={userProfile?.uid || firebaseUser?.uid || ''}
            onBack={navigateBackSmart}
            onLogCatch={() => goTo(View.LogCatch)}
            onEditCatch={(entry) =>
              navigateTo(View.LogCatch, {
                editEntry: (entry as any)?.raw || entry,
                fromCatchHistory: true,
              })
            }
            addToast={addToast as any}
            viewState={activeViewState as any}
          />
        );

      case View.MyPage:
        return (
          <GearHubView
            userProfile={
              {
                ...(userProfile as any),
                tackleBox: gearItems,
              } as any
            }
            onManageTackleBox={() => navigateTo(View.TackleBox)}
            onBack={navigateBackSmart}
            addToast={addToast as any}
          />
        );

      case View.LogCatch:
        return (
          <Suspense fallback={<Spinner />}>
            {userProfile && (
              <LogCatchView
                userProfile={userProfile}
                userId={userProfile.uid}
                onCancel={() =>
                  (activeViewState as any)?.editEntry
                    ? navigateBackSmart()
                    : (activeViewState as any)?.tournamentContext?.tournamentId
                      ? goTo(View.Events, {
                          tournamentId: (activeViewState as any).tournamentContext.tournamentId,
                        })
                      : (activeViewState as any)?.fromMap
                        ? setViewStack((prev) => {
                            if (prev.length <= 1) return prev;
                            const next = prev.slice(0, -1);
                            const mapIndex = next.length - 1;
                            if (next[mapIndex]?.view === View.Map) {
                              next[mapIndex] = {
                                ...next[mapIndex],
                                state: {
                                  ...(next[mapIndex].state || {}),
                                  ...((activeViewState as any)?.bigBlueReturnSession
                                    ? {
                                        restoreFishIQSession: (activeViewState as any)
                                          .bigBlueReturnSession,
                                      }
                                    : {}),
                                },
                              };
                            }
                            return next;
                          })
                        : goTo(View.Map)
                }
                tournamentContext={(activeViewState as any)?.tournamentContext || null}
                editEntry={(activeViewState as any)?.editEntry || null}
                onCatchAdded={(catchEntry) => {
                  // LogCatchView now writes canonical entries to users/{uid}/catchDiary.
                  // Optimistically add to catchDiaryEntries, not legacy catchLog, so the
                  // Catch History screen does not show the same catch as both Log + Diary
                  // before the Firestore listener catches up.
                  setCatchDiaryEntries((prev: any[]) => {
                    const id = String((catchEntry as any)?.id || "").trim();
                    if (!id) return prev as any;

                    const existingIndex = (prev || []).findIndex(
                      (entry: any) => String(entry?.id || "").trim() === id,
                    );

                    if (existingIndex >= 0) {
                      const next = [...(prev || [])];
                      next[existingIndex] = {
                        ...next[existingIndex],
                        ...(catchEntry as any),
                        createdFrom: next[existingIndex]?.createdFrom || "log",
                      };
                      return next as any;
                    }

                    return [
                      { ...(catchEntry as any), createdFrom: "log" },
                      ...(prev || []),
                    ] as any;
                  });
                }}
                catchLog={catchLog as any}
                catchDiaryEntries={catchDiaryEntries as any}
                weatherData={weatherData}
                coords={((activeViewState as any)?.coords ?? coords) as any}
                tempUnit={tempUnit}
                navigateTo={goTo}
                addToast={addToast}
                initialTab={(activeViewState as any)?.initialTab}
                initialQuery={(activeViewState as any)?.initialQuery || ""}
                prefilledData={
                  (activeViewState as any)?.prefilledData ??
                  ((activeViewState as any)?.species
                    ? { species: (activeViewState as any).species }
                    : null)
                }
                openInsight={Boolean((activeViewState as any)?.openInsight)}
                catchId={(activeViewState as any)?.catchId}
                onDisablePostCatchInsight={async () => {
                  if (!firebaseUser || !userProfile) return;
                  const notificationSettings = {
                    ...((userProfile as any).notificationSettings || {}),
                    showPostCatchInsight: false,
                  };
                  await updateUserProfile(firebaseUser.uid, {
                    notificationSettings,
                  } as any);
                  setUserProfile((prev: any) =>
                    prev ? { ...prev, notificationSettings } : prev,
                  );
                  addToast("Post-catch Insight turned off.", "success");
                }}
                onReturnAfterSave={(payload?: any) => {
                  if ((activeViewState as any)?.fromMap) {
                    setViewStack((prev) => {
                      if (prev.length <= 1) return prev;

                      const next = prev.slice(0, -1);
                      const mapIndex = next.length - 1;
                      if (next[mapIndex]?.view === View.Map) {
                        next[mapIndex] = {
                          ...next[mapIndex],
                          state: {
                            ...(next[mapIndex].state || {}),
                            ...((activeViewState as any)?.bigBlueReturnSession
                              ? {
                                  restoreFishIQSession: (activeViewState as any)
                                    .bigBlueReturnSession,
                                }
                              : {}),
                            justLoggedCatchCoords: payload?.coords
                              ? { lat: payload.coords.lat, lng: payload.coords.lon }
                              : null,
                            justLoggedCatchAt: Date.now(),
                          },
                        };
                      }
                      return next;
                    });
                    return;
                  }

                  goTo(View.Map, {
                    justLoggedCatchCoords: payload?.coords
                      ? { lat: payload.coords.lat, lng: payload.coords.lon }
                      : null,
                    justLoggedCatchAt: Date.now(),
                  });
                }}
                onPostLogInsight={(payload: any) => {
                  const shouldShow =
                    (userProfile as any)?.notificationSettings
                      ?.showPostCatchInsight !== false;
                  if (!shouldShow) {
                    payload?.onDone?.();
                    return;
                  }
                  setPostLogInsight(payload);
                }}
              />
            )}
          </Suspense>
        );
      case View.Achievements: {
        if (!userProfile) return <Spinner />;
        return (
          <AchievementsShell>
            <AchievementsView
              userProfile={userProfile as any}
              achievementMap={achievementMap as any}
              isDarkMode={isDarkMode}
              onBack={navigateBackSmart}
            />
          </AchievementsShell>
        );
      }
      case View.Challenges: {
        if (!userProfile) return <Spinner />;
        return (
          <ChallengesShell>
            <ChallengesView
              userProfile={userProfile as any}
              isDarkMode={isDarkMode}
users={users as any}
              viewState={activeViewState as any}
              challenges={defaultChallenges as any}
              progressByKey={challengeProgressMap as any}
              completionsByKey={challengeCompletionMap as any}
              achievementMap={achievementMap as any}
              onBack={navigateBackSmart}
              onOpenUserProfile={(targetUserId: string) =>
                navigateTo(View.PublicProfile, { targetUserId })
              }
            />
          </ChallengesShell>
        );
      }
      case View.FishIQ:
        return (
          <FishIQShell>
            <FishIQView
              userProfile={userProfile as any}
              catchLog={catchLog}
              coords={coords as any}
              mapContext={(activeViewState as any)?.mapContext || null}
              addToast={addToast as any}
              isDarkMode={isDarkMode}
              onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
              onSelectSpecies={(name: string, payload?: any) =>
                navigateTo(
                  View.LogCatch,
                  payload?.prefilledData
                    ? {
                        ...payload,
                        species: payload.prefilledData.species || name,
                      }
                    : { species: name },
                )
              }
              initialQuery={(activeViewState as any)?.initialQuery || ""}
              communityPattern={
                (activeViewState as any)?.communityPattern || null
              }
            />
          </FishIQShell>
        );
      case View.Admin: {
        if (!isAdmin)
          return (
            <div className="p-6 text-sm text-slate-500">
              Admin access denied.
            </div>
          );

        return (
          <Suspense fallback={<Spinner />}>
            <AdminShell>
              <AdminView
                userProfile={userProfile as any}
                users={users as any}
                posts={Object.values(communityPosts || {}) as any}
                marketplaceItems={marketplaceItems as any}
                tournaments={tournaments as any}
                currency={currency as any}
                onUserAndPostsDeleted={async (targetUserId: string) => {
                  try {
                    await cfAdminDeleteUserAndPosts({ userId: targetUserId });
                    addToast("User deleted.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to delete user.", "error");
                  }
                }}
                onUpdateUserRoles={async (
                  targetUserId: string,
                  roles: { isAdmin?: boolean; isSeller?: boolean },
                ) => {
                  try {
                    await cfAdminUpdateUserRoles({
                      userId: targetUserId,
                      roles,
                    });
                    addToast("Roles updated.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to update roles.", "error");
                  }
                }}
                onSendAnnouncement={async (message: string) => {
                  try {
                    await cfAdminSendAnnouncement({ message });
                    addToast("Announcement sent.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to send announcement.", "error");
                  }
                }}
                onPostDeleted={async (postId: string) => {
                  try {
                    await cfAdminDeletePost({ postId });
                    addToast("Post deleted.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to delete post.", "error");
                  }
                }}
                onPostEdited={async (postId: string, updatedData: any) => {
                  try {
                    await cfAdminEditPost({ postId, updatedData });
                    addToast("Post updated.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to update post.", "error");
                  }
                }}
                onItemDeleted={async (itemId: string) => {
                  try {
                    await cfAdminDeleteMarketplaceItem({ itemId });
                    addToast("Item deleted.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to delete item.", "error");
                  }
                }}
                onItemUpdated={async (item: MarketplaceItem) => {
                  try {
                    await cfAdminUpdateMarketplaceItem({ item });
                    addToast("Item updated.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to update item.", "error");
                  }
                }}
                onTournamentDelete={async (tournamentId: string) => {
                  try {
                    await cfAdminDeleteTournament({ tournamentId });
                    addToast("Tournament deleted.", "success");
                  } catch (e) {
                    console.error(e);
                    addToast("Unable to delete tournament.", "error");
                  }
                }}
                onCancel={() => goTo(View.EditProfile, null)}
              />
            </AdminShell>
          </Suspense>
        );
      }

      case View.NotificationSettings:
        return (
          <NotificationSettingsView
            userProfile={userProfile as any}
            onUpdateProfile={async (updated: any) => {
              if (!firebaseUser) return;

              const incomingSettings = {
                ...((userProfile as any)?.notificationSettings || {}),
                ...(updated?.notificationSettings || {}),
              } as Record<string, any>;

              const hasCrewPresenceUpdate =
                Object.prototype.hasOwnProperty.call(incomingSettings, "shareCrewMapPresence") ||
                Object.prototype.hasOwnProperty.call(incomingSettings, "crewMapPresence") ||
                Object.prototype.hasOwnProperty.call(incomingSettings, "crewMapPresenceEnabled");

              const crewPresenceEnabled =
                incomingSettings.shareCrewMapPresence === true ||
                incomingSettings.crewMapPresence === true ||
                incomingSettings.crewMapPresenceEnabled === true;

              const notificationSettings = hasCrewPresenceUpdate
                ? {
                    ...incomingSettings,
                    // Keep Crew Deck and Notification Settings reading the same value.
                    shareCrewMapPresence: crewPresenceEnabled,
                    crewMapPresence: crewPresenceEnabled,
                    crewMapPresenceEnabled: crewPresenceEnabled,
                  }
                : incomingSettings;

              await updateUserProfile(firebaseUser.uid, {
                notificationSettings,
              } as any);

              setUserProfile((prev: any) =>
                prev
                  ? ({
                      ...prev,
                      ...updated,
                      notificationSettings,
                      savedSpots: updated?.savedSpots ?? prev.savedSpots,
                    } as any)
                  : ({ ...updated, notificationSettings } as any),
              );
            }}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Map))}
            addToast={addToast as any}
            isDarkMode={isDarkMode}
          />
        );
      case View.RoddoRadio:
        return (
          <RoddoRadioView
            isDarkMode={isDarkMode}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
          />
        );

        case View.Support:
          if (!userProfile) return <Spinner />;
        
          return (
            <SettingsShell
              title="Support"
              subtitle="Send a ticket to the Roddo admin team when something goes sideways."
              isDarkMode={isDarkMode}
              onBack={() =>
                viewStack.length > 1
                  ? navigateBack()
                  : navigateToRoot(View.Profile)
              }
            >
              <SupportView
                userProfile={userProfile as any}
                currentUserProfile={normalizedUserProfile as any}
                addToast={addToast as any}
                isDarkMode={isDarkMode}
              />
            </SettingsShell>
          );

      case View.Legal:
        if (!userProfile) return <Spinner />;
        return (
          <LegalSettingsView
            userProfile={userProfile as any}
            addToast={addToast as any}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
            isDarkMode={isDarkMode}
            onOpenTos={() => navigateTo((View as any).TermsOfService)}
            onOpenPrivacy={() => navigateTo((View as any).PrivacyPolicy)}
            onOpenIntro={replayGuidedTour}
            onUpdateProfile={async (nextProfile: any) => {
              if (!firebaseUser) return;
              await updateUserProfile(firebaseUser.uid, {
                legal: nextProfile.legal,
                fishiqContributionEnabled:
                  (nextProfile as any).fishiqContributionEnabled ??
                  (nextProfile as any)?.legal?.consents?.fishiqContribution
                    ?.enabled ??
                  (userProfile as any)?.fishiqContributionEnabled ??
                  true,
                updatedAt: Date.now(),
              } as any);
              setUserProfile((prev: any) =>
                prev
                  ? ({
                      ...prev,
                      ...nextProfile,
                      savedSpots: nextProfile?.savedSpots ?? prev.savedSpots,
                    } as any)
                  : nextProfile,
              );
            }}
          />
        );

      case (View as any).TermsOfService:
        if (!userProfile) return <Spinner />;
        return (
          <TermsOfServiceView
            userProfile={userProfile as any}
            addToast={addToast as any}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
            isDarkMode={isDarkMode}
            onUpdateProfile={async (nextProfile: any) => {
              if (!firebaseUser) return;
              await updateUserProfile(firebaseUser.uid, {
                legal: nextProfile.legal,
                updatedAt: Date.now(),
              } as any);
              setUserProfile((prev: any) =>
                prev
                  ? ({
                      ...prev,
                      ...nextProfile,
                      savedSpots: nextProfile?.savedSpots ?? prev.savedSpots,
                    } as any)
                  : nextProfile,
              );
            }}
          />
        );

      case (View as any).PrivacyPolicy:
        if (!userProfile) return <Spinner />;
        return (
          <PrivacyPolicyView
            userProfile={userProfile as any}
            addToast={addToast as any}
            onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
            isDarkMode={isDarkMode}
            onUpdateProfile={async (nextProfile: any) => {
              if (!firebaseUser) return;
              await updateUserProfile(firebaseUser.uid, {
                legal: nextProfile.legal,
                updatedAt: Date.now(),
              } as any);
              setUserProfile((prev: any) =>
                prev
                  ? ({
                      ...prev,
                      ...nextProfile,
                      savedSpots: nextProfile?.savedSpots ?? prev.savedSpots,
                    } as any)
                  : nextProfile,
              );
            }}
          />
        );

      case View.Notifications:
        return (
          <NotificationsShell>
            <NotificationsView
              notifications={notifications as any}
              onBack={() => (viewStack.length > 1 ? navigateBack() : navigateToRoot(View.Profile))}
              onNotificationClick={(n: any) => {
                openNotification(n);
              }}
              onMarkAllRead={markAllNotificationsRead}
              onClearAll={clearAllNotifications}
              onMarkRead={(notificationId: string) => {
                setNotifications((prev) =>
                  prev.map((n) =>
                    n.id === notificationId && !n.read
                      ? { ...n, read: true }
                      : n,
                  ),
                );

                if (firebaseUser) {
                  markNotificationRead(firebaseUser.uid, notificationId).catch(
                    console.error,
                  );
                }
              }}
            />
          </NotificationsShell>
        );

      
      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // ROOT RENDER (NO HOOKS BELOW THIS POINT)
  // ---------------------------------------------------------------------------
  const isFullScreenView = false;
  const isComingSoonArtView =
    activeView === View.Events ||
    (!FEATURE.BAZAAR_ENABLED && !FEATURE.BAZAAR_LISTINGS_ONLY && activeView === View.Bazaar) ||
    (!FEATURE.BAZAAR_ENABLED && !FEATURE.BAZAAR_LISTINGS_ONLY && activeView === View.SellerDashboard) ||
    (!FEATURE.BAZAAR_PAYMENTS_ENABLED &&
      (activeView === View.PurchaseHistory ||
        activeView === View.Receipt ||
        activeView === View.CheckoutSuccess ||
        activeView === View.CheckoutCancel));
  const NAV_H = 84; // 🔒 must match BottomNav CORE_DOCK_H + src/index.css --roddo-nav-h
  const rodTheme = getRoddoTheme(isDarkMode);
  const isPersistentAppSurface = PERSISTENT_APP_SURFACES.has(activeView);
  const activeNonMapNode = activeView !== View.Map ? renderActiveView() : null;

  return (
    <RoddoRadioProvider>
      <div
        data-roddo-theme={isDarkMode ? "dark" : "light"}
      className={
        IS_NATIVE
          ? "w-full overflow-hidden roddo-theme-lock text-[var(--roddo-current-text,var(--roddo-text-light))] dark:text-[var(--roddo-text-dark)]"
          : "w-full h-[100dvh] min-h-[100dvh] flex justify-center items-start overflow-hidden roddo-theme-lock text-[var(--roddo-current-text,var(--roddo-text-light))] dark:text-[var(--roddo-text-dark)]"
      }
      style={{
        colorScheme: isDarkMode ? "dark" : "light",
        background: `linear-gradient(180deg, ${rodTheme.appBg} 0%, ${rodTheme.appBgAlt} 100%)`,
      }}
    >
      <div
        id="roddo-app-shell"
        data-roddo-theme={isDarkMode ? "dark" : "light"}
        className={
          IS_NATIVE
            ? "relative w-full flex flex-col overflow-hidden roddo-theme-lock text-[var(--roddo-current-text,var(--roddo-text-light))] dark:text-[var(--roddo-text-dark)]"
            : "roddo-web-shell relative w-full flex flex-col overflow-hidden roddo-theme-lock text-[var(--roddo-current-text,var(--roddo-text-light))] dark:text-[var(--roddo-text-dark)] md:rounded-[28px]"
        }
        style={
          {
            // Native keeps Roddo's measured viewport variable so keyboard/safe-area
            // behavior remains unchanged. Web uses the browser's dynamic viewport
            // directly so the desktop shell reaches the physical bottom edge instead
            // of inheriting a slightly-short visualViewport measurement.
            height: IS_NATIVE
              ? "var(--roddo-app-vh, var(--roddo-vh, 100dvh))"
              : "100dvh",
            minHeight: IS_NATIVE
              ? "var(--roddo-app-vh, var(--roddo-vh, 100dvh))"
              : "100dvh",
            maxHeight: IS_NATIVE
              ? "var(--roddo-app-vh, var(--roddo-vh, 100dvh))"
              : "100dvh",
            position: IS_NATIVE ? "fixed" : "relative",
            inset: IS_NATIVE ? 0 : undefined,
            ["--roddo-nav-h" as any]: "84px",
            background: isDarkMode
              ? `linear-gradient(180deg, ${rodTheme.appBg} 0%, ${rodTheme.appBgAlt} 100%)`
              : `linear-gradient(180deg, ${rodTheme.appBg} 0%, ${rodTheme.appBgAlt} 100%)`,
            boxShadow: IS_NATIVE ? undefined : rodTheme.shadowStrong,
            border: IS_NATIVE ? undefined : `1px solid ${rodTheme.border}`,
            opacity: appVisible ? 1 : 0,
          } as React.CSSProperties
        }
      >
        {/* Stripe return parsing stays completely dormant until Roddo Checkout is enabled. */}
        {FEATURE.BAZAAR_PAYMENTS_ENABLED ? (
          <CheckoutStatusHandler
            addToast={addToast}
            onCheckoutResult={handleCheckoutResult}
          />
        ) : null}

        {showWelcome && isProfileHydrated && userProfile && !needsLegal ? (
          <RoddoWelcomeCard
            userProfile={userProfile}
            isDarkMode={isDarkMode}
            isSaving={isFirstRunSaving}
            onStart={handleFirstRunStart}
          />
        ) : null}

        {guidedTourActive && !showWelcome && !needsLegal ? (
          <RoddoGuidedTour
            key={`roddo-guided-tour-${guidedTourSessionId}`}
            activeView={activeView}
            isDarkMode={isDarkMode}
            onNavigate={(view) => navigateToRoot(view, { source: 'guided-tour' })}
            onFinish={() => finishGuidedTour(false)}
            onSkip={() => finishGuidedTour(true)}
          />
        ) : null}

        {!isFullScreenView ? (
          <Header
            activeView={
              (activeView === View.BigBlueGlobe ? View.Map : activeView) as any
            }
            userName={
              (userProfile as any)?.displayName ||
              (userProfile as any)?.username ||
              userProfile?.name ||
              "Angler"
            }
            userAvatarUrl={
              (userProfile as any)?.avatar ||
              (userProfile as any)?.avatarUrl ||
              (userProfile as any)?.photoURL ||
              null
            }
            isDarkMode={isDarkMode}
            onToggleTheme={() => setIsDarkMode((v) => !v)}
            onOpenProfile={() => goTo(View.Profile)}
            onOpenAccount={() => navigateTo(View.Account)}
            onOpenCrewSettings={() => navigateTo(View.CrewSettings)}
            onOpenNotificationSettings={() => navigateTo(View.NotificationSettings)}
            onOpenSupport={() => navigateTo(View.Support)}
            onOpenLegal={() => navigateTo(View.Legal)}
            onOpenAdmin={isAdmin ? () => navigateTo(View.Admin) : undefined}
            onLogout={handleLogout}
            notifications={notifications as any}
            onToggleNotifications={() => setShowNotifications((v) => !v)}
          />
        ) : null}

        <NotificationsPanel
          key={`notifications-panel-${isDarkMode ? "dark" : "light"}`}
          show={showNotifications}
          notifications={notifications as any}
          isDarkMode={isDarkMode}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={markAllNotificationsRead}
          onClearAll={clearAllNotifications}
          onNotificationClick={(n: any) => {
            openNotification(n);
            setShowNotifications(false);
          }}
          onViewAll={() => {
            setShowNotifications(false);
            navigateTo(View.Notifications);
          }}
        />
        {/* Content area */}
        <main
          className={[
            "roddo-main-workspace relative flex-1 min-h-0 z-0 overflow-hidden",
            activeView === View.BigBlueGlobe || isComingSoonArtView
              ? "overscroll-none"
              : "",
          ].join(" ")}
          style={{
            // Keep Big Blue mounted for the entire authenticated app session.
            // Globe/full-art routes may paint behind the absolute BottomNav, but
            // the hidden Leaflet/FishIQ layer remains alive underneath so returning
            // to Big Blue is instant and never replays its bootstrap loader.
            // Flex owns the available height below Header. The absolute nav overlays
            // this area; non-map pages reserve its clearance in .roddo-content-scroll.
            // Do not subtract the dock here too or long pages lose reachable space.
            minHeight: 0,
          }}
        >
          {/* Persistent warm Big Blue layer. Never unmount this on tab/globe changes. */}
          <div
            aria-hidden={activeView !== View.Map}
            className={[
              "absolute inset-0 h-full w-full overflow-hidden",
              activeView === View.Map
                ? "z-0 visible pointer-events-auto"
                : "z-0 invisible pointer-events-none",
            ].join(" ")}
            style={{
              // IMPORTANT: never display:none the warm Leaflet container. display:none
              // collapses its measured geometry and forces Leaflet/divIcon layers to
              // settle again when Big Blue returns, which produces one-frame marker
              // blips. visibility:hidden removes it from paint while preserving the
              // exact layout/marker geometry in memory.
              display: "block",
            }}
          >
            {mapNode}
          </div>

          {/* Primary surfaces are mounted once per session, then hard-switched.
              Inactive surfaces retain hydration/local state but cannot paint. */}
          {PERSISTENT_APP_SURFACE_ORDER.map((surfaceView) => (
            <PersistentRouteSurface
              key={`roddo-persistent-${String(surfaceView)}`}
              view={surfaceView}
              active={activeView === surfaceView}
              portalTargetId={persistentSurfacePortalId(surfaceView)}
              scrollRef={contentScrollRef}
            >
              {activeView === surfaceView ? activeNonMapNode : null}
            </PersistentRouteSurface>
          ))}

          {/* Deeper/non-primary routes still use the normal stack lifecycle. */}
          {activeView !== View.Map && !isPersistentAppSurface && (
            <div
              key={`roddo-view-${String(activeView)}`}
              ref={contentScrollRef}
              className={[
                "roddo-content-pane absolute inset-0 z-10 h-full min-h-0 bg-transparent text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]",
                activeView === View.BigBlueGlobe || isComingSoonArtView
                  ? "overflow-hidden overscroll-none"
                  : "roddo-content-scroll overflow-y-auto",
              ].join(" ")}
            >
              <RoutePortalProvider targetId="roddo-route-portal-host">
                {activeNonMapNode}
              </RoutePortalProvider>
            </div>
          )}
        </main>

        {/* Route-owned portal hosts. Persistent data may stay warm, but presentation
            is hard-bound to the surface that owns it. No body-level route UI can
            survive an ownership change. */}
        <div
          id="roddo-map-portal-host"
          data-roddo-route-portal-host="map"
          aria-hidden={activeView !== View.Map}
          style={{ display: activeView === View.Map ? "contents" : "none" }}
        />
        {PERSISTENT_APP_SURFACE_ORDER.map((surfaceView) => (
          <div
            key={`roddo-persistent-portal-${String(surfaceView)}`}
            id={persistentSurfacePortalId(surfaceView)}
            data-roddo-route-portal-host={String(surfaceView)}
            aria-hidden={activeView !== surfaceView}
            style={{ display: activeView === surfaceView ? "contents" : "none" }}
          />
        ))}
        <div
          id="roddo-route-portal-host"
          data-roddo-route-portal-host={String(activeView)}
          aria-hidden={activeView === View.Map || isPersistentAppSurface}
          style={{
            display:
              activeView !== View.Map && !isPersistentAppSurface ? "contents" : "none",
          }}
        />

{/* Universal nav matte: prevents cards/photos/maps from visually bleeding under the rounded dock. */}
        {!isFullScreenView && !isComingSoonArtView && (
          <div
            aria-hidden="true"
            className="roddo-nav-matte pointer-events-none absolute inset-x-0 bottom-0 z-[1049]"
            style={{
              height: "calc(var(--roddo-bottom-nav-h, var(--roddo-nav-h, 76px)) + 10px)",
              background: isDarkMode
                ? "linear-gradient(180deg, rgba(9,13,24,0), rgba(13,19,34,0.96) 38%, rgb(9,13,24))"
                : "linear-gradient(180deg, rgba(247,239,230,0), rgb(247,239,230) 38%, rgb(243,232,220))",
            }}
          />
        )}

        {!isFullScreenView && (
          <div
            className="roddo-nav-dock absolute inset-x-0 bottom-0 z-[1050]"
            style={{
              height: "var(--roddo-bottom-nav-h, var(--roddo-nav-h, 76px))",
              transform: "translateZ(0)",
              WebkitTransform: "translateZ(0)",
            }}
          >
            <BottomNav
              activeView={
                (activeView === View.BigBlueGlobe
                  ? View.Map
                  : activeView) as any
              }
              navigateToRoot={navigateToRoot as any}
              userProfile={userProfile as any}
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {paymentItem &&
          (paymentItem.type === "license" || FEATURE.BAZAAR_PAYMENTS_ENABLED) && (
            <Suspense fallback={<Spinner />}>
              <PaymentGateway
                item={paymentItem as any}
                onClose={() => setPaymentItem(null)}
                onPaymentSuccess={handlePaymentSuccess}
                currency={currency as any}
                isDarkMode={isDarkMode}
              />
            </Suspense>
          )}

        {/* Beta access codes are entered only in AuthView signup. No second access-code prompt in App. */}

        {IS_NATIVE && import.meta.env.DEV ? (
          <div
            style={{
              position: "fixed",
              right: 10,
              bottom: 90,
              zIndex: 999999,
              fontSize: 12,
              maxWidth: 320,
              pointerEvents: "auto",
            }}
          >
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                onClick={() => setAuthDiagOpen((v) => !v)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(0,0,0,0.55)",
                  color: "white",
                }}
              >
                {authDiagOpen ? "Hide Auth Diag" : "Auth Diag"}
              </button>

              {authDiagOpen ? (
                <button
                  onClick={runAuthDiag}
                  disabled={authDiagBusy}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.25)",
                    background: authDiagBusy
                      ? "rgba(0,0,0,0.35)"
                      : "rgba(0,0,0,0.55)",
                    color: "white",
                  }}
                >
                  {authDiagBusy ? "Running…" : "Run"}
                </button>
              ) : null}
            </div>

            {authDiagOpen ? (
              <pre
                style={{
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(0,0,0,0.65)",
                  color: "white",
                  overflow: "auto",
                  maxHeight: 260,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(authDiagData, null, 2)}
              </pre>
            ) : null}
          </div>
        ) : null}

        <RoddoInsightCard
          open={!!postLogInsight}
          catchEntry={postLogInsight?.catchEntry ?? null}
          nearbyCatchCount={postLogInsight?.nearbyCatchCount ?? 0}
          weatherText={postLogInsight?.weatherText ?? null}
          onDisablePostCatchInsight={async () => {
            if (!firebaseUser || !userProfile) return;
            const notificationSettings = {
              ...((userProfile as any).notificationSettings || {}),
              showPostCatchInsight: false,
            };
            await updateUserProfile(firebaseUser.uid, {
              notificationSettings,
            } as any);
            setUserProfile((prev: any) =>
              prev ? { ...prev, notificationSettings } : prev,
            );
            addToast("Post-catch Insight turned off.", "success");
          }}
          onClose={() => {
            const done = postLogInsight?.onDone;
            setPostLogInsight(null);
            done?.();
          }}
        />

        <DailyRewardModal
          isOpen={dailyRewardModalOpen}
          preview={dailyRewardPreview}
          isDarkMode={isDarkMode}
          busy={dailyRewardBusy}
          onClaim={handleClaimDailyReward}
          onDismiss={dismissDailyRewardModal}
        />

        <RoddoConfirmDialog
          open={guestLogoutDialogOpen}
          title="Keep this guest passport?"
          description="This guest profile has no password. Signing out before linking Apple or Google makes this UID—and its catches, Shells, gear, and history—unrecoverable on this device."
          tone="warning"
          cancelLabel="Stay signed in"
          isBusy={guestLogoutBusy}
          isDarkMode={isDarkMode}
          onCancel={() => {
            if (!guestLogoutBusy) setGuestLogoutDialogOpen(false);
          }}
          actions={[
            {
              label: "Secure this profile",
              description: "Link Apple or Google without changing your Roddo UID.",
              icon: "🔐",
              tone: "primary",
              onClick: () => {
                setGuestLogoutDialogOpen(false);
                navigateToRoot(View.EditProfile);
              },
            },
            {
              label: "Sign out and reset guest",
              description: "Continue only if you accept that this guest profile cannot be recovered.",
              icon: "⚠️",
              tone: "danger",
              onClick: async () => {
                setGuestLogoutBusy(true);
                try {
                  await performLogout();
                  setGuestLogoutDialogOpen(false);
                } finally {
                  setGuestLogoutBusy(false);
                }
              },
            },
          ]}
        />

        <LoadingScreen
          visible={shouldShowWarmAppBoot}
          label="Opening Roddo…"
          detail="Preparing Big Blue"
          progress={warmBootProgress}
        />

        <ToastContainer
          toasts={toasts as any}
          isDarkMode={isDarkMode}
          onDismiss={(id: string) =>
            setToasts((prev) => prev.filter((t) => t.id !== id))
          }
        />
      </div>
    </div>
    </RoddoRadioProvider>
  );
};

// ---------------------------------------------------------------------------
// DEV-ONLY LANDING PAGE PREVIEW
// ---------------------------------------------------------------------------
// Usage: http://localhost:3000/?landing=1
//
// This does not change production routing. The preview switch is intentionally
// guarded by import.meta.env.DEV so a ?landing=1 query on roddo.app cannot alter
// the production app/landing behavior.
const App: React.FC = () => {
  const showLandingPreview =
    import.meta.env.DEV &&
    !Capacitor.isNativePlatform() &&
    new URLSearchParams(window.location.search).get("landing") === "1";

  if (showLandingPreview) {
    const openLocalApp = () => {
      window.location.assign("/app");
    };

    return (
      <LandingPage
        onOpenApp={openLocalApp}
        onSignIn={openLocalApp}
        termsHref="/terms"
        privacyHref="/privacy"
      />
    );
  }

  return <RoddoApp />;
};

export default App;
